import * as THREE from 'three';

export interface Position3D {
  x: number;
  y: number;
  z: number;
}

export interface PerimeterCloseResult {
  success: boolean;
  message: string;
  closingElementId?: string;
  gapDistance: number;
  finalGap: number;
  addedSection?: {
    id: string;
    position: { x: number; y: number; z: number };
    dimensions: { width: number; height: number };
    widthMultiplier: number;
    baseWidth: number;
  };
}

export class PerimeterCloser {
  constructor(
    private scene: THREE.Scene,
    private preparedModel: THREE.Scene
  ) {
    console.log('[PC] PRECYZYJNE ZAMYKANIE - v3.5 SWAP FIRST');
  }

  async close(fenceConfigParam: any, selectedColor?: string, selectedVariant?: string): Promise<PerimeterCloseResult> {
    
    // 🔍🔍🔍 LOGOWANIE PARAMETRÓW WEJŚCIOWYCH - SPRAWDZAM ROZPOZNANIE WARIANTU 🔍🔍🔍
    console.log(`\n\n[PC] ========================================`);
    console.log(`[PC] 🔍🔍🔍 PARAMETRY WEJŚCIOWE close() 🔍🔍🔍`);
    console.log(`[PC] ========================================`);
    console.log(`[PC]   fenceConfigParam?.id = "${fenceConfigParam?.id}"`);
    console.log(`[PC]   selectedVariant = "${selectedVariant}"`);
    console.log(`[PC]   selectedColor = "${selectedColor}"`);
    console.log(`[PC]   fenceConfigParam =`, fenceConfigParam);
    console.log(`[PC] ========================================\n`);
    
    // 🎯 Wykryj typ ogrodzenia (Royal Mix/Muro Mix) - używane w wielu miejscach
    const fenceConfigId = fenceConfigParam?.id || '';
    const isRoyalMix = fenceConfigId === 'royal-mix' || fenceConfigId === 'royal_s50' || fenceConfigId === 'royal_p50' || fenceConfigId?.startsWith('royal_');
    const isRoyalS50 = fenceConfigId === 'royal_s50' || (isRoyalMix && selectedVariant === 's50');
    const isRoyalP50 = fenceConfigId === 'royal_p50' || (isRoyalMix && selectedVariant === 'p50');
    
    // 1. POBRANIE ELEMENTÓW
    let elementsData: any;
    try { elementsData = (window as any).getAllFenceElements?.(); } catch (e) {}
    let elements3DRef: Map<string, THREE.Group> | null = null;
    try { elements3DRef = (window as any).elements3DRef?.current; } catch (e) {}

    const elements: THREE.Group[] = elementsData?.map((d: any) => elements3DRef?.get(d?.id)).filter(Boolean) || [];
    if (elements.length < 2) {
      return { success: false, message: 'Za mało elementów', gapDistance: 0, finalGap: 0 };
    }

    const first = elements[0];
    const last = elements[elements.length - 1];

    // 🏁 PIERWSZY I OSTATNI ELEMENT
    console.log(`\n[PC] 🏁 PIERWSZY I OSTATNI ELEMENT:`);
    console.log(`[PC]   FIRST element:`);
    console.log(`[PC]     id = ${first.userData?.fenceElementId || 'brak'}`);
    console.log(`[PC]     typ = ${first.userData?.elementType || 'brak'}`);
    console.log(`[PC]     pozycja = (${first.position.x.toFixed(3)}, ${first.position.y.toFixed(3)}, ${first.position.z.toFixed(3)})`);
    console.log(`[PC]     rotacja = ${(first.rotation.y * 180 / Math.PI).toFixed(2)}°`);
    console.log(`[PC]     leftEdge = ${first.userData?.leftEdge ? `(${first.userData.leftEdge.x.toFixed(3)}, ${first.userData.leftEdge.z.toFixed(3)})` : 'brak'}`);
    console.log(`[PC]     rightEdge = ${first.userData?.rightEdge ? `(${first.userData.rightEdge.x.toFixed(3)}, ${first.userData.rightEdge.z.toFixed(3)})` : 'brak'}`);
    console.log(`[PC]     width = ${first.userData?.width?.toFixed(4) || 'brak'}m`);
    console.log(`[PC]     baseWidth = ${first.userData?.baseWidth?.toFixed(4) || 'brak'}m`);
    console.log(`[PC]     widthMultiplier = ${first.userData?.widthMultiplier || 'brak'}`);
    console.log(`[PC]   LAST element:`);
    console.log(`[PC]     id = ${last.userData?.fenceElementId || 'brak'}`);
    console.log(`[PC]     typ = ${last.userData?.elementType || 'brak'}`);
    console.log(`[PC]     pozycja = (${last.position.x.toFixed(3)}, ${last.position.y.toFixed(3)}, ${last.position.z.toFixed(3)})`);
    console.log(`[PC]     rotacja = ${(last.rotation.y * 180 / Math.PI).toFixed(2)}°`);
    console.log(`[PC]     leftEdge = ${last.userData?.leftEdge ? `(${last.userData.leftEdge.x.toFixed(3)}, ${last.userData.leftEdge.z.toFixed(3)})` : 'brak'}`);
    console.log(`[PC]     rightEdge = ${last.userData?.rightEdge ? `(${last.userData.rightEdge.x.toFixed(3)}, ${last.userData.rightEdge.z.toFixed(3)})` : 'brak'}`);
    console.log(`[PC]     width = ${last.userData?.width?.toFixed(4) || 'brak'}m`);
    console.log(`[PC]     baseWidth = ${last.userData?.baseWidth?.toFixed(4) || 'brak'}m`);
    console.log(`[PC]     widthMultiplier = ${last.userData?.widthMultiplier || 'brak'}`);

    // 2. PUNKTY DOCELOWE - ✅ UŻYWAMY PRAWEJ KRAWĘDZI PIERWSZEGO
    let firstRight = (first.userData?.rightEdge instanceof THREE.Vector3) 
      ? first.userData.rightEdge.clone() 
      : new THREE.Vector3(first.position.x + 0.198, first.position.y, first.position.z);
    
    let lastRight = (last.userData?.rightEdge instanceof THREE.Vector3) 
      ? last.userData.rightEdge.clone() 
      : new THREE.Vector3(last.position.x + 0.198, last.position.y, last.position.z);

    // 🎯 KOREKTA DLA SLIM: zawsze przesuń firstRight OD pierwszego słupka (kompensacja błędu)
    if (fenceConfigParam?.id === 'slim') {
      // Oblicz kierunek od pozycji pierwszego słupka do firstRight (OD słupka)
      const firstPos = first.position.clone();
      const directionFromPost = new THREE.Vector3(
        firstRight.x - firstPos.x,
        0,
        firstRight.z - firstPos.z
      );
      const distanceToPost = directionFromPost.length();
      directionFromPost.normalize();
      
      // Przesuń firstRight o małą wartość (np. 0.15m) OD słupka
      const correctionDistance = 0.15; // 150mm korekty
      const originalFirstRight = firstRight.clone();
      firstRight.add(directionFromPost.multiplyScalar(correctionDistance));
      
      console.log(`[PC] 🎯 KOREKTA SLIM: firstRight przesunięty OD pierwszego słupka o ${correctionDistance.toFixed(3)}m`);
      console.log(`[PC]    przed: (${originalFirstRight.x.toFixed(3)}, ${originalFirstRight.z.toFixed(3)})`);
      console.log(`[PC]    po: (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
      console.log(`[PC]    dystans do słupka: przed=${distanceToPost.toFixed(3)}m, po=${(distanceToPost + correctionDistance).toFixed(3)}m`);
    }

    // 🎯 KOREKTA DLA P22 MURO MIX: zawsze przesuń firstRight OD pierwszego słupka (kompensacja błędu)
    const isMuroMix = fenceConfigParam?.id?.startsWith('lupane-muro');
    const isMuroP22 = (fenceConfigParam?.id === 'lupane-muro-p22') || (isMuroMix && selectedVariant === 'p22');
    if (isMuroP22) {
      // Oblicz kierunek od pozycji pierwszego słupka do firstRight (OD słupka)
      const firstPos = first.position.clone();
      const directionFromPost = new THREE.Vector3(
        firstRight.x - firstPos.x,
        0,
        firstRight.z - firstPos.z
      );
      const distanceToPost = directionFromPost.length();
      
      if (distanceToPost > 0.001) {
        directionFromPost.normalize();
        
        // Przesuń firstRight o małą wartość (np. 0.2m) OD słupka
        const correctionDistance = 0.2; // 200mm korekty
        const originalFirstRight = firstRight.clone();
        firstRight.add(directionFromPost.multiplyScalar(correctionDistance));
        
        console.log(`[PC] 🎯 KOREKTA P22 MURO MIX: firstRight przesunięty OD pierwszego słupka o ${correctionDistance.toFixed(3)}m`);
        console.log(`[PC]    przed: (${originalFirstRight.x.toFixed(3)}, ${originalFirstRight.z.toFixed(3)})`);
        console.log(`[PC]    po: (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
        console.log(`[PC]    dystans do słupka: przed=${distanceToPost.toFixed(3)}m, po=${(distanceToPost + correctionDistance).toFixed(3)}m`);
      } else {
        console.log(`[PC] ⚠️ P22 MURO MIX: distanceToPost=${distanceToPost.toFixed(6)}m jest za małe, pomijam korektę`);
      }
    }

    // 🎯 KOREKTA DLA P38 MURO MIX: zawsze przesuń firstRight OD pierwszego słupka (kompensacja błędu)
    const isMuroP38 = (fenceConfigParam?.id === 'lupane-muro-p38') || (isMuroMix && selectedVariant === 'p38');
    if (isMuroP38) {
      // Oblicz kierunek od pozycji pierwszego słupka do firstRight (OD słupka)
      const firstPos = first.position.clone();
      const directionFromPost = new THREE.Vector3(
        firstRight.x - firstPos.x,
        0,
        firstRight.z - firstPos.z
      );
      const distanceToPost = directionFromPost.length();
      
      if (distanceToPost > 0.001) {
        directionFromPost.normalize();
        
        // Przesuń firstRight o małą wartość (np. 0.25m) OD słupka
        const correctionDistance = 0.25; // 250mm korekty
        const originalFirstRight = firstRight.clone();
        firstRight.add(directionFromPost.multiplyScalar(correctionDistance));
        
        console.log(`[PC] 🎯 KOREKTA P38 MURO MIX: firstRight przesunięty OD pierwszego słupka o ${correctionDistance.toFixed(3)}m`);
        console.log(`[PC]    przed: (${originalFirstRight.x.toFixed(3)}, ${originalFirstRight.z.toFixed(3)})`);
        console.log(`[PC]    po: (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
        console.log(`[PC]    dystans do słupka: przed=${distanceToPost.toFixed(3)}m, po=${(distanceToPost + correctionDistance).toFixed(3)}m`);
      } else {
        console.log(`[PC] ⚠️ P38 MURO MIX: distanceToPost=${distanceToPost.toFixed(6)}m jest za małe, pomijam korektę`);
      }
    }

    // 🎯 KOREKTA DLA S22 MURO MIX: zawsze przesuń firstRight OD pierwszego słupka (kompensacja błędu)
    const isMuroS22 = (fenceConfigParam?.id === 'lupane-muro-s22') || (isMuroMix && selectedVariant === 's22');
    if (isMuroS22) {
      // Oblicz kierunek od pozycji pierwszego słupka do firstRight (OD słupka)
      const firstPos = first.position.clone();
      const directionFromPost = new THREE.Vector3(
        firstRight.x - firstPos.x,
        0,
        firstRight.z - firstPos.z
      );
      const distanceToPost = directionFromPost.length();
      
      if (distanceToPost > 0.001) {
        directionFromPost.normalize();
        
        // Przesuń firstRight o małą wartość (np. 0.2m) OD słupka
        const correctionDistance = 0.2; // 200mm korekty
        const originalFirstRight = firstRight.clone();
        firstRight.add(directionFromPost.multiplyScalar(correctionDistance));
        
        console.log(`[PC] 🎯 KOREKTA S22 MURO MIX: firstRight przesunięty OD pierwszego słupka o ${correctionDistance.toFixed(3)}m`);
        console.log(`[PC]    przed: (${originalFirstRight.x.toFixed(3)}, ${originalFirstRight.z.toFixed(3)})`);
        console.log(`[PC]    po: (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
        console.log(`[PC]    dystans do słupka: przed=${distanceToPost.toFixed(3)}m, po=${(distanceToPost + correctionDistance).toFixed(3)}m`);
      } else {
        console.log(`[PC] ⚠️ S22 MURO MIX: distanceToPost=${distanceToPost.toFixed(6)}m jest za małe, pomijam korektę`);
      }
    }

    // 🎯 KOREKTA DLA S38 MURO MIX: użyj dokładnie tej samej logiki co P22 (0.2m zamiast 0.25m)
    const isMuroS38 = (fenceConfigParam?.id === 'lupane-muro-s38') || (isMuroMix && selectedVariant === 's38');
    if (isMuroS38) {
      // Oblicz kierunek od pozycji pierwszego słupka do firstRight (OD słupka)
      const firstPos = first.position.clone();
      const directionFromPost = new THREE.Vector3(
        firstRight.x - firstPos.x,
        0,
        firstRight.z - firstPos.z
      );
      const distanceToPost = directionFromPost.length();
      
      if (distanceToPost > 0.001) {
        directionFromPost.normalize();
        
        // Przesuń firstRight o małą wartość (0.2m - dokładnie jak P22) OD słupka
        const correctionDistance = 0.2; // 200mm korekty - taka sama jak P22
        const originalFirstRight = firstRight.clone();
        firstRight.add(directionFromPost.multiplyScalar(correctionDistance));
        
        console.log(`[PC] 🎯 KOREKTA S38 MURO MIX (używa P22): firstRight przesunięty OD pierwszego słupka o ${correctionDistance.toFixed(3)}m`);
        console.log(`[PC]    przed: (${originalFirstRight.x.toFixed(3)}, ${originalFirstRight.z.toFixed(3)})`);
        console.log(`[PC]    po: (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
        console.log(`[PC]    dystans do słupka: przed=${distanceToPost.toFixed(3)}m, po=${(distanceToPost + correctionDistance).toFixed(3)}m`);
      } else {
        console.log(`[PC] ⚠️ S38 MURO MIX: distanceToPost=${distanceToPost.toFixed(6)}m jest za małe, pomijam korektę`);
      }
    }

    // 🎯 KOREKTA DLA ROYAL MIX (S50/P50): zawsze przesuń firstRight OD pierwszego słupka (kompensacja błędu)
    console.log(`[PC] 🔍 DEBUG ROYAL MIX: fenceConfigParam?.id = "${fenceConfigParam?.id}"`);
    if (isRoyalMix) {
      console.log(`[PC] ✅ ROYAL MIX: Warunek spełniony, wykonuję korektę`);
      // Oblicz kierunek od pozycji pierwszego słupka do firstRight (OD słupka)
      const firstPos = first.position.clone();
      const directionFromPost = new THREE.Vector3(
        firstRight.x - firstPos.x,
        0,
        firstRight.z - firstPos.z
      );
      const distanceToPost = directionFromPost.length();
      
      if (distanceToPost > 0.001) {
        directionFromPost.normalize();
        
      // Przesuń firstRight o małą wartość (np. 0.2m) OD słupka
      const correctionDistance = 0.2; // 200mm korekty
        const originalFirstRight = firstRight.clone();
        firstRight.add(directionFromPost.multiplyScalar(correctionDistance));
        
        console.log(`[PC] 🎯 KOREKTA ROYAL MIX (S50): firstRight przesunięty OD pierwszego słupka o ${correctionDistance.toFixed(3)}m`);
        console.log(`[PC]    przed: (${originalFirstRight.x.toFixed(3)}, ${originalFirstRight.z.toFixed(3)})`);
        console.log(`[PC]    po: (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
        console.log(`[PC]    dystans do słupka: przed=${distanceToPost.toFixed(3)}m, po=${(distanceToPost + correctionDistance).toFixed(3)}m`);
      } else {
        console.log(`[PC] ⚠️ ROYAL MIX: distanceToPost=${distanceToPost.toFixed(6)}m jest za małe, pomijam korektę`);
      }
    } else {
      console.log(`[PC] ❌ ROYAL MIX: Warunek NIE spełniony (id="${fenceConfigParam?.id}")`);
    }

    // 🔍 WERYFIKACJA KRAWĘDZI
    console.log(`\n[PC] 🔍 WERYFIKACJA KRAWĘDZI:`);
    console.log(`[PC]   FIRST element - obliczenia teoretyczne:`);
    const firstDirVector = new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), first.rotation.y);
    const firstHalfWidth = (first.userData?.width || 0.396) / 2;
    const firstTheoreticalLeft = new THREE.Vector3()
      .copy(first.position)
      .addScaledVector(firstDirVector, -firstHalfWidth);
    const firstTheoreticalRight = new THREE.Vector3()
      .copy(first.position)
      .addScaledVector(firstDirVector, firstHalfWidth);
    console.log(`[PC]     teoretyczny leftEdge  = (${firstTheoreticalLeft.x.toFixed(3)}, ${firstTheoreticalLeft.z.toFixed(3)})`);
    console.log(`[PC]     teoretyczny rightEdge = (${firstTheoreticalRight.x.toFixed(3)}, ${firstTheoreticalRight.z.toFixed(3)})`);
    if (first.userData?.leftEdge && first.userData?.rightEdge) {
      const firstLeftDiff = first.userData.leftEdge.distanceTo(firstTheoreticalLeft);
      const firstRightDiff = first.userData.rightEdge.distanceTo(firstTheoreticalRight);
      console.log(`[PC]     różnica userData.leftEdge  vs teoretyczny = ${firstLeftDiff.toFixed(4)}m`);
      console.log(`[PC]     różnica userData.rightEdge vs teoretyczny = ${firstRightDiff.toFixed(4)}m`);
    }
    
    console.log(`[PC]   LAST element - obliczenia teoretyczne:`);
    const lastDirVector = new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), last.rotation.y);
    const lastHalfWidth = (last.userData?.width || 0.396) / 2;
    const lastTheoreticalLeft = new THREE.Vector3()
      .copy(last.position)
      .addScaledVector(lastDirVector, -lastHalfWidth);
    const lastTheoreticalRight = new THREE.Vector3()
      .copy(last.position)
      .addScaledVector(lastDirVector, lastHalfWidth);
    console.log(`[PC]     teoretyczny leftEdge  = (${lastTheoreticalLeft.x.toFixed(3)}, ${lastTheoreticalLeft.z.toFixed(3)})`);
    console.log(`[PC]     teoretyczny rightEdge = (${lastTheoreticalRight.x.toFixed(3)}, ${lastTheoreticalRight.z.toFixed(3)})`);
    if (last.userData?.leftEdge && last.userData?.rightEdge) {
      const lastLeftDiff = last.userData.leftEdge.distanceTo(lastTheoreticalLeft);
      const lastRightDiff = last.userData.rightEdge.distanceTo(lastTheoreticalRight);
      console.log(`[PC]     różnica userData.leftEdge  vs teoretyczny = ${lastLeftDiff.toFixed(4)}m`);
      console.log(`[PC]     różnica userData.rightEdge vs teoretyczny = ${lastRightDiff.toFixed(4)}m`);
    }
    
    console.log(`[PC]   MOŻLIWE POŁĄCZENIA:`);
    const lastLeftToFirstLeft = last.userData?.leftEdge && first.userData?.leftEdge 
      ? last.userData.leftEdge.distanceTo(first.userData.leftEdge) 
      : null;
    const lastRightToFirstRight = lastRight.distanceTo(firstRight);
    if (lastLeftToFirstLeft !== null) {
      console.log(`[PC]     lastLeft → firstLeft = ${lastLeftToFirstLeft.toFixed(4)}m`);
    }
    console.log(`[PC]     lastRight → firstRight = ${lastRightToFirstRight.toFixed(4)}m`);

    const gap = lastRight.distanceTo(firstRight);
    
    // 🔍 OBLICZAM PUNKTY DOCELOWE (przed przesunięciem)
    console.log(`\n[PC] 🔍 OBLICZAM PUNKTY DOCELOWE (przed przesunięciem):`);
    console.log(`[PC]   Używane krawędzie:`);
    console.log(`[PC]     PIERWSZEGO elementu - rightEdge (zewnętrzna, koniec): ${first.userData?.rightEdge ? 'z userData' : 'obliczone fallback'}`);
    console.log(`[PC]     OSTATNIEGO elementu - rightEdge (zewnętrzna, początek): ${last.userData?.rightEdge ? 'z userData' : 'obliczone fallback'}`);
    console.log(`[PC]   Punkty:`);
    console.log(`[PC]     firstRight (koniec pierwszego) = (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
    console.log(`[PC]     lastRight (początek ostatniego) = (${lastRight.x.toFixed(3)}, ${lastRight.z.toFixed(3)})`);
    
    // 🔍 OBLICZAM GAP (przed przesunięciem)
    console.log(`\n[PC] 🔍 OBLICZAM GAP (przed przesunięciem):`);
    console.log(`[PC]   gap (oryginalny) = ${gap.toFixed(4)}m`);
    console.log(`[PC]   Odległość między lastRight a firstRight (prosta linia)`);
    
    // 2.0. ZNAJDŹ ISTNIEJĄCĄ SEKCJĘ (potrzebna do obliczeń)
    const existingSection = elements.find(el => el.userData?.elementType === 'section');
    console.log(`[PC] 🔍 DEBUG: fenceConfigParam.id = "${fenceConfigParam?.id}", selectedVariant = "${selectedVariant}"`);
    console.log(`[PC] 🔍 DEBUG: existingSection found = ${!!existingSection}`);
    if (existingSection) {
      console.log(`[PC] 🔍 DEBUG existingSection PRZED obliczeniem modelBaseWidth:`);
      console.log(`[PC]    userData.width = ${existingSection.userData?.width}`);
      console.log(`[PC]    userData.widthMultiplier = ${existingSection.userData?.widthMultiplier}`);
      console.log(`[PC]    userData.baseWidth = ${existingSection.userData?.baseWidth}`);
      console.log(`[PC]    userData.elementType = ${existingSection.userData?.elementType}`);
    } else {
      console.log(`[PC] ⚠️ BRAK existingSection - nie znaleziono żadnej sekcji w elements!`);
      console.log(`[PC]    elements.length = ${elements.length}`);
      console.log(`[PC]    elements types:`, elements.map(el => el.userData?.elementType));
    }
    
    // 2.1. SZEROKOŚĆ BAZOWA (potrzebna do przesunięcia) - używa rightOffset[1]
    console.log(`\n[PC] 🔍 OBLICZAM SZEROKOŚCI:`);
    let baseWidth = this.getBaseWidth(elements, fenceConfigParam);
    console.log(`[PC]   baseWidth (spacing) = ${baseWidth.toFixed(4)}m`);
    console.log(`[PC]     źródło: ${existingSection?.userData?.baseWidth ? 'userData.baseWidth' : existingSection?.userData?.width ? 'userData.width' : 'konfiguracja'}`);
    
    // 2.1b. SZEROKOŚĆ METALU (do wydłużenia sekcji zamykającej) - używa metalRightOffset[1] lub fallback
    const sectionConfig = fenceConfigParam?.elements?.section;
    let metalWidth = sectionConfig?.metalRightOffset?.[1] || sectionConfig?.rightOffset?.[1] || baseWidth;
    console.log(`[PC]   metalWidth (wydłużenie) = ${metalWidth.toFixed(4)}m`);
    console.log(`[PC]     źródło: ${sectionConfig?.metalRightOffset?.[1] ? 'metalRightOffset[1]' : sectionConfig?.rightOffset?.[1] ? 'rightOffset[1]' : 'baseWidth fallback'}`);
    
    // 2.1c. BAZOWA SZEROKOŚĆ MODELU (do skalowania) - sprawdź rzeczywistą szerokość istniejącej sekcji
    // To jest szerokość modelu sekcji dla widthMultiplier=1
    console.log(`[PC]   modelBaseWidth (do skalowania):`);
    let modelBaseWidth = baseWidth; // Domyślnie użyj baseWidth
    console.log(`[PC]     początkowa wartość (z baseWidth) = ${modelBaseWidth.toFixed(4)}m`);
    
    // 🎯 NAJWAŻNIEJSZE: Sprawdź rzeczywistą szerokość istniejącej sekcji z userData
    if (existingSection) {
      if (existingSection.userData?.width && existingSection.userData?.widthMultiplier === 1) {
        // Jeśli mamy sekcję z widthMultiplier=1, użyj jej rzeczywistej szerokości
        modelBaseWidth = existingSection.userData.width;
        console.log(`[PC] 📐 modelBaseWidth (z istniejącej sekcji 1x) = ${modelBaseWidth.toFixed(4)}m`);
      } else if (existingSection.userData?.width && existingSection.userData?.widthMultiplier > 1) {
        // Jeśli mamy sekcję z widthMultiplier>1, oblicz bazową szerokość
        modelBaseWidth = existingSection.userData.width / existingSection.userData.widthMultiplier;
        console.log(`[PC] 📐 modelBaseWidth (obliczone: ${existingSection.userData.width.toFixed(4)}m / ${existingSection.userData.widthMultiplier}) = ${modelBaseWidth.toFixed(4)}m`);
      }
    }
    
    // Fallback: użyj defaultWidth z konfiguracji
    if (!existingSection || modelBaseWidth === baseWidth) {
      const fromDefaultWidth = sectionConfig?.defaultWidth;
      const fromRightOffset = sectionConfig?.rightOffset?.[1];
      modelBaseWidth = fromDefaultWidth || fromRightOffset || baseWidth;
      console.log(`[PC] 📐 modelBaseWidth (z konfiguracji/fallback):`);
      console.log(`[PC]    sectionConfig.defaultWidth = ${fromDefaultWidth?.toFixed(4) || 'brak'}m`);
      console.log(`[PC]    sectionConfig.rightOffset[1] = ${fromRightOffset?.toFixed(4) || 'brak'}m`);
      console.log(`[PC]    wybrany modelBaseWidth = ${modelBaseWidth.toFixed(4)}m`);
      if (existingSection && modelBaseWidth === baseWidth) {
        console.log(`[PC] ⚠️ FALLBACK: existingSection istnieje, ale modelBaseWidth === baseWidth, używam fallbacku`);
      }
    }
    
    // 2.2. OBLICZ ŚRODEK OBWODU (dla przesunięcia do tyłu)
    const perimeterCenter = new THREE.Vector3();
    elements.forEach(el => {
      perimeterCenter.add(el.position);
    });
    perimeterCenter.divideScalar(elements.length);
    
    // 2.3. PRZESUŃ PUNKTY DOCELOWE OD ŚRODKA OBWODU (żeby element dotykał dalej na krawędziach)
    const midPoint = new THREE.Vector3()
      .addVectors(lastRight, firstRight)
      .multiplyScalar(0.5);
    
    const directionFromCenter = new THREE.Vector3()
      .subVectors(midPoint, perimeterCenter)
      .normalize();
    
    // 🎯 Dla Gładkie Long: baseWidth = metalWidth = 0.198m, więc bez zmian
    // 🎯 Dla P22: używamy metalWidth (0.340m) zamiast baseWidth (0.420m) - bardziej precyzyjne
    // Przesuń punkty o 1/2 szerokości metalu OD środka (w przeciwną stronę)
    const shiftDistance = metalWidth * 0.5;
    console.log(`[PC] 🔍 Przesunięcie punktów docelowych:`);
    console.log(`[PC]    directionFromCenter = (${directionFromCenter.x.toFixed(4)}, ${directionFromCenter.z.toFixed(4)})`);
    console.log(`[PC]    shiftDistance = ${shiftDistance.toFixed(4)}m`);
    
    lastRight.addScaledVector(directionFromCenter, shiftDistance);
    firstRight.addScaledVector(directionFromCenter, shiftDistance);
    
    // Przelicz gap po przesunięciu punktów
    const adjustedGap = lastRight.distanceTo(firstRight);
    
    if (gap < 0.01) {
      return { success: false, message: 'Obwód już zamknięty', gapDistance: gap, finalGap: gap };
    }

    // 🎯 PUNKTY DOCELOWE (FINALNE po przesunięciu)
    console.log(`\n[PC] 🎯 PUNKTY DOCELOWE (FINALNE po przesunięciu):`);
    console.log(`[PC]   lastRight (koniec last)   = (${lastRight.x.toFixed(3)}, ${lastRight.z.toFixed(3)})`);
    console.log(`[PC]   firstRight (koniec first) = (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
    console.log(`[PC]   gap (oryginalny, przed przesunięciem) = ${gap.toFixed(4)}m`);
    console.log(`[PC]   gap (po przesunięciu od środka) = ${adjustedGap.toFixed(4)}m`);
    console.log(`[PC]   shiftDistance (metalWidth * 0.5) = ${shiftDistance.toFixed(4)}m`);
    console.log(`[PC]   Przesunięcie: ${shiftDistance.toFixed(4)}m OD środka obwodu`);

    // 4. WARIANT
    console.log(`[PC] 🔍 OKREŚLANIE WARIANTU:`);
    console.log(`[PC]   fenceConfigParam?.id = "${fenceConfigParam?.id}"`);
    console.log(`[PC]   selectedVariant = "${selectedVariant}"`);
    const variant = this.determineVariant(fenceConfigParam, selectedVariant);
    console.log(`[PC]   ✅ OKREŚLONY WARIANT = "${variant}"`);
    console.log(`[PC]   📋 Ten wariant będzie przekazany do applyRoyalMetalCorrection`);

    // 5. TRANSFORMACJA
    const dx = firstRight.x - lastRight.x;
    const dz = firstRight.z - lastRight.z;
    const targetRotation = Math.atan2(-dz, dx);
    const directionRight = new THREE.Vector3(dx, 0, dz).normalize(); // kierunek od lastRight do firstRight (potrzebny później)
    
    // 🎯 Dla gladkie_long: adjustedGap to dokładnie długość, którą potrzebujemy (bez wydłużenia)
    // 🎯 Dla innych systemów z różnymi metalWidth: możemy dodać lekkie wydłużenie, jeśli to potrzebne
    // 🎯 Dla Royal Mix S50/P50: NIE dodawaj extension, bo realWidth jest obliczane tylko z bloków (bez metalu)
    let targetGap = adjustedGap;
    let centerShift = new THREE.Vector3(0, 0, 0);
    
    // 🎯 DLA S38: NIE zwiększaj targetGap tutaj - zamiast tego zwiększymy scaleX na końcu, po wszystkich korektach
    // (jeśli zwiększymy targetGap tutaj, to scaleX będzie 3x większy, więc realWidthBeforeCorrection też będzie 3x większy,
    // więc correctionFactor będzie bliski 1.0 i nie będzie dodatkowego skalowania!)
    const isMuroS38ForTargetGap = (fenceConfigParam?.id === 'lupane-muro-s38') || (fenceConfigParam?.id?.startsWith('lupane-muro') && selectedVariant === 's38');
    // if (isMuroS38ForTargetGap) {
    //   const s38GapMultiplier = 3.0; // Zwiększ targetGap o 3x
    //   targetGap = adjustedGap * s38GapMultiplier;
    //   console.log(`[PC] 🎯 S38: ZWIĘKSZAM targetGap O 3x: adjustedGap (${adjustedGap.toFixed(4)}m) × ${s38GapMultiplier} = targetGap (${targetGap.toFixed(4)}m)`);
    // }
    // 🎯 Dla S38 MURO MIX w sekcji zamykającej: użyj logiki P22 (bez dodatkowego wydłużenia)
    // 🎯 Jeśli metalWidth jest WIĘKSZY niż baseWidth (np. P22: metalWidth=0.340m > baseWidth=0.420m to błąd, ale dla innych przypadków)
    // to możemy dodać lekkie wydłużenie
    // 🎯 Dla Royal Mix S50/P50: POMIŃ extension, bo realWidth jest obliczane tylko z bloków
    if (metalWidth > baseWidth * 1.1 && !(isRoyalS50 || isRoyalP50)) {
      // Dodaj lekkie wydłużenie tylko jeśli metalWidth jest znacznie większy od baseWidth
      const extensionFactor = 0.3; // Wydłużenie o 30% różnicy
      const extension = (metalWidth - baseWidth) * extensionFactor;
      targetGap = adjustedGap + extension;
      
      // Przesuń środek lekko w prawo (w kierunku firstRight), żeby wydłużenie było od końca
      centerShift = directionRight.clone().multiplyScalar(extension * 0.5);
      
      console.log(`[PC] 🎯 WYDŁUŻENIE: metalWidth (${metalWidth.toFixed(4)}m) > baseWidth (${baseWidth.toFixed(4)}m)`);
      console.log(`[PC]    extension = ${extension.toFixed(4)}m, centerShift = ${centerShift.length().toFixed(4)}m`);
    } else {
      if (isRoyalS50 || isRoyalP50) {
        console.log(`[PC] 🎯 BEZ WYDŁUŻENIA dla Royal Mix ${isRoyalS50 ? 'S50' : 'P50'}: realWidth jest obliczane tylko z bloków (bez metalu), więc nie potrzebujemy extension`);
      } else {
        console.log(`[PC] 🎯 BEZ WYDŁUŻENIA: metalWidth (${metalWidth.toFixed(4)}m) ≈ baseWidth (${baseWidth.toFixed(4)}m)`);
      }
    }
    
    // 🎯 Skaluj względem bazowej szerokości modelu (defaultWidth), nie rightOffset!
    // 🎯 DLA S38: targetGap już został zwiększony o 3x powyżej, więc scaleX będzie automatycznie 3x większy
    let scaleX = targetGap / modelBaseWidth;
    
    // 🎯 DLA S38: WERYFIKACJA - sprawdź czy targetGap został faktycznie zwiększony
    const isMuroS38ForVerification = (fenceConfigParam?.id === 'lupane-muro-s38') || (fenceConfigParam?.id?.startsWith('lupane-muro') && selectedVariant === 's38');
    if (isMuroS38ForVerification) {
      console.log(`[PC] 🔍 S38 WERYFIKACJA targetGap:`);
      console.log(`[PC]     adjustedGap = ${adjustedGap.toFixed(4)}m`);
      console.log(`[PC]     targetGap = ${targetGap.toFixed(4)}m`);
      console.log(`[PC]     modelBaseWidth = ${modelBaseWidth.toFixed(4)}m`);
      console.log(`[PC]     scaleX = ${scaleX.toFixed(4)}`);
      console.log(`[PC]     targetGap / adjustedGap = ${(targetGap / adjustedGap).toFixed(4)} (powinno być 3.0)`);
    }
    
    const exactCenterX = (lastRight.x + firstRight.x) / 2 + centerShift.x;
    const exactCenterZ = (lastRight.z + firstRight.z) / 2 + centerShift.z;

    // 🔧 TRANSFORMACJA
    console.log(`\n[PC] 🔧 TRANSFORMACJA:`);
    console.log(`[PC]   Skalowanie (scaleX):`);
    console.log(`[PC]     adjustedGap (dystans między punktami) = ${adjustedGap.toFixed(4)}m`);
    console.log(`[PC]     targetGap (z wydłużeniem jeśli potrzebne) = ${targetGap.toFixed(4)}m`);
    console.log(`[PC]     modelBaseWidth (bazowa szerokość modelu) = ${modelBaseWidth.toFixed(4)}m`);
    console.log(`[PC]     scaleX = targetGap / modelBaseWidth = ${scaleX.toFixed(3)}`);
    console.log(`[PC]   Pozycja (center):`);
    console.log(`[PC]     exactCenterX = ${exactCenterX.toFixed(3)}m`);
    console.log(`[PC]     exactCenterZ = ${exactCenterZ.toFixed(3)}m`);
    console.log(`[PC]     centerShift (wydłużenie) = ${centerShift.length().toFixed(4)}m`);
    console.log(`[PC]   Rotacja:`);
    console.log(`[PC]     targetRotation = ${(targetRotation * 180 / Math.PI).toFixed(2)}°`);
    console.log(`[PC]     dx = ${dx.toFixed(4)}, dz = ${dz.toFixed(4)}`);

    // 6. DODANIE ELEMENTU
    // 🎯 Zawsze używaj widthMultiplier = 1 dla sekcji zamykającej (będzie skalowane przez scaleX)
    // Tak samo dla P22 i P38 - oba używają widthMultiplier = 1 (jak P22, który działa dobrze)
    const closingWidthMultiplier = 1;
    
    const id = `closing-${Date.now()}`;
    await (window as any).addFenceElement?.(
      id, 
      'section', 
      { x: exactCenterX, y: 0, z: exactCenterZ }, 
      fenceConfigParam, 
      undefined, 
      selectedColor, 
      undefined, 
      closingWidthMultiplier, 
      variant
    );

    await new Promise(r => setTimeout(r, 600));

    const el = this.findClosingElementSafe(id);
    if (!el) {
      return { success: false, message: 'Nie znaleziono elementu', gapDistance: gap, finalGap: gap };
    }

    el.userData.isClosingSection = true;
    el.userData.leftEdge = lastRight.clone();
    el.userData.rightEdge = firstRight.clone();

    // 🔧 SEKCJA ZAMYKAJĄCA (tuż po utworzeniu)
    console.log(`\n[PC] 🔧 SEKCJA ZAMYKAJĄCA (po utworzeniu, przed skalowaniem):`);
    console.log(`[PC]   id = ${id}`);
    console.log(`[PC]   elementType = ${el.userData?.elementType || 'brak'}`);
    console.log(`[PC]   pozycja = (${el.position.x.toFixed(3)}, ${el.position.y.toFixed(3)}, ${el.position.z.toFixed(3)})`);
    console.log(`[PC]   rotacja = ${(el.rotation.y * 180 / Math.PI).toFixed(2)}°`);
    console.log(`[PC]   scale.x = ${el.scale.x.toFixed(3)}`);
    console.log(`[PC]   userData.width = ${el.userData?.width?.toFixed(4) || 'brak'}m`);
    console.log(`[PC]   userData.baseWidth = ${el.userData?.baseWidth?.toFixed(4) || 'brak'}m`);
    console.log(`[PC]   userData.widthMultiplier = ${el.userData?.widthMultiplier || 'brak'}`);
    console.log(`[PC]   closingWidthMultiplier (użyty) = ${closingWidthMultiplier}`);

    // 🎯 OBLICZAM RZECZYWISTĄ SZEROKOŚĆ MODELU (LOCAL SPACE)
    // Nowo utworzony element ma userData.baseWidth lub userData.width ustawione poprawnie
    console.log(`\n[PC] 🎯 OBLICZAM RZECZYWISTĄ SZEROKOŚĆ MODELU (LOCAL SPACE):`);
    if (!existingSection) {
      const realModelWidth = el.userData?.baseWidth || el.userData?.width;
      console.log(`[PC]   BRAK existingSection - używam szerokości z userData nowo utworzonej sekcji:`);
      console.log(`[PC]     userData.baseWidth = ${el.userData?.baseWidth?.toFixed(4) || 'brak'}m`);
      console.log(`[PC]     userData.width = ${el.userData?.width?.toFixed(4) || 'brak'}m`);
      console.log(`[PC]     realModelWidth (z userData) = ${realModelWidth?.toFixed(4) || 'brak'}m`);
      console.log(`[PC]     poprzednie modelBaseWidth = ${modelBaseWidth.toFixed(4)}m`);
      
      // Jeśli rzeczywista szerokość modelu jest znacznie inna niż modelBaseWidth, użyj rzeczywistej
      if (realModelWidth && Math.abs(realModelWidth - modelBaseWidth) > 0.01) {
        console.log(`[PC]     🔧 KOREKTA: różnica > 0.01m, używam realModelWidth`);
        console.log(`[PC]       różnica = ${Math.abs(realModelWidth - modelBaseWidth).toFixed(4)}m`);
        modelBaseWidth = realModelWidth;
        // Przelicz scaleX z nową bazową szerokością
        // 🎯 DLA S38: targetGap już został zwiększony o 3x, więc scaleX będzie automatycznie 3x większy
        const oldScaleX = scaleX;
        scaleX = targetGap / modelBaseWidth;
        console.log(`[PC]       stary scaleX = ${oldScaleX.toFixed(3)}`);
        console.log(`[PC]       nowy scaleX = ${targetGap.toFixed(4)}m / ${modelBaseWidth.toFixed(4)}m = ${scaleX.toFixed(3)}`);
      } else if (!realModelWidth) {
        console.log(`[PC]     ⚠️ userData.baseWidth i userData.width nie są ustawione!`);
      } else {
        console.log(`[PC]     ✅ modelBaseWidth jest już poprawny (różnica < 0.01m), nie ma potrzeby korekty`);
        console.log(`[PC]       różnica = ${Math.abs(realModelWidth - modelBaseWidth).toFixed(4)}m`);
      }
    } else {
      console.log(`[PC]   existingSection istnieje - modelBaseWidth już obliczone wcześniej`);
      console.log(`[PC]     modelBaseWidth = ${modelBaseWidth.toFixed(4)}m`);
    }

    // 7. ZASTOSUJ TRANSFORMACJE (pierwsze skalowanie)
    el.position.set(exactCenterX, 0, exactCenterZ);
    el.rotation.y = targetRotation;
    el.scale.x = scaleX;
    
    // 🎯 DLA S38: DODATKOWE WYDŁUŻENIE PO USTAWIENIU scale.x - NIE POTRZEBNE, już mamy 3x na początku
    // if (isMuroS38ForScale) {
    //   const additionalS38Multiplier = 1.15; // Dodatkowe 15% wydłużenia
    //   el.scale.x = el.scale.x * additionalS38Multiplier;
    //   scaleX = el.scale.x;
    //   console.log(`[PC] 🎯 S38 DODATKOWE WYDŁUŻENIE (po ustawieniu): scale.x × ${additionalS38Multiplier} = ${el.scale.x.toFixed(3)}`);
    // }
    
    el.updateMatrixWorld(true);
    
    // 7.5. 🎯 ROYAL/MURO KOREKTA METALU (PRZED korektą skalowania!)
    // Aplikuj Royal correction najpierw, bo zmienia szerokość elementu
    console.log(`\n[PC] 🔧 ROYAL/MURO CORRECTION - PRZED:`);
    console.log(`[PC]   📋 WYWOŁANIE applyRoyalMetalCorrection:`);
    console.log(`[PC]      fenceConfigParam.id = "${fenceConfigParam?.id}"`);
    console.log(`[PC]      variant = "${variant}"`);
    console.log(`[PC]      selectedVariant = "${selectedVariant}"`);
    const bboxBeforeRoyal = new THREE.Box3().setFromObject(el);
    console.log(`[PC]   bbox.width = ${(bboxBeforeRoyal.max.x - bboxBeforeRoyal.min.x).toFixed(4)}m`);
    console.log(`[PC]   el.scale.x = ${el.scale.x.toFixed(4)}`);
    
    this.applyRoyalMetalCorrection(el, fenceConfigParam, variant);
    el.updateMatrixWorld(true);
    
    console.log(`[PC] 🔧 ROYAL/MURO CORRECTION - PO:`);
    const bboxAfterRoyal = new THREE.Box3().setFromObject(el);
    console.log(`[PC]   bbox.width = ${(bboxAfterRoyal.max.x - bboxAfterRoyal.min.x).toFixed(4)}m`);
    console.log(`[PC]   el.scale.x = ${el.scale.x.toFixed(4)}`);
    console.log(`[PC]   różnica = ${((bboxAfterRoyal.max.x - bboxAfterRoyal.min.x) - (bboxBeforeRoyal.max.x - bboxBeforeRoyal.min.x)).toFixed(4)}m`);
    
    // 🔍 KOREKTA SKALOWANIA (dla modeli z metalem - PO Royal correction)
    // Po zastosowaniu skalowania zmierz rzeczywistą szerokość i skoryguj jeśli potrzeba
    console.log(`\n[PC] 🔍 KOREKTA SKALOWANIA (iteracyjna - używam REALWIDTH z projekcji):`);
    console.log(`[PC]   Po Royal correction i pierwszym skalowaniu...`);
    
    // 🎯 UŻYWAM REALWIDTH z getWorldEdges zamiast bbox actualWidth (dla większości systemów)
    const edgesBeforeCorrection = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
    let realWidthBeforeCorrection = edgesBeforeCorrection.left.distanceTo(edgesBeforeCorrection.right);
    
    const bboxAfterFirstScale = new THREE.Box3().setFromObject(el);
    const bboxWidth = bboxAfterFirstScale.max.x - bboxAfterFirstScale.min.x;
    const expectedWidth = targetGap;


    // 🎯 SPECJALNIE DLA SEKCJI ZAMYKAJĄCEJ P22/P38/S22/S38 MURO MIX:
    // Używaj bboxWidth jako długości do dopasowania - chcemy, żeby WIDOCZNE bloczki miały długość targetGap.
    // 🎯 Dla S38 w sekcji zamykającej: użyj logiki P22
    const isMuroMixLocal_Closing = fenceConfigParam?.id?.startsWith('lupane-muro');
    const isMuroP22Local_Closing = fenceConfigParam?.id === 'lupane-muro-p22' || (isMuroMixLocal_Closing && (selectedVariant === 'p22' || selectedVariant === 's38')); // 🎯 S38 używa logiki P22
    const isMuroP38Local_Closing = fenceConfigParam?.id === 'lupane-muro-p38' || (isMuroMixLocal_Closing && selectedVariant === 'p38');
    const isMuroS22Local_Closing = fenceConfigParam?.id === 'lupane-muro-s22' || (isMuroMixLocal_Closing && selectedVariant === 's22');
    const isMuroS38Local_Closing = isMuroP22Local_Closing; // 🎯 S38 używa dokładnie tej samej logiki co P22

    if (el.userData?.isClosingSection && (isMuroP22Local_Closing || isMuroP38Local_Closing || isMuroS22Local_Closing || isMuroS38Local_Closing)) {
      let variantName = "Unknown";
      if (isMuroP22Local_Closing || (fenceConfigParam?.id === 'lupane-muro-s38' || (isMuroMixLocal_Closing && selectedVariant === 's38'))) variantName = isMuroP22Local_Closing ? "P22" : "S38 (używa P22)";
      else if (isMuroP38Local_Closing) variantName = "P38";
      else if (isMuroS22Local_Closing) variantName = "S22";

      // DLA WSZYSTKICH WARIANTÓW (P22, P38, S22, S38) - UŻYWAJ BBOXWIDTH
      console.log(`[PC] ${variantName} MURO MIX sekcja zamykająca - używam bboxWidth ${bboxWidth.toFixed(4)}m zamiast realWidth ${realWidthBeforeCorrection.toFixed(4)}m do korekty skalowania`);
      realWidthBeforeCorrection = bboxWidth;
    }

    const widthError = realWidthBeforeCorrection - expectedWidth;

    console.log(`[PC] ⚙️ ITERACJA 0 przed korektą`);
    console.log(`[PC]    bbox.min.x: ${bboxAfterFirstScale.min.x.toFixed(4)}m`);
    console.log(`[PC]    bbox.max.x: ${bboxAfterFirstScale.max.x.toFixed(4)}m`);
    console.log(`[PC]    bbox width: ${bboxWidth.toFixed(4)}m`);
    console.log(`[PC]    realWidth (z projekcji!): ${realWidthBeforeCorrection.toFixed(4)}m`);
    console.log(`[PC]    różnica realWidth - bbox: ${(realWidthBeforeCorrection - bboxWidth).toFixed(4)}m`);
    console.log(`[PC]    targetGap (expected): ${expectedWidth.toFixed(4)}m`);
    console.log(`[PC]    błąd: ${widthError.toFixed(4)}m (${((widthError / expectedWidth) * 100).toFixed(1)}%)`);
    console.log(`[PC]    scaleX: ${scaleX.toFixed(3)}`);

    // POPRAWKA: Obniżony próg korekty z 2% na 0.5% lub absolutny błąd 5mm dla większej precyzji
    const relativeError = Math.abs(widthError) / expectedWidth;
    const absoluteError = Math.abs(widthError);
    let needsCorrection = relativeError > 0.005 || absoluteError > 0.005; // 0.5% lub 5mm

    // DLA WSZYSTKICH WARIANTÓW MURO (P22, P38, S22, S38) - ZAWSZE WYMUSZAMY KOREKTĘ
    // 🎯 Dla S38 w sekcji zamykającej: użyj logiki P22
    if (el.userData?.isClosingSection && (isMuroP22Local_Closing || isMuroP38Local_Closing || isMuroS22Local_Closing || isMuroS38Local_Closing)) {
      needsCorrection = true;
      let variantName = "Unknown";
      if (isMuroP22Local_Closing || (fenceConfigParam?.id === 'lupane-muro-s38' || (isMuroMixLocal_Closing && selectedVariant === 's38'))) variantName = isMuroP22Local_Closing ? "P22" : "S38 (używa P22)";
      else if (isMuroP38Local_Closing) variantName = "P38";
      else if (isMuroS22Local_Closing) variantName = "S22";
      console.log(`[PC]   🔴 ${variantName} MURO MIX - WYMUSZAM KOREKTĘ (niezależnie od needsCorrection)`);
    }

    // ⚠️ ROYAL S50 NAJPIERW: sprawdź czy bbox nie wystaje (niezależnie od needsCorrection)
    const isRoyalS50Local = fenceConfigParam?.id === 'royals50' || 
      fenceConfigParam?.id === 'royal-mix' || 
      (fenceConfigParam?.id?.includes('royal') && selectedVariant === 's50');

    if (isRoyalS50Local && el.userData?.isClosingSection) {
      const bboxCheck = new THREE.Box3().setFromObject(el);
      const bboxWidthCheck = bboxCheck.max.x - bboxCheck.min.x;
      const minPostX = Math.min(first.position.x, last.position.x);
      const maxPostX = Math.max(first.position.x, last.position.x);
      const postRangeX = Math.abs(maxPostX - minPostX);
      const overhangLeft = minPostX - bboxCheck.min.x;
      const overhangRight = bboxCheck.max.x - maxPostX;
      const totalBboxOverhang = Math.max(0, -overhangLeft) + Math.max(0, overhangRight);

      console.log(`[PC] 🔍 ROYAL S50 Sprawdzam bbox PRZED korektą skalowania`);
      console.log(`[PC]   Bbox szerokość: ${bboxWidthCheck.toFixed(4)}m`);
      console.log(`[PC]   Zakres słupków: ${postRangeX.toFixed(4)}m`);
      console.log(`[PC]   Wystaje: lewo=${Math.max(0, -overhangLeft * 1000).toFixed(1)}mm, prawo=${Math.max(0, overhangRight * 1000).toFixed(1)}mm, razem=${(totalBboxOverhang * 1000).toFixed(1)}mm`);

      if (totalBboxOverhang > 0.01) { // Bbox wystaje >10mm
        const targetBboxWidth = postRangeX * 0.97;
        const bboxScaleFactor = targetBboxWidth / bboxWidthCheck;
        const adjustedScaleX = scaleX * bboxScaleFactor;

        console.log(`[PC] ⚠️ ROYAL S50 Zmniejszam scale.x żeby bbox się zmieścił`);
        console.log(`[PC]   Docelowa bbox szerokość: ${targetBboxWidth.toFixed(4)}m (97% zakresu słupków)`);
        console.log(`[PC]   Współczynnik: ${bboxScaleFactor.toFixed(4)}`);
        console.log(`[PC]   scale.x: ${scaleX.toFixed(3)} → ${adjustedScaleX.toFixed(3)}`);

        scaleX = adjustedScaleX;
        el.scale.x = adjustedScaleX;
        el.updateMatrixWorld(true);

        const bboxAfterAdjustment = new THREE.Box3().setFromObject(el);
        const newBboxWidth = bboxAfterAdjustment.max.x - bboxAfterAdjustment.min.x;
        console.log(`[PC] ✅ PO KOREKCIE BBOX: nowy bbox width = ${newBboxWidth.toFixed(4)}m`);
      }
    }

    if (needsCorrection) {
      console.log(`[PC] ✅ ZASTOSUJ KOREKTĘ (błąd ${(relativeError * 100).toFixed(2)}% lub ${(absoluteError * 1000).toFixed(1)}mm > próg)`);

      let correctionFactor = expectedWidth / realWidthBeforeCorrection;

      // DLA WSZYSTKICH WARIANTÓW MURO (P22, P38, S22, S38) - UŻYWAMY NORMALNEGO correctionFactor BEZ MNOŻNIKÓW
      // 🎯 Dla S38 w sekcji zamykającej: użyj logiki P22
      if (el.userData?.isClosingSection && (isMuroP22Local_Closing || isMuroP38Local_Closing || isMuroS22Local_Closing || isMuroS38Local_Closing)) {
        let variantName = "Unknown";
        if (isMuroP22Local_Closing || (fenceConfigParam?.id === 'lupane-muro-s38' || (isMuroMixLocal_Closing && selectedVariant === 's38'))) variantName = isMuroP22Local_Closing ? "P22" : "S38 (używa P22)";
        else if (isMuroP38Local_Closing) variantName = "P38";
        else if (isMuroS22Local_Closing) variantName = "S22";
        
        console.log(`[PC]   🎯 ${variantName} MURO MIX - KOREKTA STANDARDOWA (bez dodatkowych mnożników):`);
        console.log(`[PC]     expectedWidth: ${expectedWidth.toFixed(4)}m`);
        console.log(`[PC]     realWidthBeforeCorrection (bboxWidth): ${realWidthBeforeCorrection.toFixed(4)}m`);
        console.log(`[PC]     correctionFactor: ${correctionFactor.toFixed(4)}`);
      }

      const oldScaleX = scaleX;
      const newScaleX = scaleX * correctionFactor;
      // 🎯 DLA S38: targetGap już został zwiększony o 3x, więc scaleX będzie automatycznie 3x większy po correctionFactor

      console.log(`[PC] correctionFactor ${expectedWidth.toFixed(4)}m / ${realWidthBeforeCorrection.toFixed(4)}m = ${correctionFactor.toFixed(4)}`);
      console.log(`[PC] poprzedni scaleX: ${oldScaleX.toFixed(3)}`);
      console.log(`[PC] nowy scaleX: ${oldScaleX.toFixed(3)} * ${correctionFactor.toFixed(4)} = ${newScaleX.toFixed(3)}`);
      
      // 🎯 DLA S38: WERYFIKACJA przed i po korekcie
      if (isMuroS38ForVerification) {
        console.log(`[PC] 🔍 S38 WERYFIKACJA przed korektą correctionFactor:`);
        console.log(`[PC]     expectedWidth (targetGap) = ${expectedWidth.toFixed(4)}m`);
        console.log(`[PC]     realWidthBeforeCorrection = ${realWidthBeforeCorrection.toFixed(4)}m`);
        console.log(`[PC]     correctionFactor = ${correctionFactor.toFixed(4)}`);
        console.log(`[PC]     stary scaleX = ${oldScaleX.toFixed(4)}`);
        console.log(`[PC]     nowy scaleX = ${newScaleX.toFixed(4)}`);
      }

      scaleX = newScaleX;
      el.scale.x = scaleX;
      
      // 🎯 DLA S38: WERYFIKACJA po korekcie correctionFactor
      if (isMuroS38ForVerification) {
        console.log(`[PC] 🔍 S38 WERYFIKACJA po korekcie correctionFactor:`);
        console.log(`[PC]     el.scale.x (ustawiony) = ${el.scale.x.toFixed(4)}`);
        console.log(`[PC]     scaleX (zmienna) = ${scaleX.toFixed(4)}`);
        console.log(`[PC]     czy są równe? ${Math.abs(el.scale.x - scaleX) < 0.0001 ? '✅ TAK' : '❌ NIE'}`);
      }
      el.updateMatrixWorld(true);

      // KLUCZOWE: Po zmianie scale.x bbox się przesuwa! Muszę skorygować pozycję elementu
      const bboxAfterScaleChange = new THREE.Box3().setFromObject(el);
      const bboxCenterX_afterScale = (bboxAfterScaleChange.min.x + bboxAfterScaleChange.max.x) / 2;
      const bboxCenterZ_afterScale = (bboxAfterScaleChange.min.z + bboxAfterScaleChange.max.z) / 2;
      const positionOffsetX = exactCenterX - bboxCenterX_afterScale;
      const positionOffsetZ = exactCenterZ - bboxCenterZ_afterScale;

      console.log(`[PC] 🔧 KOREKTA POZYCJI po zmianie scale.x`);
      console.log(`[PC]   Bbox center po skalowaniu: (${bboxCenterX_afterScale.toFixed(3)}, ${bboxCenterZ_afterScale.toFixed(3)})`);
      console.log(`[PC]   Docelowe centrum: (${exactCenterX.toFixed(3)}, ${exactCenterZ.toFixed(3)})`);
      console.log(`[PC]   Potrzebne przesunięcie: (${positionOffsetX.toFixed(3)}, ${positionOffsetZ.toFixed(3)})`);

      el.position.x += positionOffsetX;
      el.position.z += positionOffsetZ;
      el.updateMatrixWorld(true);

      console.log(`[PC] ✅ Ustawiono pozycję elementu na (${el.position.x.toFixed(3)}, ${el.position.z.toFixed(3)})`);

      // PONOWNIE zastosuj Royal correction po zmianie skalowania
      console.log(`[PC] 🔁 PONOWNIE zastosuj Royal correction (ITERACJA 1)`);
      this.applyRoyalMetalCorrection(el, fenceConfigParam, variant);
      el.updateMatrixWorld(true);

      // Zmierz ponownie - używam bboxWidth dla wszystkich MURO wariantów (P22, P38, S22, S38)
      const bboxAfterCorrection = new THREE.Box3().setFromObject(el);
      const bboxWidthAfter = bboxAfterCorrection.max.x - bboxAfterCorrection.min.x;
      let realWidthAfterCorrection = bboxWidthAfter;

      // Dla MURO wariantów używamy bboxWidth
      if (el.userData?.isClosingSection && (isMuroP22Local_Closing || isMuroP38Local_Closing || isMuroS22Local_Closing || isMuroS38Local_Closing)) {
        realWidthAfterCorrection = bboxWidthAfter;
      } else {
        // Dla innych systemów używamy realWidth z projekcji
        const edgesAfterCorrection = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
        realWidthAfterCorrection = edgesAfterCorrection.left.distanceTo(edgesAfterCorrection.right);
      }

      const finalError = realWidthAfterCorrection - expectedWidth;

      console.log(`[PC] ⚙️ ITERACJA 1 po korekcie`);
      console.log(`[PC]    bbox width: ${bboxWidthAfter.toFixed(4)}m`);
      console.log(`[PC]    realWidth: ${realWidthAfterCorrection.toFixed(4)}m`);
      console.log(`[PC]    błąd: ${finalError.toFixed(4)}m (${((finalError / expectedWidth) * 100).toFixed(1)}%)`);

      // DODATKOWA ITERACJA jeśli błąd nadal >3mm
      const finalAbsoluteError = Math.abs(finalError);
      const finalRelativeError = Math.abs(finalError) / expectedWidth;

      if ((finalRelativeError > 0.002 || finalAbsoluteError > 0.003) && Math.abs(finalError) < Math.abs(widthError)) {
        console.log(`[PC] 🔁 DODATKOWA ITERACJA (błąd nadal ${(finalRelativeError * 100).toFixed(2)}% lub ${(finalAbsoluteError * 1000).toFixed(1)}mm)`);

        let secondCorrectionFactor = expectedWidth / realWidthAfterCorrection;

        const oldScaleX2 = scaleX;
        const newScaleX2 = scaleX * secondCorrectionFactor;
        // 🎯 DLA S38: targetGap już został zwiększony o 3x, więc scaleX będzie automatycznie 3x większy po drugiej korekcie

        console.log(`[PC] correctionFactor2 ${expectedWidth.toFixed(4)}m / ${realWidthAfterCorrection.toFixed(4)}m = ${secondCorrectionFactor.toFixed(4)}`);
        console.log(`[PC] poprzedni scaleX: ${oldScaleX2.toFixed(3)}`);
        console.log(`[PC] nowy scaleX: ${oldScaleX2.toFixed(3)} * ${secondCorrectionFactor.toFixed(4)} = ${newScaleX2.toFixed(3)}`);

        scaleX = newScaleX2;
        el.scale.x = scaleX;
        el.updateMatrixWorld(true);

        // Korekta pozycji po drugiej zmianie scale.x
        const bboxAfterScale2 = new THREE.Box3().setFromObject(el);
        const bboxCenterX_afterScale2 = (bboxAfterScale2.min.x + bboxAfterScale2.max.x) / 2;
        const bboxCenterZ_afterScale2 = (bboxAfterScale2.min.z + bboxAfterScale2.max.z) / 2;
        const positionOffset2X = exactCenterX - bboxCenterX_afterScale2;
        const positionOffset2Z = exactCenterZ - bboxCenterZ_afterScale2;

        console.log(`[PC] 🔧 KOREKTA POZYCJI po drugiej zmianie scale.x`);
        console.log(`[PC]   Bbox center: (${bboxCenterX_afterScale2.toFixed(3)}, ${bboxCenterZ_afterScale2.toFixed(3)})`);
        console.log(`[PC]   Docelowe centrum: (${exactCenterX.toFixed(3)}, ${exactCenterZ.toFixed(3)})`);
        console.log(`[PC]   Potrzebne przesunięcie: (${positionOffset2X.toFixed(3)}, ${positionOffset2Z.toFixed(3)})`);

        el.position.x += positionOffset2X;
        el.position.z += positionOffset2Z;
        el.updateMatrixWorld(true);

        // PONOWNIE zastosuj Royal correction
        this.applyRoyalMetalCorrection(el, fenceConfigParam, variant);
        el.updateMatrixWorld(true);

        // Zmierz po raz trzeci
        const bboxAfterCorrection2 = new THREE.Box3().setFromObject(el);
        const bboxWidthAfter2 = bboxAfterCorrection2.max.x - bboxAfterCorrection2.min.x;
        let realWidthAfterCorrection2 = bboxWidthAfter2;

        if (el.userData?.isClosingSection && (isMuroP22Local_Closing || isMuroP38Local_Closing || isMuroS22Local_Closing || isMuroS38Local_Closing)) {
          realWidthAfterCorrection2 = bboxWidthAfter2;
        } else {
          const edgesAfterCorrection2 = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
          realWidthAfterCorrection2 = edgesAfterCorrection2.left.distanceTo(edgesAfterCorrection2.right);
        }

        const finalError2 = realWidthAfterCorrection2 - expectedWidth;

        console.log(`[PC] ⚙️ ITERACJA 2 po drugiej korekcie`);
        console.log(`[PC]    bbox width: ${bboxWidthAfter2.toFixed(4)}m`);
        console.log(`[PC]    realWidth: ${realWidthAfterCorrection2.toFixed(4)}m`);
        console.log(`[PC]    błąd: ${finalError2.toFixed(4)}m (${((finalError2 / expectedWidth) * 100).toFixed(1)}%)`);
        console.log(`[PC] ✅ Druga korekta zakończona`);
      } else {
        console.log(`[PC] ✅ Korekta zakończona`);
      }
    } else {
      console.log(`[PC] ⏭️ Błąd <0.5% i <5mm, nie ma potrzeby korekty`);
    }





    // 🎯 PROSTA KOREKTA DLA METALU W SEKCJI ZAMYKAJĄCEJ ROYAL MIX S50 / P50
    // Po wszystkich obliczeniach po prostu przesuń metal dalej wzdłuż jego lokalnej osi X (w przeciwną stronę).
    if (
      (
        fenceConfigParam?.id === 'royal_s50' ||
        fenceConfigParam?.id === 'royal_p50' ||
        (fenceConfigParam?.id === 'royal-mix' && (selectedVariant === 's50' || selectedVariant === 'p50'))
      ) &&
      el.userData?.isClosingSection
    ) {
      const extraOffsetX = -0.145; // 0.145m dalej względem aktualnej pozycji, ale w drugą stronę
      console.log(
        `[PC] 🎯 ROYAL MIX ${selectedVariant?.toUpperCase() || 'S/P50'}: przesuwam METAL w sekcji zamykającej w lokalnej osi X o ${extraOffsetX.toFixed(3)}m`
      );

      el.traverse((child) => {
        const name = child.name || '';
        if (name === 'group_30') {
          const metalMesh = child as THREE.Mesh;
          metalMesh.position.x += extraOffsetX;
          console.log(
            `[PC] 🔧 ROYAL MIX ${selectedVariant?.toUpperCase() || 'S/P50'}: metal "${name}" nowa pozycja localX=${metalMesh.position.x.toFixed(3)}m`
          );
        }
      });

      el.updateMatrixWorld(true);
    }

    // 🎯 PROSTA KOREKTA DLA METALU W SEKCJI ZAMYKAJĄCEJ P22 MURO MIX
    // Po wszystkich obliczeniach po prostu przesuń metal wzdłuż jego lokalnej osi X i Y.
    // 🎯 Dla S38 w sekcji zamykającej: użyj dokładnie tej samej logiki co P22 - traktuj S38 jak P22
    const isMuroMixForMetal = fenceConfigParam?.id?.startsWith('lupane-muro');
    const isMuroP22Closing = (fenceConfigParam?.id === 'lupane-muro-p22') || (isMuroMixForMetal && (selectedVariant === 'p22' || selectedVariant === 's38')); // 🎯 S38 używa logiki P22
    if (isMuroP22Closing && el.userData?.isClosingSection) {
      const extraOffsetX = 0.08; // Wartość przesunięcia w osi X (dodatnia = w prawo, ujemna = w lewo)
      const extraOffsetY = -0.06; // Wartość przesunięcia w osi Y (ujemna = w dół, dodatnia = w górę)
      const variantName = (selectedVariant === 's38') ? 'S38 (używa P22)' : 'P22';
      console.log(
        `[PC] 🎯 ${variantName} MURO MIX: przesuwam METAL w sekcji zamykającej w lokalnej osi X o ${extraOffsetX.toFixed(3)}m i Y o ${extraOffsetY.toFixed(3)}m`
      );

      el.traverse((child) => {
        const name = child.name || '';
        if (name === 'group_0') {
          const metalMesh = child as THREE.Mesh;
          metalMesh.position.x += extraOffsetX;
          metalMesh.position.y += extraOffsetY;
          console.log(
            `[PC] 🔧 ${variantName} MURO MIX: metal "${name}" nowa pozycja localX=${metalMesh.position.x.toFixed(3)}m, localY=${metalMesh.position.y.toFixed(3)}m`
          );
        }
      });

      el.updateMatrixWorld(true);
    }

    // 🎯 Dla P22 i P38 w Muro Mix: NIE ma specjalnego przesunięcia (działa tak samo jak standardowa sekcja zamykająca)
    if (fenceConfigParam?.id !== 'slim' && fenceConfigParam?.id !== 'royal-mix') {
      console.log(`[PC] 🔍 DEBUG: fenceConfigParam.id = "${fenceConfigParam?.id}", selectedVariant = "${selectedVariant}"`);
    }

    // 🎯 PROSTA KOREKTA DLA METALU W SEKCJI ZAMYKAJĄCEJ S22 MURO MIX
    // Po wszystkich obliczeniach po prostu przesuń metal wzdłuż jego lokalnej osi X i Y.
    const isMuroS22Closing = (fenceConfigParam?.id === 'lupane-muro-s22') || 
                             ((fenceConfigParam?.id === 'lupane-muro-mix' || fenceConfigParam?.id?.startsWith('lupane-muro')) && selectedVariant === 's22');
    if (isMuroS22Closing && el.userData?.isClosingSection) {
      const extraOffsetX = 0.08; // Wartość przesunięcia w osi X (dodatnia = w prawo, ujemna = w lewo)
      const extraOffsetY = -0.06; // Wartość przesunięcia w osi Y (ujemna = w dół, dodatnia = w górę)
      console.log(
        `[PC] 🎯 S22 MURO MIX: przesuwam METAL w sekcji zamykającej w lokalnej osi X o ${extraOffsetX.toFixed(3)}m i Y o ${extraOffsetY.toFixed(3)}m`
      );

      el.traverse((child) => {
        const name = child.name || '';
        if (name === 'group_0') {
          const metalMesh = child as THREE.Mesh;
          metalMesh.position.x += extraOffsetX;
          metalMesh.position.y += extraOffsetY;
          console.log(
            `[PC] 🔧 S22 MURO MIX: metal "${name}" nowa pozycja localX=${metalMesh.position.x.toFixed(3)}m, localY=${metalMesh.position.y.toFixed(3)}m`
          );
        }
      });

      el.updateMatrixWorld(true);
    }

    // 🎯 Dla S22 w sekcji zamykającej: obniż blok dekoracyjny o 0.13m
    if (isMuroS22Closing && el.userData?.isClosingSection) {
      console.log(`[PC] 🔧 S22 sekcja zamykająca: Obniżam blok dekoracyjny (group_77) o 0.13m`);
      el.traverse((child) => {
        const name = child.name || '';
        if (name === 'group_77') {
          const decBlock = child as THREE.Mesh;
          const originalY = decBlock.position.y;
          decBlock.position.y -= 0.13;
          console.log(`[PC] 🔧 Blok dekoracyjny (${name}): Y=${originalY.toFixed(3)}m → ${decBlock.position.y.toFixed(3)}m`);
        }
      });
      el.updateMatrixWorld(true);
    }


    // 🎯 FINALNE KRAWĘDZIE (po wszystkich korektach)
    console.log(`\n[PC] 🎯 FINALNE KRAWĘDZIE (po wszystkich korektach):`);
    console.log(`[PC]   Obliczam finalne krawędzie...`);
    const finalEdgesResult = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
    const finalEdges = { left: finalEdgesResult.left, right: finalEdgesResult.right };
    
    console.log(`[PC]   FINALNA POZYCJA elementu:`);
    console.log(`[PC]     pos = (${el.position.x.toFixed(3)}, Y=${el.position.y.toFixed(3)}, ${el.position.z.toFixed(3)})`);
    console.log(`[PC]     rot = ${(el.rotation.y * 180 / Math.PI).toFixed(2)}°`);
    console.log(`[PC]     scale.x = ${el.scale.x.toFixed(3)}`);
    
    console.log(`[PC]   RZECZYWISTE KRAWĘDZIE (3D, z getWorldEdges):`);
    console.log(`[PC]     finalLeft  = (${finalEdges.left.x.toFixed(3)}, Y=${finalEdges.left.y.toFixed(3)}, ${finalEdges.left.z.toFixed(3)})`);
    console.log(`[PC]     finalRight = (${finalEdges.right.x.toFixed(3)}, Y=${finalEdges.right.y.toFixed(3)}, ${finalEdges.right.z.toFixed(3)})`);
    
    console.log(`[PC]   TARGETY (punkty docelowe):`);
    console.log(`[PC]     targetLeft (lastRight)   = (${lastRight.x.toFixed(3)}, Y=${lastRight.y.toFixed(3)}, ${lastRight.z.toFixed(3)})`);
    console.log(`[PC]     targetRight (firstRight) = (${firstRight.x.toFixed(3)}, Y=${firstRight.y.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
    
    // 🔍 ANALIZA ODLEGŁOŚCI DO SŁUPKÓW
    console.log(`\n[PC]   🔍 DYSTANSE DO SŁUPKÓW (w 2D, XZ):`);
    console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
    
    // Prawa krawędź sekcji → pierwszy słupek (first)
    const rightToFirst = new THREE.Vector3().subVectors(
      new THREE.Vector3(first.position.x, 0, first.position.z),
      new THREE.Vector3(finalEdges.right.x, 0, finalEdges.right.z)
    );
    console.log(`[PC]   PRAWA krawędź sekcji → PIERWSZY słupek:`);
    console.log(`[PC]     finalRight = (${finalEdges.right.x.toFixed(3)}, ${finalEdges.right.z.toFixed(3)})`);
    console.log(`[PC]     first.pos = (${first.position.x.toFixed(3)}, ${first.position.z.toFixed(3)})`);
    console.log(`[PC]     Wektor: (${rightToFirst.x.toFixed(4)}, ${rightToFirst.z.toFixed(4)})`);
    console.log(`[PC]     Dystans: ${rightToFirst.length().toFixed(4)}m = ${(rightToFirst.length() * 1000).toFixed(1)}mm`);
    
    // Lewa krawędź sekcji → ostatni słupek (last)
    const leftToLast = new THREE.Vector3().subVectors(
      new THREE.Vector3(last.position.x, 0, last.position.z),
      new THREE.Vector3(finalEdges.left.x, 0, finalEdges.left.z)
    );
    console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
    console.log(`[PC]   LEWA krawędź sekcji → OSTATNI słupek:`);
    console.log(`[PC]     finalLeft = (${finalEdges.left.x.toFixed(3)}, ${finalEdges.left.z.toFixed(3)})`);
    console.log(`[PC]     last.pos = (${last.position.x.toFixed(3)}, ${last.position.z.toFixed(3)})`);
    console.log(`[PC]     Wektor: (${leftToLast.x.toFixed(4)}, ${leftToLast.z.toFixed(4)})`);
    console.log(`[PC]     Dystans: ${leftToLast.length().toFixed(4)}m = ${(leftToLast.length() * 1000).toFixed(1)}mm`);
    console.log(`[PC]   ═══════════════════════════════════════════════════════════`);
    
    // 🎯 Porównuj tylko w płaszczyźnie XZ (ustaw Y=0 dla obu)
    console.log(`\n[PC]   Porównanie w 2D (XZ, Y=0):`);
    const finalLeft2D = new THREE.Vector3(finalEdges.left.x, 0, finalEdges.left.z);
    const finalRight2D = new THREE.Vector3(finalEdges.right.x, 0, finalEdges.right.z);
    const lastRight2D = new THREE.Vector3(lastRight.x, 0, lastRight.z);
    const firstRight2D = new THREE.Vector3(firstRight.x, 0, firstRight.z);
    
    // 🎯 Użyj let, żeby móc zaktualizować po wszystkich korektach
    let finalLeftErr = finalLeft2D.distanceTo(lastRight2D);
    let finalRightErr = finalRight2D.distanceTo(firstRight2D);

    console.log(`[PC]   SZCZEGÓŁOWA ANALIZA BŁĘDÓW:`);
    console.log(`[PC]   ═══════════════════════════════════════════════════════════`);
    
    // LEWA KRAWĘDŹ
    const dx_left = finalLeft2D.x - lastRight2D.x;
    const dz_left = finalLeft2D.z - lastRight2D.z;
    console.log(`[PC]   🔴 LEWA KRAWĘDŹ (finalLeft vs targetLeft=lastRight):`);
    console.log(`[PC]     finalLeft    = (${finalLeft2D.x.toFixed(4)}, ${finalLeft2D.z.toFixed(4)})`);
    console.log(`[PC]     targetLeft   = (${lastRight2D.x.toFixed(4)}, ${lastRight2D.z.toFixed(4)})`);
    console.log(`[PC]     WEKTOR BŁĘDU (target - actual):`);
    console.log(`[PC]       dx = ${(-dx_left).toFixed(4)}m = ${(-dx_left * 1000).toFixed(1)}mm`);
    console.log(`[PC]       dz = ${(-dz_left).toFixed(4)}m = ${(-dz_left * 1000).toFixed(1)}mm`);
    console.log(`[PC]     DYSTANS = ${finalLeftErr.toFixed(4)}m = ${(finalLeftErr * 1000).toFixed(1)}mm`);
    if (dx_left < 0) {
      console.log(`[PC]     ➡️  Krawędź jest ZA DALEKO W LEWO (w kierunku -X)`);
    } else if (dx_left > 0) {
      console.log(`[PC]     ⬅️  Krawędź jest ZA DALEKO W PRAWO (w kierunku +X)`);
    }
    if (dz_left < 0) {
      console.log(`[PC]     ⬆️  Krawędź jest ZA DALEKO W DÓŁ (w kierunku -Z)`);
    } else if (dz_left > 0) {
      console.log(`[PC]     ⬇️  Krawędź jest ZA DALEKO W GÓRĘ (w kierunku +Z)`);
    }
    
    console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
    
    // PRAWA KRAWĘDŹ
    const dx_right = finalRight2D.x - firstRight2D.x;
    const dz_right = finalRight2D.z - firstRight2D.z;
    console.log(`[PC]   🟢 PRAWA KRAWĘDŹ (finalRight vs targetRight=firstRight):`);
    console.log(`[PC]     finalRight   = (${finalRight2D.x.toFixed(4)}, ${finalRight2D.z.toFixed(4)})`);
    console.log(`[PC]     targetRight  = (${firstRight2D.x.toFixed(4)}, ${firstRight2D.z.toFixed(4)})`);
    console.log(`[PC]     WEKTOR BŁĘDU (target - actual):`);
    console.log(`[PC]       dx = ${(-dx_right).toFixed(4)}m = ${(-dx_right * 1000).toFixed(1)}mm`);
    console.log(`[PC]       dz = ${(-dz_right).toFixed(4)}m = ${(-dz_right * 1000).toFixed(1)}mm`);
    console.log(`[PC]     DYSTANS = ${finalRightErr.toFixed(4)}m = ${(finalRightErr * 1000).toFixed(1)}mm`);
    if (dx_right < 0) {
      console.log(`[PC]     ➡️  Krawędź jest ZA DALEKO W LEWO (w kierunku -X)`);
    } else if (dx_right > 0) {
      console.log(`[PC]     ⬅️  Krawędź jest ZA DALEKO W PRAWO (w kierunku +X)`);
    }
    if (dz_right < 0) {
      console.log(`[PC]     ⬆️  Krawędź jest ZA DALEKO W DÓŁ (w kierunku -Z)`);
    } else if (dz_right > 0) {
      console.log(`[PC]     ⬇️  Krawędź jest ZA DALEKO W GÓRĘ (w kierunku +Z)`);
    }
    
    // 🎯 DODATKOWA KOREKTA POZYCJI TYLKO DLA SEKCJI ZAMYKAJĄCEJ P22/P38/S22/S38 (MURO MIX):
    // Ustaw sekcję tak, żeby finalRight == firstRight i finalLeft == lastRight (w granicach dokładności).
    // 🎯 Dla S38 w sekcji zamykającej: użyj logiki P22
    const isMuroMixLocal_Pos = fenceConfigParam?.id?.startsWith('lupane-muro');
    const isMuroP22Local_Pos =
      fenceConfigParam?.id === 'lupane-muro-p22' || (isMuroMixLocal_Pos && (selectedVariant === 'p22' || selectedVariant === 's38')); // 🎯 S38 używa logiki P22
    const isMuroP38Local_Pos =
      fenceConfigParam?.id === 'lupane-muro-p38' || (isMuroMixLocal_Pos && selectedVariant === 'p38');
    const isMuroS22Local_Pos =
      fenceConfigParam?.id === 'lupane-muro-s22' || (isMuroMixLocal_Pos && selectedVariant === 's22');
    const isMuroS38Local_Pos = isMuroP22Local_Pos; // 🎯 S38 używa dokładnie tej samej logiki co P22
    if (el.userData?.isClosingSection && (isMuroP22Local_Pos || isMuroP38Local_Pos || isMuroS22Local_Pos || isMuroS38Local_Pos)) {
      let variantName = 'Unknown';
      if (isMuroP22Local_Pos || (fenceConfigParam?.id === 'lupane-muro-s38' || (isMuroMixLocal_Pos && selectedVariant === 's38'))) variantName = isMuroP22Local_Pos ? 'P22' : 'S38 (używa P22)';
      else if (isMuroP38Local_Pos) variantName = 'P38';
      else if (isMuroS22Local_Pos) variantName = 'S22';
      console.log(`[PC]   🎯 ${variantName} MURO MIX (sekcja zamykająca): PROSTA KOREKTA (jak dla gladkie_long)`);
      
      // 🎯 UŻYJ TEJ SAMEJ PROSTEJ LOGIKI CO DLA GLADKIE_LONG
      // Oblicz kierunek sekcji (od finalRight do finalLeft)
      const sectionDirection = new THREE.Vector3().subVectors(finalLeft2D, finalRight2D).normalize();
      
      // Oblicz projekcje błędów na kierunek sekcji
      const leftErrorVec = new THREE.Vector3().subVectors(lastRight2D, finalLeft2D);
      const rightErrorVec = new THREE.Vector3().subVectors(firstRight2D, finalRight2D);
      
      const leftProjection = leftErrorVec.dot(sectionDirection);
      const rightProjection = rightErrorVec.dot(sectionDirection);
      
      const actualSectionLength = finalLeft2D.distanceTo(finalRight2D);
      
      console.log(`[PC]     Kierunek sekcji: (${sectionDirection.x.toFixed(4)}, ${sectionDirection.z.toFixed(4)})`);
      console.log(`[PC]     Projekcje błędów na kierunek sekcji:`);
      console.log(`[PC]       LEWA: ${(leftProjection * 1000).toFixed(1)}mm ${leftProjection > 0 ? '(ZA KRÓTKA - brakuje)' : '(ZA DŁUGA - wystaje)'}`);
      console.log(`[PC]       PRAWA: ${(rightProjection * 1000).toFixed(1)}mm ${rightProjection < 0 ? '(ZA KRÓTKA - brakuje)' : '(ZA DŁUGA - wystaje)'}`);
      
      // Sprawdź czy krawędzie nie sięgają do targetów (projekcje > 0 dla lewej, < 0 dla prawej)
      const leftTooShort = leftProjection > 0.0005; // 0.5mm
      const rightTooShort = rightProjection < -0.0005; // 0.5mm
      
      // Sprawdź czy krawędzie wystają poza targety
      const leftTooLong = leftProjection < -0.0005; // 0.5mm
      const rightTooLong = rightProjection > 0.0005; // 0.5mm
      
      if (leftTooShort || rightTooShort || leftTooLong || rightTooLong) {
        // Oblicz ile dokładnie brakuje lub wystaje
        let neededExtension = 0;
        
        if (leftTooShort && rightTooShort) {
          // Obie krawędzie za krótkie - wydłuż sekcję
          neededExtension = Math.max(leftProjection, -rightProjection);
          console.log(`[PC]   🔧 OBE KRAWĘDZIE ZA KRÓTKIE - wydłużam sekcję o ${(neededExtension * 1000).toFixed(1)}mm`);
        } else if (leftTooShort) {
          // Tylko lewa za krótka
          neededExtension = leftProjection;
          console.log(`[PC]   🔧 LEWA KRAWĘDŹ ZA KRÓTKA - wydłużam sekcję o ${(neededExtension * 1000).toFixed(1)}mm`);
        } else if (rightTooShort) {
          // Tylko prawa za krótka
          neededExtension = -rightProjection;
          console.log(`[PC]   🔧 PRAWA KRAWĘDŹ ZA KRÓTKA - wydłużam sekcję o ${(neededExtension * 1000).toFixed(1)}mm`);
        } else if (leftTooLong && rightTooLong) {
          // Obie krawędzie za długie - skróć sekcję
          neededExtension = Math.min(leftProjection, -rightProjection);
          console.log(`[PC]   🔧 OBE KRAWĘDZIE ZA DŁUGIE - skracam sekcję o ${(Math.abs(neededExtension) * 1000).toFixed(1)}mm`);
        } else if (leftTooLong) {
          // Tylko lewa za długa
          neededExtension = leftProjection;
          console.log(`[PC]   🔧 LEWA KRAWĘDŹ ZA DŁUGA - skracam sekcję o ${(Math.abs(neededExtension) * 1000).toFixed(1)}mm`);
        } else if (rightTooLong) {
          // Tylko prawa za długa
          neededExtension = -rightProjection;
          console.log(`[PC]   🔧 PRAWA KRAWĘDŹ ZA DŁUGA - skracam sekcję o ${(Math.abs(neededExtension) * 1000).toFixed(1)}mm`);
        }
        
        if (Math.abs(neededExtension) > 0.0001) { // > 0.1mm
          // 🎯 ITERACYJNA KOREKTA - powtarzaj aż będzie idealnie (bo applyRoyalMetalCorrection może zmienić geometrię)
          const targetLength = lastRight2D.distanceTo(firstRight2D);
          let maxIterations = 10;
          let iteration = 0;
          
          while (iteration < maxIterations) {
            iteration++;
            
            // Oblicz aktualną długość i różnicę względem targetu
            const edgesCurr = this.getWorldEdges(el, baseWidth, el.scale.x, fenceConfigParam, selectedVariant);
            const leftCurr = new THREE.Vector3(edgesCurr.left.x, 0, edgesCurr.left.z);
            const rightCurr = new THREE.Vector3(edgesCurr.right.x, 0, edgesCurr.right.z);
            const actualLengthCurr = leftCurr.distanceTo(rightCurr);
            const lengthDiff = targetLength - actualLengthCurr;
            
            // Oblicz projekcje błędów
            const leftErrorVecCurr = new THREE.Vector3().subVectors(lastRight2D, leftCurr);
            const rightErrorVecCurr = new THREE.Vector3().subVectors(firstRight2D, rightCurr);
            const sectionDirCurr = new THREE.Vector3().subVectors(leftCurr, rightCurr).normalize();
            const leftProjCurr = leftErrorVecCurr.dot(sectionDirCurr);
            const rightProjCurr = rightErrorVecCurr.dot(sectionDirCurr);
            const leftTooShortCurr = leftProjCurr > 0.0005;
            const rightTooShortCurr = rightProjCurr < -0.0005;
            
            console.log(`[PC]     ITERACJA ${iteration}: długość = ${actualLengthCurr.toFixed(4)}m, target = ${targetLength.toFixed(4)}m, różnica = ${(lengthDiff * 1000).toFixed(1)}mm`);
            console.log(`[PC]       LEWA proj = ${(leftProjCurr * 1000).toFixed(1)}mm, PRAWA proj = ${(rightProjCurr * 1000).toFixed(1)}mm`);
            
            // Jeśli błędy są małe, zakończ
            if (Math.abs(lengthDiff) < 0.001 && !leftTooShortCurr && !rightTooShortCurr) {
              console.log(`[PC]     ✅ ITERACJA ${iteration}: Krawędzie idealne, kończę`);
              finalLeft2D.set(leftCurr.x, 0, leftCurr.z);
              finalRight2D.set(rightCurr.x, 0, rightCurr.z);
              break;
            }
            
            // Oblicz potrzebne wydłużenie - używaj BEZPOŚREDNIO różnicy długości
            let neededExtCurr = 0;
            if (Math.abs(lengthDiff) > 0.001) {
              // Jeśli różnica długości jest duża, użyj jej bezpośrednio
              neededExtCurr = lengthDiff;
              console.log(`[PC]       🔧 Używam różnicy długości: ${(neededExtCurr * 1000).toFixed(1)}mm`);
            } else {
              // Jeśli różnica jest mała, użyj projekcji
              if (leftTooShortCurr && rightTooShortCurr) {
                neededExtCurr = Math.max(leftProjCurr, -rightProjCurr);
              } else if (leftTooShortCurr) {
                neededExtCurr = leftProjCurr;
              } else if (rightTooShortCurr) {
                neededExtCurr = -rightProjCurr;
              }
              console.log(`[PC]       🔧 Używam projekcji: ${(neededExtCurr * 1000).toFixed(1)}mm`);
            }
            
            // 🎯 Dla S38 MURO MIX w sekcji zamykającej: użyj logiki P22 (bez dodatkowego mnożnika)
            
            if (Math.abs(neededExtCurr) < 0.0001) {
              console.log(`[PC]     ✅ ITERACJA ${iteration}: Brak potrzeby korekty, kończę`);
              finalLeft2D.set(leftCurr.x, 0, leftCurr.z);
              finalRight2D.set(rightCurr.x, 0, rightCurr.z);
              break;
            }
            
            // Skaluj sekcję
            const newLength = actualLengthCurr + neededExtCurr;
            if (newLength <= 0 || actualLengthCurr <= 0) {
              console.error(`[PC]     ❌ BŁĄD: newLength=${newLength}, actualLengthCurr=${actualLengthCurr}, przerywam`);
              break;
            }
            const scaleFactor = newLength / actualLengthCurr;
            if (scaleFactor <= 0 || scaleFactor > 10 || scaleFactor < 0.1) {
              console.error(`[PC]     ❌ BŁĄD: scaleFactor=${scaleFactor}, przerywam`);
              break;
            }
            
            el.scale.x *= scaleFactor;
            scaleX = el.scale.x;
            el.updateMatrixWorld(true);
            
            // Zastosuj Royal/Muro correction
            this.applyRoyalMetalCorrection(el, fenceConfigParam, variant);
            el.updateMatrixWorld(true);
            
            // 🎯 OBLICZ KRAWĘDZIE PO SKALOWANIU I KOREKCIE - używaj ich do korekty pozycji
            const edgesAfterScale = this.getWorldEdges(el, baseWidth, el.scale.x, fenceConfigParam, selectedVariant);
            const leftAfterScale = new THREE.Vector3(edgesAfterScale.left.x, 0, edgesAfterScale.left.z);
            const rightAfterScale = new THREE.Vector3(edgesAfterScale.right.x, 0, edgesAfterScale.right.z);
            
            // Korekta pozycji - używaj krawędzi PO skalowaniu
            const leftErr = new THREE.Vector3().subVectors(lastRight, leftAfterScale);
            const rightErr = new THREE.Vector3().subVectors(firstRight, rightAfterScale);
            const posCorrection = new THREE.Vector3().addVectors(leftErr, rightErr).multiplyScalar(0.5);
            
            console.log(`[PC]       🔧 KOREKTA POZYCJI (iteracja ${iteration}):`);
            console.log(`[PC]         lastRight (target lewej) = (${lastRight.x.toFixed(3)}, ${lastRight.z.toFixed(3)})`);
            console.log(`[PC]         leftAfterScale (rzeczywista lewa) = (${leftAfterScale.x.toFixed(3)}, ${leftAfterScale.z.toFixed(3)})`);
            console.log(`[PC]         leftErr = (${leftErr.x.toFixed(3)}, ${leftErr.z.toFixed(3)})`);
            console.log(`[PC]         firstRight (target prawej) = (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
            console.log(`[PC]         rightAfterScale (rzeczywista prawa) = (${rightAfterScale.x.toFixed(3)}, ${rightAfterScale.z.toFixed(3)})`);
            console.log(`[PC]         rightErr = (${rightErr.x.toFixed(3)}, ${rightErr.z.toFixed(3)})`);
            console.log(`[PC]         posCorrection = (${posCorrection.x.toFixed(3)}, ${posCorrection.z.toFixed(3)}), długość = ${posCorrection.length().toFixed(6)}m`);
            
            if (posCorrection.length() > 0.0001 && posCorrection.length() < 100) {
              const oldPosX = el.position.x;
              const oldPosZ = el.position.z;
              el.position.x += posCorrection.x;
              el.position.z += posCorrection.z;
              el.updateMatrixWorld(true);
              console.log(`[PC]         ✅ Przesunięto pozycję: (${oldPosX.toFixed(3)}, ${oldPosZ.toFixed(3)}) → (${el.position.x.toFixed(3)}, ${el.position.z.toFixed(3)})`);
            } else {
              console.log(`[PC]         ⚠️ Korekta pozycji pominięta (długość=${posCorrection.length().toFixed(6)}m - poza zakresem)`);
            }
          }
          
          // Zaktualizuj finalne krawędzie
          const finalEdges = this.getWorldEdges(el, baseWidth, el.scale.x, fenceConfigParam, selectedVariant);
          finalLeft2D.set(finalEdges.left.x, 0, finalEdges.left.z);
          finalRight2D.set(finalEdges.right.x, 0, finalEdges.right.z);
          
          console.log(`[PC]     ✅ KOREKTA DŁUGOŚCI I POZYCJI ZAKOŃCZONA (${iteration} iteracji)`);
        }
      } else {
        console.log(`[PC]     ✅ KRAWĘDZIE SIĘGAJĄ DO TARGETÓW (różnica < 0.5mm)`);
      }
    }
    
    console.log(`[PC]   ═══════════════════════════════════════════════════════════`);
    
    // 🎯 OBLICZ AKTUALNE KRAWĘDZIE dla dalszej analizy (po wszystkich korektach)
    const finalEdgesForAnalysis = this.getWorldEdges(el, baseWidth, el.scale.x, fenceConfigParam, selectedVariant);
    const finalLeftForAnalysis = new THREE.Vector3(finalEdgesForAnalysis.left.x, 0, finalEdgesForAnalysis.left.z);
    const finalRightForAnalysis = new THREE.Vector3(finalEdgesForAnalysis.right.x, 0, finalEdgesForAnalysis.right.z);
    finalLeft2D.set(finalLeftForAnalysis.x, 0, finalLeftForAnalysis.z);
    finalRight2D.set(finalRightForAnalysis.x, 0, finalRightForAnalysis.z);
    
    // 🎯 ZAKTUALIZUJ finalLeftErr i finalRightErr po wszystkich korektach
    finalLeftErr = finalLeft2D.distanceTo(lastRight2D);
    finalRightErr = finalRight2D.distanceTo(firstRight2D);
    
    // ANALIZA SYMETRYCZNOŚCI BŁĘDÓW
    console.log(`[PC]   🔍 ANALIZA SYMETRYCZNOŚCI:`);
    const avgDx = (dx_left + dx_right) / 2;
    const avgDz = (dz_left + dz_right) / 2;
    console.log(`[PC]     Średni dx = ${avgDx.toFixed(4)}m = ${(avgDx * 1000).toFixed(1)}mm`);
    console.log(`[PC]     Średni dz = ${avgDz.toFixed(4)}m = ${(avgDz * 1000).toFixed(1)}mm`);
    
    if (Math.abs(avgDx) > 0.001 || Math.abs(avgDz) > 0.001) {
      console.log(`[PC]     ⚠️  Błędy NIESYMETRYCZNE - problem z pozycją lub rotacją sekcji`);
    } else {
      console.log(`[PC]     ✅ Błędy SYMETRYCZNE - sekcja jest po prostu za długa/krótka`);
    }
    
    // CZY SEKCJA JEST ZA DŁUGA CZY ZA KRÓTKA?
    console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
    console.log(`[PC]   📏 DŁUGOŚĆ SEKCJI vs DYSTANS MIĘDZY TARGETAMI:`);
    const actualSectionLength = finalLeft2D.distanceTo(finalRight2D);
    const targetDistance = lastRight2D.distanceTo(firstRight2D);
    const lengthDiff = actualSectionLength - targetDistance;
    console.log(`[PC]     Rzeczywista długość sekcji (między krawędziami) = ${actualSectionLength.toFixed(4)}m`);
    console.log(`[PC]     Wymagany dystans (między targetami) = ${targetDistance.toFixed(4)}m`);
    console.log(`[PC]     Różnica = ${lengthDiff.toFixed(4)}m = ${(lengthDiff * 1000).toFixed(1)}mm`);

    // 🎯 DODATKOWE LOGI SPECYFICZNE DLA P22/P38/S38 MURO MIX (sekcja zamykająca)
    // 🎯 Dla S38 w sekcji zamykającej: użyj logiki P22
    const isMuroMixLocal = fenceConfigParam?.id?.startsWith('lupane-muro');
    const isMuroP22Local =
      fenceConfigParam?.id === 'lupane-muro-p22' || (isMuroMixLocal && (selectedVariant === 'p22' || selectedVariant === 's38')); // 🎯 S38 używa logiki P22
    const isMuroP38Local =
      fenceConfigParam?.id === 'lupane-muro-p38' || (isMuroMixLocal && selectedVariant === 'p38');
    const isMuroS38Local = isMuroP22Local; // 🎯 S38 używa dokładnie tej samej logiki co P22
    if (el.userData?.isClosingSection && (isMuroP22Local || isMuroP38Local || isMuroS38Local)) {
      const variantName = isMuroP22Local || (fenceConfigParam?.id === 'lupane-muro-s38' || (isMuroMixLocal && selectedVariant === 's38')) ? (isMuroP22Local ? 'P22' : 'S38 (używa P22)') : 'P38';
      console.log(`[PC]   🔍 [${variantName} CLOSING] DIAGNOSTYKA DŁUGOŚCI:`);
      console.log(`[PC]     fenceConfigParam.id = "${fenceConfigParam?.id}", selectedVariant = "${selectedVariant}"`);
      console.log(`[PC]     targetGap (z TRANSFORMACJI) = ${targetGap.toFixed(4)}m`);
      console.log(
        `[PC]     realWidth (z getWorldEdges FINAL) = ${actualSectionLength.toFixed(4)}m (diff = ${(
          actualSectionLength - targetGap
        ).toFixed(4)}m = ${((actualSectionLength - targetGap) * 1000).toFixed(1)}mm)`
      );
      console.log(
        `[PC]     firstRight2D = (${firstRight2D.x.toFixed(4)}, ${firstRight2D.z.toFixed(
          4
        )}), lastRight2D = (${lastRight2D.x.toFixed(4)}, ${lastRight2D.z.toFixed(4)})`
      );
      console.log(
        `[PC]     finalLeft2D  = (${finalLeft2D.x.toFixed(4)}, ${finalLeft2D.z.toFixed(
          4
        )}), finalRight2D = (${finalRight2D.x.toFixed(4)}, ${finalRight2D.z.toFixed(4)})`
      );
      console.log(
        `[PC]     first.pos = (${first.position.x.toFixed(4)}, ${first.position.z.toFixed(
          4
        )}), last.pos = (${last.position.x.toFixed(4)}, ${last.position.z.toFixed(4)})`
      );
      console.log(
        `[PC]     scaleX (sekcji zamykającej) = ${scaleX.toFixed(6)}, modelBaseWidth = ${modelBaseWidth.toFixed(4)}m`
      );
    }
    
    if (lengthDiff > 0.001) {
      console.log(`[PC]     ⚠️  SEKCJA JEST ZA DŁUGA o ${(lengthDiff * 1000).toFixed(1)}mm`);
      console.log(`[PC]     💡 Sekcja wystaje poza targety z obu stron`);
    } else if (lengthDiff < -0.001) {
      console.log(`[PC]     ⚠️  SEKCJA JEST ZA KRÓTKA o ${(-lengthDiff * 1000).toFixed(1)}mm`);
      console.log(`[PC]     💡 Sekcja nie sięga targetów`);
      
      // 🎯 DODATKOWA KOREKTA DŁUGOŚCI DLA ROYAL S50/P50 (jeśli sekcja jest za krótka)
      const isRoyalForLengthCheck = fenceConfigParam?.id === 'royal_s50' || 
                              fenceConfigParam?.id === 'royal_p50' ||
                              (fenceConfigParam?.id === 'royal-mix' && (selectedVariant === 's50' || selectedVariant === 'p50'));
      
      if (el.userData?.isClosingSection && isRoyalForLengthCheck && Math.abs(lengthDiff) > 0.003) {
        const variantName = selectedVariant?.toUpperCase() || 'S/P50';
        console.log(`[PC]   🎯 ROYAL ${variantName} (sekcja zamykająca): KOREKTA DŁUGOŚCI (sekaja za krótka o ${(-lengthDiff * 1000).toFixed(1)}mm)`);
        
        // Oblicz współczynnik korekty - zwiększ długość sekcji
        const scaleCorrectionFactor = targetDistance / actualSectionLength;
        const oldScaleX = el.scale.x;
        const newScaleX = el.scale.x * scaleCorrectionFactor;
        
        console.log(`[PC]     scaleCorrectionFactor = ${targetDistance.toFixed(4)}m / ${actualSectionLength.toFixed(4)}m = ${scaleCorrectionFactor.toFixed(6)}`);
        console.log(`[PC]     scale.x: ${oldScaleX.toFixed(6)} → ${newScaleX.toFixed(6)}`);
        
        el.scale.x = newScaleX;
        scaleX = newScaleX;
        el.updateMatrixWorld(true);
        
        // 🎯 PONOWNIE zastosuj Royal correction po zmianie skalowania
        this.applyRoyalMetalCorrection(el, fenceConfigParam, variant);
        el.updateMatrixWorld(true);
        
        // Oblicz ponownie finalne krawędzie po korekcie
        const finalEdgesAfterCorrection = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
        const finalLeftAfter = new THREE.Vector3(finalEdgesAfterCorrection.left.x, 0, finalEdgesAfterCorrection.left.z);
        const finalRightAfter = new THREE.Vector3(finalEdgesAfterCorrection.right.x, 0, finalEdgesAfterCorrection.right.z);
        const actualSectionLengthAfter = finalLeftAfter.distanceTo(finalRightAfter);
        const lengthDiffAfter = actualSectionLengthAfter - targetDistance;
        
        console.log(`[PC]     Po korekcie: długość = ${actualSectionLengthAfter.toFixed(4)}m, różnica = ${(lengthDiffAfter * 1000).toFixed(1)}mm`);
        
        // Skoryguj pozycję, żeby krawędzie trafiały w targety
        const leftErrorAfter = new THREE.Vector3().subVectors(lastRight, finalEdgesAfterCorrection.left);
        const rightErrorAfter = new THREE.Vector3().subVectors(firstRight, finalEdgesAfterCorrection.right);
        const correctionAfter = new THREE.Vector3()
          .addVectors(leftErrorAfter, rightErrorAfter)
          .multiplyScalar(0.5);
        
        el.position.x += correctionAfter.x;
        el.position.z += correctionAfter.z;
        el.updateMatrixWorld(true);
        
        console.log(`[PC]     ✅ KOREKTA DŁUGOŚCI I POZYCJI ZAKOŃCZONA`);
      }
    } else {
      console.log(`[PC]     ✅ DŁUGOŚĆ SEKCJI IDEALNA (różnica < 1mm)`);
    }
    
    // 🎯 DODATKOWA KOREKTA DŁUGOŚCI DLA GLADKIE_LONG (używam projekcji na kierunek sekcji)
    // 🎯 WAŻNE: Ta korekta działa ZAWSZE dla gladkie_long, niezależnie od lengthDiff
    const isGladkieLong = fenceConfigParam?.id === 'gladkie_long' || fenceConfigParam?.id === 'gladkie-long';
    
    if (el.userData?.isClosingSection && isGladkieLong) {
        console.log(`[PC]   🎯 GLADKIE LONG (sekcja zamykająca): ANALIZA PROJEKCJI NA KIERUNEK SEKCJI`);
        
        // Oblicz kierunek sekcji (od finalRight do finalLeft)
        const sectionDirection = new THREE.Vector3().subVectors(finalLeft2D, finalRight2D).normalize();
        
        // Oblicz projekcje błędów na kierunek sekcji
        const leftErrorVec = new THREE.Vector3().subVectors(lastRight2D, finalLeft2D);
        const rightErrorVec = new THREE.Vector3().subVectors(firstRight2D, finalRight2D);
        
        const leftProjection = leftErrorVec.dot(sectionDirection);
        const rightProjection = rightErrorVec.dot(sectionDirection);
        
        console.log(`[PC]     Kierunek sekcji: (${sectionDirection.x.toFixed(4)}, ${sectionDirection.z.toFixed(4)})`);
        console.log(`[PC]     Projekcje błędów na kierunek sekcji:`);
        console.log(`[PC]       LEWA: ${(leftProjection * 1000).toFixed(1)}mm ${leftProjection > 0 ? '(ZA KRÓTKA - brakuje)' : '(ZA DŁUGA - wystaje)'}`);
        console.log(`[PC]       PRAWA: ${(rightProjection * 1000).toFixed(1)}mm ${rightProjection < 0 ? '(ZA KRÓTKA - brakuje)' : '(ZA DŁUGA - wystaje)'}`);
        
        // Sprawdź czy krawędzie nie sięgają do targetów (projekcje > 0 dla lewej, < 0 dla prawej)
        const leftTooShort = leftProjection > 0.0005; // 0.5mm
        const rightTooShort = rightProjection < -0.0005; // 0.5mm
        
        // Sprawdź czy krawędzie wystają poza targety
        const leftTooLong = leftProjection < -0.0005; // 0.5mm
        const rightTooLong = rightProjection > 0.0005; // 0.5mm
        
        if (leftTooShort || rightTooShort || leftTooLong || rightTooLong) {
          // Oblicz ile dokładnie brakuje lub wystaje
          let neededExtension = 0;
          
          if (leftTooShort && rightTooShort) {
            // Obie krawędzie za krótkie - wydłuż sekcję
            neededExtension = Math.max(leftProjection, -rightProjection);
            console.log(`[PC]   🔧 OBE KRAWĘDZIE ZA KRÓTKIE - wydłużam sekcję o ${(neededExtension * 1000).toFixed(1)}mm`);
          } else if (leftTooShort) {
            // Tylko lewa za krótka
            neededExtension = leftProjection;
            console.log(`[PC]   🔧 LEWA KRAWĘDŹ ZA KRÓTKA - wydłużam sekcję o ${(neededExtension * 1000).toFixed(1)}mm`);
          } else if (rightTooShort) {
            // Tylko prawa za krótka
            neededExtension = -rightProjection;
            console.log(`[PC]   🔧 PRAWA KRAWĘDŹ ZA KRÓTKA - wydłużam sekcję o ${(neededExtension * 1000).toFixed(1)}mm`);
          } else if (leftTooLong && rightTooLong) {
            // Obie krawędzie za długie - skróć sekcję
            neededExtension = Math.min(leftProjection, -rightProjection);
            console.log(`[PC]   🔧 OBE KRAWĘDZIE ZA DŁUGIE - skracam sekcję o ${(Math.abs(neededExtension) * 1000).toFixed(1)}mm`);
          } else if (leftTooLong) {
            // Tylko lewa za długa
            neededExtension = leftProjection;
            console.log(`[PC]   🔧 LEWA KRAWĘDŹ ZA DŁUGA - skracam sekcję o ${(Math.abs(neededExtension) * 1000).toFixed(1)}mm`);
          } else if (rightTooLong) {
            // Tylko prawa za długa
            neededExtension = -rightProjection;
            console.log(`[PC]   🔧 PRAWA KRAWĘDŹ ZA DŁUGA - skracam sekcję o ${(Math.abs(neededExtension) * 1000).toFixed(1)}mm`);
          }
          
          if (Math.abs(neededExtension) > 0.0001) { // > 0.1mm
            // Oblicz nową długość sekcji
            const newSectionLength = actualSectionLength + neededExtension;
            const scaleCorrectionFactor = newSectionLength / actualSectionLength;
            const oldScaleX = el.scale.x;
            const newScaleX = el.scale.x * scaleCorrectionFactor;
            
            console.log(`[PC]     Obecna długość sekcji: ${actualSectionLength.toFixed(4)}m`);
            console.log(`[PC]     Potrzebna długość: ${newSectionLength.toFixed(4)}m`);
            console.log(`[PC]     scaleCorrectionFactor = ${scaleCorrectionFactor.toFixed(6)}`);
            console.log(`[PC]     scale.x: ${oldScaleX.toFixed(6)} → ${newScaleX.toFixed(6)}`);
            
            el.scale.x = newScaleX;
            scaleX = newScaleX;
            el.updateMatrixWorld(true);
            
            // Oblicz ponownie finalne krawędzie po korekcie
            const finalEdgesAfterCorrection = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
            const finalLeftAfter = new THREE.Vector3(finalEdgesAfterCorrection.left.x, 0, finalEdgesAfterCorrection.left.z);
            const finalRightAfter = new THREE.Vector3(finalEdgesAfterCorrection.right.x, 0, finalEdgesAfterCorrection.right.z);
            const actualSectionLengthAfter = finalLeftAfter.distanceTo(finalRightAfter);
            
            // Oblicz nowe projekcje
            const leftErrorVecAfter = new THREE.Vector3().subVectors(lastRight2D, finalLeftAfter);
            const rightErrorVecAfter = new THREE.Vector3().subVectors(firstRight2D, finalRightAfter);
            const sectionDirectionAfter = new THREE.Vector3().subVectors(finalLeftAfter, finalRightAfter).normalize();
            const leftProjectionAfter = leftErrorVecAfter.dot(sectionDirectionAfter);
            const rightProjectionAfter = rightErrorVecAfter.dot(sectionDirectionAfter);
            
            console.log(`[PC]     Po korekcie:`);
            console.log(`[PC]       Długość sekcji: ${actualSectionLengthAfter.toFixed(4)}m`);
            console.log(`[PC]       LEWA projekcja: ${(leftProjectionAfter * 1000).toFixed(1)}mm`);
            console.log(`[PC]       PRAWA projekcja: ${(rightProjectionAfter * 1000).toFixed(1)}mm`);
            
            // Skoryguj pozycję, żeby krawędzie trafiały w targety
            const leftErrorAfter = new THREE.Vector3().subVectors(lastRight, finalEdgesAfterCorrection.left);
            const rightErrorAfter = new THREE.Vector3().subVectors(firstRight, finalEdgesAfterCorrection.right);
            const correctionAfter = new THREE.Vector3()
              .addVectors(leftErrorAfter, rightErrorAfter)
              .multiplyScalar(0.5);
            
            el.position.x += correctionAfter.x;
            el.position.z += correctionAfter.z;
            el.updateMatrixWorld(true);
            
            console.log(`[PC]     ✅ KOREKTA DŁUGOŚCI I POZYCJI ZAKOŃCZONA`);
          }
        } else {
          console.log(`[PC]     ✅ KRAWĘDZIE SIĘGAJĄ DO TARGETÓW (różnica < 0.5mm)`);
        }
      }
    
    console.log(`[PC]   ═══════════════════════════════════════════════════════════`);
    
    // 🔍 WIZUALNA REPREZENTACJA POZYCJI
    console.log(`[PC]   🎨 WIZUALIZACJA POZYCJI (oś X):`);
    const positions = [
      { name: 'firstRight (target)', x: firstRight2D.x, symbol: '🟢' },
      { name: 'finalRight (actual)', x: finalRight2D.x, symbol: '🔵' },
      { name: 'finalLeft (actual)', x: finalLeft2D.x, symbol: '🔵' },
      { name: 'lastRight (target)', x: lastRight2D.x, symbol: '🟢' },
    ].sort((a, b) => a.x - b.x);
    
    console.log(`[PC]     Kolejność na osi X (od najmniejszego):`);
    positions.forEach((pos, idx) => {
      console.log(`[PC]     ${idx+1}. ${pos.symbol} ${pos.name.padEnd(25)} X=${pos.x.toFixed(4)}m`);
    });
    
    console.log(`[PC]   🎨 WIZUALIZACJA POZYCJI (oś Z):`);
    const positionsZ = [
      { name: 'firstRight (target)', z: firstRight2D.z, symbol: '🟢' },
      { name: 'finalRight (actual)', z: finalRight2D.z, symbol: '🔵' },
      { name: 'finalLeft (actual)', z: finalLeft2D.z, symbol: '🔵' },
      { name: 'lastRight (target)', z: lastRight2D.z, symbol: '🟢' },
    ].sort((a, b) => a.z - b.z);
    
    console.log(`[PC]     Kolejność na osi Z (od najmniejszego):`);
    positionsZ.forEach((pos, idx) => {
      console.log(`[PC]     ${idx+1}. ${pos.symbol} ${pos.name.padEnd(25)} Z=${pos.z.toFixed(4)}m`);
    });
    
    console.log(`[PC]   ═══════════════════════════════════════════════════════════`);
    
    // 🔍 CZY SEKCJA WYSTAJE POZA TARGETY?
    console.log(`[PC]   🔍 CZY KRAWĘDZIE SEKCJI WYSTAJĄ POZA TARGETY?`);
    
    // Sprawdź czy finalLeft jest dalej niż lastRight (w kierunku od środka)
    const leftDirection = new THREE.Vector3().subVectors(lastRight2D, firstRight2D).normalize();
    const leftToFinal = new THREE.Vector3().subVectors(finalLeft2D, lastRight2D);
    const leftProjection = leftToFinal.dot(leftDirection);
    
    if (leftProjection > 0) {
      console.log(`[PC]     🔴 LEWA: finalLeft WYSTAJE POZA lastRight o ${(leftProjection * 1000).toFixed(1)}mm`);
    } else {
      console.log(`[PC]     🔴 LEWA: finalLeft NIE DOSIĘGA do lastRight o ${(-leftProjection * 1000).toFixed(1)}mm`);
    }
    
    // Sprawdź czy finalRight jest dalej niż firstRight (w kierunku od środka)
    const rightDirection = new THREE.Vector3().subVectors(firstRight2D, lastRight2D).normalize();
    const rightToFinal = new THREE.Vector3().subVectors(finalRight2D, firstRight2D);
    const rightProjection = rightToFinal.dot(rightDirection);
    
    if (rightProjection > 0) {
      console.log(`[PC]     🟢 PRAWA: finalRight WYSTAJE POZA firstRight o ${(rightProjection * 1000).toFixed(1)}mm`);
    } else {
      console.log(`[PC]     🟢 PRAWA: finalRight NIE DOSIĘGA do firstRight o ${(-rightProjection * 1000).toFixed(1)}mm`);
    }
    
    console.log(`[PC]   ═══════════════════════════════════════════════════════════`);
    console.log(`[PC]   ✅ BŁĘDY KOŃCOWE (dystanse w mm):`);
    console.log(`[PC]     LEWA krawędź  → lastRight   = ${(finalLeftErr * 1000).toFixed(1)}mm`);
    console.log(`[PC]     PRAWA krawędź → firstRight  = ${(finalRightErr * 1000).toFixed(1)}mm`);
    console.log(`[PC]     SUMA błędów = ${((finalLeftErr + finalRightErr) * 1000).toFixed(1)}mm`);

    // 🎯 DODATKOWA KOREKTA POZYCJI DLA ROYAL S50/P50 (jeśli krawędzie nie trafiają w targety)
    const isRoyalS50OrP50 = fenceConfigParam?.id === 'royal_s50' || 
                            fenceConfigParam?.id === 'royal_p50' ||
                            (fenceConfigParam?.id === 'royal-mix' && (selectedVariant === 's50' || selectedVariant === 'p50'));
    
    const totalError = finalLeftErr + finalRightErr;
    // 🎯 POPRAWKA: Obniżony próg z 10mm na 2mm dla Royal S50 i gladkie_long, żeby reagować na mniejsze błędy
    const isGladkieLongForCorrection = fenceConfigParam?.id === 'gladkie_long' || fenceConfigParam?.id === 'gladkie-long';
    const minErrorThreshold = (isRoyalS50OrP50 || isGladkieLongForCorrection) ? 0.002 : 0.010; // 2mm dla Royal S50 i gladkie_long, 10mm dla innych
    
    // 🔍 SZCZEGÓŁOWE LOGOWANIE DLA ROYAL S50
    if (el.userData?.isClosingSection && isRoyalS50OrP50) {
      const variantName = selectedVariant?.toUpperCase() || 'S/P50';
      console.log(`\n[PC] 🔍 [ROYAL ${variantName}] SZCZEGÓŁOWA ANALIZA KRAWĘDZI PRZED KOREKTĄ:`);
      console.log(`[PC]   ═══════════════════════════════════════════════════════════`);
      console.log(`[PC]   LEWA KRAWĘDŹ:`);
      console.log(`[PC]     finalLeft (rzeczywista)  = (${finalLeft2D.x.toFixed(4)}, ${finalLeft2D.z.toFixed(4)})`);
      console.log(`[PC]     lastRight (target)       = (${lastRight2D.x.toFixed(4)}, ${lastRight2D.z.toFixed(4)})`);
      console.log(`[PC]     Wektor: finalLeft → lastRight = (${(lastRight2D.x - finalLeft2D.x).toFixed(4)}, ${(lastRight2D.z - finalLeft2D.z).toFixed(4)})`);
      console.log(`[PC]     Dystans = ${finalLeftErr.toFixed(4)}m = ${(finalLeftErr * 1000).toFixed(1)}mm`);
      
      // Sprawdź czy finalLeft sięga do lastRight (czy jest za krótka)
      const leftDir = new THREE.Vector3().subVectors(lastRight2D, firstRight2D).normalize();
      const leftToTarget = new THREE.Vector3().subVectors(lastRight2D, finalLeft2D);
      const leftProjection = leftToTarget.dot(leftDir);
      if (leftProjection > 0.001) {
        console.log(`[PC]     ⚠️  LEWA KRAWĘDŹ NIE SIĘGA DO TARGETU! Brakuje ${(leftProjection * 1000).toFixed(1)}mm`);
        console.log(`[PC]     💡 Sekcja jest ZA KRÓTKA z lewej strony`);
      } else if (leftProjection < -0.001) {
        console.log(`[PC]     ⚠️  LEWA KRAWĘDŹ WYSTAJE POZA TARGET! Wystaje ${(-leftProjection * 1000).toFixed(1)}mm`);
        console.log(`[PC]     💡 Sekcja jest ZA DŁUGA z lewej strony`);
      } else {
        console.log(`[PC]     ✅ LEWA KRAWĘDŹ SIĘGA DO TARGETU (różnica < 1mm)`);
      }
      
      console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
      console.log(`[PC]   PRAWA KRAWĘDŹ:`);
      console.log(`[PC]     finalRight (rzeczywista) = (${finalRight2D.x.toFixed(4)}, ${finalRight2D.z.toFixed(4)})`);
      console.log(`[PC]     firstRight (target)      = (${firstRight2D.x.toFixed(4)}, ${firstRight2D.z.toFixed(4)})`);
      console.log(`[PC]     Wektor: finalRight → firstRight = (${(firstRight2D.x - finalRight2D.x).toFixed(4)}, ${(firstRight2D.z - finalRight2D.z).toFixed(4)})`);
      console.log(`[PC]     Dystans = ${finalRightErr.toFixed(4)}m = ${(finalRightErr * 1000).toFixed(1)}mm`);
      
      // Sprawdź czy finalRight sięga do firstRight (czy jest za krótka)
      const rightDir = new THREE.Vector3().subVectors(firstRight2D, lastRight2D).normalize();
      const rightToTarget = new THREE.Vector3().subVectors(firstRight2D, finalRight2D);
      const rightProjection = rightToTarget.dot(rightDir);
      if (rightProjection > 0.001) {
        console.log(`[PC]     ⚠️  PRAWA KRAWĘDŹ NIE SIĘGA DO TARGETU! Brakuje ${(rightProjection * 1000).toFixed(1)}mm`);
        console.log(`[PC]     💡 Sekcja jest ZA KRÓTKA z prawej strony`);
      } else if (rightProjection < -0.001) {
        console.log(`[PC]     ⚠️  PRAWA KRAWĘDŹ WYSTAJE POZA TARGET! Wystaje ${(-rightProjection * 1000).toFixed(1)}mm`);
        console.log(`[PC]     💡 Sekcja jest ZA DŁUGA z prawej strony`);
      } else {
        console.log(`[PC]     ✅ PRAWA KRAWĘDŹ SIĘGA DO TARGETU (różnica < 1mm)`);
      }
      
      console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
      console.log(`[PC]   DŁUGOŚĆ SEKCJI:`);
      console.log(`[PC]     Rzeczywista długość (finalLeft → finalRight) = ${actualSectionLength.toFixed(4)}m`);
      console.log(`[PC]     Wymagany dystans (lastRight → firstRight)    = ${targetDistance.toFixed(4)}m`);
      console.log(`[PC]     Różnica = ${(actualSectionLength - targetDistance).toFixed(4)}m = ${((actualSectionLength - targetDistance) * 1000).toFixed(1)}mm`);
      if (actualSectionLength < targetDistance - 0.001) {
        console.log(`[PC]     ⚠️  SEKCJA JEST ZA KRÓTKA o ${((targetDistance - actualSectionLength) * 1000).toFixed(1)}mm`);
      } else if (actualSectionLength > targetDistance + 0.001) {
        console.log(`[PC]     ⚠️  SEKCJA JEST ZA DŁUGA o ${((actualSectionLength - targetDistance) * 1000).toFixed(1)}mm`);
      } else {
        console.log(`[PC]     ✅ DŁUGOŚĆ SEKCJI OK`);
      }
      console.log(`[PC]   ═══════════════════════════════════════════════════════════`);
      
      // 🔍 SPRAWDŹ CZY GEOMETRIA SEKCJI WYSTAJE POZA SŁUPKI
      console.log(`[PC]   🔍 ANALIZA BOUNDING BOXU SEKCJI vs POZYCJE SŁUPKÓW:`);
      console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
      
      // Pobierz bounding box całej sekcji w world space
      const sectionBbox = new THREE.Box3().setFromObject(el);
      
      console.log(`[PC]   BOUNDING BOX SEKCJI (world space):`);
      console.log(`[PC]     bbox.min = (${sectionBbox.min.x.toFixed(4)}, ${sectionBbox.min.z.toFixed(4)})`);
      console.log(`[PC]     bbox.max = (${sectionBbox.max.x.toFixed(4)}, ${sectionBbox.max.z.toFixed(4)})`);
      console.log(`[PC]     szerokość = ${(sectionBbox.max.x - sectionBbox.min.x).toFixed(4)}m`);
      console.log(`[PC]     głębokość = ${(sectionBbox.max.z - sectionBbox.min.z).toFixed(4)}m`);
      
      console.log(`[PC]   POZYCJE SŁUPKÓW:`);
      console.log(`[PC]     PIERWSZY słupek (first) = (${first.position.x.toFixed(4)}, ${first.position.z.toFixed(4)})`);
      console.log(`[PC]     OSTATNI słupek (last)   = (${last.position.x.toFixed(4)}, ${last.position.z.toFixed(4)})`);
      
      console.log(`[PC]   TARGETY (punkty mocowania do słupków):`);
      console.log(`[PC]     firstRight (przy pierwszym słupku) = (${firstRight2D.x.toFixed(4)}, ${firstRight2D.z.toFixed(4)})`);
      console.log(`[PC]     lastRight (przy ostatnim słupku)   = (${lastRight2D.x.toFixed(4)}, ${lastRight2D.z.toFixed(4)})`);
      
      console.log(`[PC]   OBLICZONE KRAWĘDZIE (z getWorldEdges):`);
      console.log(`[PC]     finalLeft  = (${finalLeft2D.x.toFixed(4)}, ${finalLeft2D.z.toFixed(4)})`);
      console.log(`[PC]     finalRight = (${finalRight2D.x.toFixed(4)}, ${finalRight2D.z.toFixed(4)})`);
      
      console.log(`[PC]   ⚠️  PORÓWNANIE: Obliczone krawędzie vs Bounding Box:`);
      console.log(`[PC]     finalLeft.x  = ${finalLeft2D.x.toFixed(4)}m, ale bbox.max.x = ${sectionBbox.max.x.toFixed(4)}m`);
      console.log(`[PC]     finalRight.x = ${finalRight2D.x.toFixed(4)}m, ale bbox.min.x = ${sectionBbox.min.x.toFixed(4)}m`);
      console.log(`[PC]     Różnica LEWA:  ${((sectionBbox.max.x - finalLeft2D.x) * 1000).toFixed(1)}mm (${sectionBbox.max.x > finalLeft2D.x ? 'bbox SZERSZY' : 'krawędź SZERSZA'})`);
      console.log(`[PC]     Różnica PRAWA: ${((finalRight2D.x - sectionBbox.min.x) * 1000).toFixed(1)}mm (${finalRight2D.x > sectionBbox.min.x ? 'krawędź SZERSZA' : 'bbox SZERSZY'})`);
      
      console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
      console.log(`[PC]   🚨 CZY GEOMETRIA SEKCJI WYSTAJE POZA SŁUPKI?`);
      
      // Sprawdź czy bounding box wystaje poza słupki w osi X
      const firstPostX = first.position.x;
      const lastPostX = last.position.x;
      const minPostX = Math.min(firstPostX, lastPostX);
      const maxPostX = Math.max(firstPostX, lastPostX);
      
      const overhangLeft = minPostX - sectionBbox.min.x;  // >0 jeśli sekcja wystaje na lewo
      const overhangRight = sectionBbox.max.x - maxPostX; // >0 jeśli sekcja wystaje na prawo
      
      console.log(`[PC]   OŚ X (szerokość):`);
      console.log(`[PC]     Zakres SŁUPKÓW:  X ∈ [${minPostX.toFixed(4)}, ${maxPostX.toFixed(4)}]`);
      console.log(`[PC]     Zakres SEKCJI:   X ∈ [${sectionBbox.min.x.toFixed(4)}, ${sectionBbox.max.x.toFixed(4)}]`);
      
      if (overhangLeft > 0.001) {
        console.log(`[PC]     🔴 LEWA STRONA: Geometria WYSTAJE o ${(overhangLeft * 1000).toFixed(1)}mm poza lewy słupek!`);
        console.log(`[PC]         bbox.min.x (${sectionBbox.min.x.toFixed(4)}) < minPostX (${minPostX.toFixed(4)})`);
      } else if (overhangLeft < -0.001) {
        console.log(`[PC]     ✅ LEWA STRONA: Geometria NIE WYSTAJE (w granicach ${(Math.abs(overhangLeft) * 1000).toFixed(1)}mm od słupka)`);
      } else {
        console.log(`[PC]     ✅ LEWA STRONA: Geometria dokładnie przy słupku`);
      }
      
      if (overhangRight > 0.001) {
        console.log(`[PC]     🔴 PRAWA STRONA: Geometria WYSTAJE o ${(overhangRight * 1000).toFixed(1)}mm poza prawy słupek!`);
        console.log(`[PC]         bbox.max.x (${sectionBbox.max.x.toFixed(4)}) > maxPostX (${maxPostX.toFixed(4)})`);
      } else if (overhangRight < -0.001) {
        console.log(`[PC]     ✅ PRAWA STRONA: Geometria NIE WYSTAJE (w granicach ${(Math.abs(overhangRight) * 1000).toFixed(1)}mm od słupka)`);
      } else {
        console.log(`[PC]     ✅ PRAWA STRONA: Geometria dokładnie przy słupku`);
      }
      
      // Sprawdź czy bounding box wystaje poza słupki w osi Z
      const firstPostZ = first.position.z;
      const lastPostZ = last.position.z;
      const minPostZ = Math.min(firstPostZ, lastPostZ);
      const maxPostZ = Math.max(firstPostZ, lastPostZ);
      
      const overhangFront = minPostZ - sectionBbox.min.z;  // >0 jeśli sekcja wystaje do przodu
      const overhangBack = sectionBbox.max.z - maxPostZ;   // >0 jeśli sekcja wystaje do tyłu
      
      console.log(`[PC]   OŚ Z (głębokość):`);
      console.log(`[PC]     Zakres SŁUPKÓW:  Z ∈ [${minPostZ.toFixed(4)}, ${maxPostZ.toFixed(4)}]`);
      console.log(`[PC]     Zakres SEKCJI:   Z ∈ [${sectionBbox.min.z.toFixed(4)}, ${sectionBbox.max.z.toFixed(4)}]`);
      
      if (overhangFront > 0.001) {
        console.log(`[PC]     🟡 PRZÓD: Geometria wystaje o ${(overhangFront * 1000).toFixed(1)}mm przed linię słupków`);
        console.log(`[PC]         bbox.min.z (${sectionBbox.min.z.toFixed(4)}) < minPostZ (${minPostZ.toFixed(4)})`);
      } else {
        console.log(`[PC]     ✅ PRZÓD: Geometria nie wystaje przed słupki`);
      }
      
      if (overhangBack > 0.001) {
        console.log(`[PC]     🟡 TYŁ: Geometria wystaje o ${(overhangBack * 1000).toFixed(1)}mm za linię słupków`);
        console.log(`[PC]         bbox.max.z (${sectionBbox.max.z.toFixed(4)}) > maxPostZ (${maxPostZ.toFixed(4)})`);
      } else {
        console.log(`[PC]     ✅ TYŁ: Geometria nie wystaje za słupki`);
      }
      
      console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
      console.log(`[PC]   📊 PODSUMOWANIE WYSTAWANIA:`);
      const totalOverhangX = Math.max(0, overhangLeft) + Math.max(0, overhangRight);
      const totalOverhangZ = Math.max(0, overhangFront) + Math.max(0, overhangBack);
      
      // 🎯 DLA ROYAL: NIE koryguj bbox - metal może wystawać, liczy się tylko długość krawędzi
      const isRoyalFamily = fenceConfigParam?.id?.includes('royal') || false;
      
      if (isRoyalFamily && (totalOverhangX > 0.01)) {
        console.log(`[PC]     🎯 ROYAL: Bbox wystaje o ${(totalOverhangX * 1000).toFixed(1)}mm - ZMNIEJSZAM SCALE.X!`);
        
        const currentBboxWidth = sectionBbox.max.x - sectionBbox.min.x;
        const postRangeX = maxPostX - minPostX;
        const targetBboxWidth = postRangeX * 0.97; // 97% zakresu słupków (3% margines)
        const bboxScaleFactor = targetBboxWidth / currentBboxWidth;
        
        console.log(`[PC]       Obecna bbox szerokość: ${currentBboxWidth.toFixed(4)}m`);
        console.log(`[PC]       Zakres słupków: ${postRangeX.toFixed(4)}m`);
        console.log(`[PC]       Docelowa bbox szerokość: ${targetBboxWidth.toFixed(4)}m`);
        console.log(`[PC]       Współczynnik skalowania: ${bboxScaleFactor.toFixed(4)}`);
        console.log(`[PC]       scale.x: ${el.scale.x.toFixed(3)} → ${(el.scale.x * bboxScaleFactor).toFixed(3)}`);
        
        const oldScaleX = el.scale.x;
        el.scale.x *= bboxScaleFactor;
        el.updateMatrixWorld(true);
        
        // 🎯 KLUCZOWE: Po zmianie scale.x bbox się przesuwa! Muszę skorygować pozycję elementu
        // żeby bbox był wycentrowany między słupkami
        const bboxAfterScale = new THREE.Box3().setFromObject(el);
        const bboxCenterAfterScale = (bboxAfterScale.min.x + bboxAfterScale.max.x) / 2;
        const postCenterX = (minPostX + maxPostX) / 2;
        const centerOffsetX = postCenterX - bboxCenterAfterScale;
        
        console.log(`[PC]     📊 ANALIZA PO SKALOWANIU:`);
        console.log(`[PC]       Bbox center X: ${bboxCenterAfterScale.toFixed(4)}m`);
        console.log(`[PC]       Post center X: ${postCenterX.toFixed(4)}m`);
        console.log(`[PC]       Bbox offset: ${(centerOffsetX * 1000).toFixed(1)}mm`);
        
        // 🎯 NIE PRZESUWAJ NA PODSTAWIE BBOX! Oblicz gdzie są KRAWĘDZIE i dopiero wtedy przesuń
        console.log(`[PC]     🎯 OBLICZAM KRAWĘDZIE PO SKALOWANIU BBOX...`);
        const edgesAfterBboxScale = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
        const finalLeftAfterBboxScale = new THREE.Vector3(edgesAfterBboxScale.left.x, 0, edgesAfterBboxScale.left.z);
        const finalRightAfterBboxScale = new THREE.Vector3(edgesAfterBboxScale.right.x, 0, edgesAfterBboxScale.right.z);
        
        // Oblicz błędy krawędzi (target - actual)
        const leftErrorVec = new THREE.Vector3().subVectors(lastRight, finalLeftAfterBboxScale);
        const rightErrorVec = new THREE.Vector3().subVectors(firstRight, finalRightAfterBboxScale);
        
        // Średni błąd (korekta pozycji)
        const avgCorrection = new THREE.Vector3(
          (leftErrorVec.x + rightErrorVec.x) / 2,
          0,
          (leftErrorVec.z + rightErrorVec.z) / 2
        );
        
        console.log(`[PC]       Krawędź lewa:  (${finalLeftAfterBboxScale.x.toFixed(3)}, ${finalLeftAfterBboxScale.z.toFixed(3)})`);
        console.log(`[PC]       Target lewa:   (${lastRight.x.toFixed(3)}, ${lastRight.z.toFixed(3)})`);
        console.log(`[PC]       Błąd lewy:     (${(leftErrorVec.x * 1000).toFixed(1)}mm, ${(leftErrorVec.z * 1000).toFixed(1)}mm)`);
        console.log(`[PC]       Krawędź prawa: (${finalRightAfterBboxScale.x.toFixed(3)}, ${finalRightAfterBboxScale.z.toFixed(3)})`);
        console.log(`[PC]       Target prawa:  (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
        console.log(`[PC]       Błąd prawy:    (${(rightErrorVec.x * 1000).toFixed(1)}mm, ${(rightErrorVec.z * 1000).toFixed(1)}mm)`);
        console.log(`[PC]       Średnia korekta: (${(avgCorrection.x * 1000).toFixed(1)}mm, ${(avgCorrection.z * 1000).toFixed(1)}mm)`);
        
        // Przesuń element o średnią korektę (żeby krawędzie trafiły w targety)
        el.position.x += avgCorrection.x;
        el.position.z += avgCorrection.z;
        el.updateMatrixWorld(true);
        
        // Sprawdź finalny bbox
        const newBbox = new THREE.Box3().setFromObject(el);
        const newBboxWidth = newBbox.max.x - newBbox.min.x;
        const newOverhangLeft = minPostX - newBbox.min.x;
        const newOverhangRight = newBbox.max.x - maxPostX;
        const newTotalOverhang = Math.max(0, -newOverhangLeft) + Math.max(0, newOverhangRight);
        
        console.log(`[PC]     ✅ PO KOREKCIE (scale.x: ${oldScaleX.toFixed(3)} → ${el.scale.x.toFixed(3)}):`);
        console.log(`[PC]       Nowa bbox: X ∈ [${newBbox.min.x.toFixed(4)}, ${newBbox.max.x.toFixed(4)}], szerokość: ${newBboxWidth.toFixed(4)}m`);
        console.log(`[PC]       Nowe wystawanie: lewo=${(Math.max(0, -newOverhangLeft) * 1000).toFixed(1)}mm, prawo=${(Math.max(0, newOverhangRight) * 1000).toFixed(1)}mm, razem=${(newTotalOverhang * 1000).toFixed(1)}mm`);
        
        // 🎯 OZNACZ ŻE SEKCJA ZOSTAŁA PRZESKALOWANA - nie pozwól na dalsze zmiany scale.x!
        el.userData.bboxWasScaled = true;
        console.log(`[PC]     ⚠️  USTAWIONO flagę bboxWasScaled=true - WYŁĄCZAM dalsze korekty scale.x!`);
        
      } else if (isRoyalFamily) {
        console.log(`[PC]     ✅ ROYAL: Bbox mieści się w granicach słupków (wystaje < 10mm)`);
      }
      
      // 🎯 DLA MURO P22/P38/S22: Identyczna logika jak dla Royal!
      const isMuroFamily = fenceConfigParam?.id?.startsWith('lupane-muro') || false;
      const isMuroP22_bbox = (fenceConfigParam?.id === 'lupane-muro-p22') || (isMuroFamily && selectedVariant === 'p22');
      const isMuroP38_bbox = (fenceConfigParam?.id === 'lupane-muro-p38') || (isMuroFamily && selectedVariant === 'p38');
      const isMuroS22_bbox = (fenceConfigParam?.id === 'lupane-muro-s22') || (isMuroFamily && selectedVariant === 's22');
      const isMuroAny = isMuroP22_bbox || isMuroP38_bbox || isMuroS22_bbox;
      
      if (isMuroAny && (totalOverhangX > 0.01)) {
        const variantName = isMuroP22_bbox ? 'P22' : isMuroP38_bbox ? 'P38' : 'S22';
        console.log(`[PC]     🎯 MURO ${variantName}: Bbox wystaje o ${(totalOverhangX * 1000).toFixed(1)}mm - ZMNIEJSZAM SCALE.X!`);
        
        const currentBboxWidth = sectionBbox.max.x - sectionBbox.min.x;
        const postRangeX = maxPostX - minPostX;
        const targetBboxWidth = postRangeX * 0.97; // 97% zakresu słupków (3% margines)
        const bboxScaleFactor = targetBboxWidth / currentBboxWidth;
        
        console.log(`[PC]       Obecna bbox szerokość: ${currentBboxWidth.toFixed(4)}m`);
        console.log(`[PC]       Zakres słupków: ${postRangeX.toFixed(4)}m`);
        console.log(`[PC]       Docelowa bbox szerokość: ${targetBboxWidth.toFixed(4)}m`);
        console.log(`[PC]       Współczynnik skalowania: ${bboxScaleFactor.toFixed(4)}`);
        console.log(`[PC]       scale.x: ${el.scale.x.toFixed(3)} → ${(el.scale.x * bboxScaleFactor).toFixed(3)}`);
        
        const oldScaleX_muro = el.scale.x;
        el.scale.x *= bboxScaleFactor;
        el.updateMatrixWorld(true);
        
        // 🎯 NIE PRZESUWAJ NA PODSTAWIE BBOX! Oblicz gdzie są KRAWĘDZIE i dopiero wtedy przesuń
        console.log(`[PC]     🎯 OBLICZAM KRAWĘDZIE PO SKALOWANIU BBOX...`);
        const edgesAfterBboxScale_muro = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
        const finalLeftAfterBboxScale_muro = new THREE.Vector3(edgesAfterBboxScale_muro.left.x, 0, edgesAfterBboxScale_muro.left.z);
        const finalRightAfterBboxScale_muro = new THREE.Vector3(edgesAfterBboxScale_muro.right.x, 0, edgesAfterBboxScale_muro.right.z);
        
        // Oblicz błędy krawędzi (target - actual)
        const leftErrorVec_muro = new THREE.Vector3().subVectors(lastRight, finalLeftAfterBboxScale_muro);
        const rightErrorVec_muro = new THREE.Vector3().subVectors(firstRight, finalRightAfterBboxScale_muro);
        
        // Średni błąd (korekta pozycji)
        const avgCorrection_muro = new THREE.Vector3(
          (leftErrorVec_muro.x + rightErrorVec_muro.x) / 2,
          0,
          (leftErrorVec_muro.z + rightErrorVec_muro.z) / 2
        );
        
        console.log(`[PC]       Krawędź lewa:  (${finalLeftAfterBboxScale_muro.x.toFixed(3)}, ${finalLeftAfterBboxScale_muro.z.toFixed(3)})`);
        console.log(`[PC]       Target lewa:   (${lastRight.x.toFixed(3)}, ${lastRight.z.toFixed(3)})`);
        console.log(`[PC]       Błąd lewy:     (${(leftErrorVec_muro.x * 1000).toFixed(1)}mm, ${(leftErrorVec_muro.z * 1000).toFixed(1)}mm)`);
        console.log(`[PC]       Krawędź prawa: (${finalRightAfterBboxScale_muro.x.toFixed(3)}, ${finalRightAfterBboxScale_muro.z.toFixed(3)})`);
        console.log(`[PC]       Target prawa:  (${firstRight.x.toFixed(3)}, ${firstRight.z.toFixed(3)})`);
        console.log(`[PC]       Błąd prawy:    (${(rightErrorVec_muro.x * 1000).toFixed(1)}mm, ${(rightErrorVec_muro.z * 1000).toFixed(1)}mm)`);
        console.log(`[PC]       Średnia korekta: (${(avgCorrection_muro.x * 1000).toFixed(1)}mm, ${(avgCorrection_muro.z * 1000).toFixed(1)}mm)`);
        
        // Przesuń element o średnią korektę (żeby krawędzie trafiły w targety)
        el.position.x += avgCorrection_muro.x;
        el.position.z += avgCorrection_muro.z;
        el.updateMatrixWorld(true);
        
        // Sprawdź finalny bbox
        const newBbox_muro = new THREE.Box3().setFromObject(el);
        const newBboxWidth_muro = newBbox_muro.max.x - newBbox_muro.min.x;
        const newOverhangLeft_muro = minPostX - newBbox_muro.min.x;
        const newOverhangRight_muro = newBbox_muro.max.x - maxPostX;
        const newTotalOverhang_muro = Math.max(0, -newOverhangLeft_muro) + Math.max(0, newOverhangRight_muro);
        
        console.log(`[PC]     ✅ PO KOREKCIE (scale.x: ${oldScaleX_muro.toFixed(3)} → ${el.scale.x.toFixed(3)}):`);
        console.log(`[PC]       Nowa bbox: X ∈ [${newBbox_muro.min.x.toFixed(4)}, ${newBbox_muro.max.x.toFixed(4)}], szerokość: ${newBboxWidth_muro.toFixed(4)}m`);
        console.log(`[PC]       Nowe wystawanie: lewo=${(Math.max(0, -newOverhangLeft_muro) * 1000).toFixed(1)}mm, prawo=${(Math.max(0, newOverhangRight_muro) * 1000).toFixed(1)}mm, razem=${(newTotalOverhang_muro * 1000).toFixed(1)}mm`);
        
        // 🎯 OZNACZ ŻE SEKCJA ZOSTAŁA PRZESKALOWANA - nie pozwól na dalsze zmiany scale.x!
        el.userData.bboxWasScaled = true;
        console.log(`[PC]     ⚠️  USTAWIONO flagę bboxWasScaled=true - WYŁĄCZAM dalsze korekty scale.x!`);
        
        // 🎯 DRUGA ITERACJA KOREKTY POZYCJI DLA MURO - doprecyzowanie krawędzi
        console.log(`[PC]     🔧 DRUGA ITERACJA KOREKTY POZYCJI (doprecyzowanie)...`);
        const edgesAfter2ndCorrection_muro = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
        const finalLeftAfter2_muro = new THREE.Vector3(edgesAfter2ndCorrection_muro.left.x, 0, edgesAfter2ndCorrection_muro.left.z);
        const finalRightAfter2_muro = new THREE.Vector3(edgesAfter2ndCorrection_muro.right.x, 0, edgesAfter2ndCorrection_muro.right.z);
        
        const leftError2_muro = new THREE.Vector3().subVectors(lastRight, finalLeftAfter2_muro);
        const rightError2_muro = new THREE.Vector3().subVectors(firstRight, finalRightAfter2_muro);
        
        const totalError2_muro = leftError2_muro.length() + rightError2_muro.length();
        
        console.log(`[PC]       Błąd lewy:  ${(leftError2_muro.length() * 1000).toFixed(1)}mm`);
        console.log(`[PC]       Błąd prawy: ${(rightError2_muro.length() * 1000).toFixed(1)}mm`);
        console.log(`[PC]       Suma błędów: ${(totalError2_muro * 1000).toFixed(1)}mm`);
        
        if (totalError2_muro > 0.005) { // Jeśli błąd > 5mm
          console.log(`[PC]     🎯 BŁĄD > 5mm - DODATKOWA KOREKTA POZYCJI`);
          
          const avgCorrection2_muro = new THREE.Vector3(
            (leftError2_muro.x + rightError2_muro.x) / 2,
            0,
            (leftError2_muro.z + rightError2_muro.z) / 2
          );
          
          console.log(`[PC]       Dodatkowa korekta: (${(avgCorrection2_muro.x * 1000).toFixed(1)}mm, ${(avgCorrection2_muro.z * 1000).toFixed(1)}mm)`);
          
          el.position.x += avgCorrection2_muro.x;
          el.position.z += avgCorrection2_muro.z;
          el.updateMatrixWorld(true);
          
          // Weryfikacja finalna
          const edgesFinal_muro = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
          const finalLeftFinal_muro = new THREE.Vector3(edgesFinal_muro.left.x, 0, edgesFinal_muro.left.z);
          const finalRightFinal_muro = new THREE.Vector3(edgesFinal_muro.right.x, 0, edgesFinal_muro.right.z);
          
          const leftErrorFinal_muro = finalLeftFinal_muro.distanceTo(lastRight);
          const rightErrorFinal_muro = finalRightFinal_muro.distanceTo(firstRight);
          
          console.log(`[PC]     ✅ PO DRUGIEJ KOREKCIE:`);
          console.log(`[PC]       Błąd lewy:  ${(leftErrorFinal_muro * 1000).toFixed(1)}mm`);
          console.log(`[PC]       Błąd prawy: ${(rightErrorFinal_muro * 1000).toFixed(1)}mm`);
        } else {
          console.log(`[PC]     ✅ Błąd < 5mm - nie ma potrzeby dodatkowej korekty`);
        }
        
      } else if (isMuroAny) {
        const variantName = isMuroP22_bbox ? 'P22' : isMuroP38_bbox ? 'P38' : isMuroS22_bbox ? 'S22' : 'S38';
        console.log(`[PC]     ✅ MURO ${variantName}: Bbox mieści się w granicach słupków (wystaje < 10mm)`);
      }
      
      if (!isRoyalFamily && !isMuroAny && (totalOverhangX > 0.001 || totalOverhangZ > 0.001)) {
        console.log(`[PC]     🚨 SEKCJA WYSTAJE POZA SŁUPKI!`);
        if (totalOverhangX > 0.001) {
          console.log(`[PC]       - W osi X (szerokość): ${(totalOverhangX * 1000).toFixed(1)}mm`);
        }
        if (totalOverhangZ > 0.001) {
          console.log(`[PC]       - W osi Z (głębokość): ${(totalOverhangZ * 1000).toFixed(1)}mm`);
        }
        
        // 🎯 AUTOMATYCZNE PRZESKALOWANIE, ŻEBY SEKCJA ZMIEŚCIŁA SIĘ MIĘDZY SŁUPKAMI
        console.log(`[PC]   ─────────────────────────────────────────────────────────────`);
        
        if (true) { // zawsze true bo isRoyalFamily jest false tutaj
          console.log(`[PC]   🔧 AUTOMATYCZNE PRZESKALOWANIE SEKCJI:`);
          
          const currentBboxWidth = sectionBbox.max.x - sectionBbox.min.x;
          const currentBboxDepth = sectionBbox.max.z - sectionBbox.min.z;
          const postRangeX = Math.abs(maxPostX - minPostX);
          const postRangeZ = Math.abs(maxPostZ - minPostZ);
          
          console.log(`[PC]     Obecna szerokość bbox: ${currentBboxWidth.toFixed(4)}m`);
          console.log(`[PC]     Zakres słupków X: ${postRangeX.toFixed(4)}m`);
          console.log(`[PC]     Obecna głębokość bbox: ${currentBboxDepth.toFixed(4)}m`);
          console.log(`[PC]     Zakres słupków Z: ${postRangeZ.toFixed(4)}m`);
          
          // Oblicz potrzebne współczynniki skalowania (z marginesem bezpieczeństwa 5mm na stronę)
          const safetyMargin = 0.010; // 10mm całkowitego marginesu (5mm na stronę)
          let scaleFactorX = 1.0;
          let scaleFactorZ = 1.0;
          
          if (totalOverhangX > 0.001) {
            // Potrzebna szerokość = zakres słupków - margines
            const targetWidth = postRangeX - safetyMargin;
            scaleFactorX = targetWidth / currentBboxWidth;
            console.log(`[PC]     Wymagane zmniejszenie X: z ${currentBboxWidth.toFixed(4)}m do ${targetWidth.toFixed(4)}m`);
            console.log(`[PC]     Współczynnik skalowania X: ${scaleFactorX.toFixed(4)}`);
          }
          
          if (totalOverhangZ > 0.001) {
            // Potrzebna głębokość = zakres słupków - margines
            const targetDepth = postRangeZ - safetyMargin;
            scaleFactorZ = targetDepth / currentBboxDepth;
            console.log(`[PC]     Wymagane zmniejszenie Z: z ${currentBboxDepth.toFixed(4)}m do ${targetDepth.toFixed(4)}m`);
            console.log(`[PC]     Współczynnik skalowania Z: ${scaleFactorZ.toFixed(4)}`);
          }
          
          // Zastosuj skalowanie jeśli potrzebne
          if (scaleFactorX < 1.0 || scaleFactorZ < 1.0) {
          // 🎯 KROK 1: Zapisz centrum bbox PRZED skalowaniem (w world space)
          const bboxCenterBeforeScaling = new THREE.Vector3(
            (sectionBbox.min.x + sectionBbox.max.x) / 2,
            (sectionBbox.min.y + sectionBbox.max.y) / 2,
            (sectionBbox.min.z + sectionBbox.max.z) / 2
          );
          console.log(`[PC]     🎯 CENTRUM PRZED SKALOWANIEM: (${bboxCenterBeforeScaling.x.toFixed(4)}, ${bboxCenterBeforeScaling.z.toFixed(4)})`);
          
          // 🎯 KROK 2: Zastosuj skalowanie BEZPOŚREDNIO NA WIERZCHOŁKACH wszystkich meshów
          console.log(`[PC]     🔧 SKALOJĘ WIERZCHOŁKI WSZYSTKICH MESHÓW (bezpośrednia modyfikacja pozycji):`);
          let meshCount = 0;
          let vertexCount = 0;
          
          el.traverse((child: any) => {
            if (child.isMesh && child.geometry) {
              meshCount++;
              
              const geometry = child.geometry;
              const positionAttribute = geometry.getAttribute('position');
              
              if (positionAttribute) {
                // Przeskaluj każdy wierzchołek bezpośrednio
                for (let i = 0; i < positionAttribute.count; i++) {
                  const x = positionAttribute.getX(i);
                  const y = positionAttribute.getY(i);
                  const z = positionAttribute.getZ(i);
                  
                  // Zastosuj skalowanie
                  const newX = scaleFactorX < 1.0 ? x * scaleFactorX : x;
                  const newZ = scaleFactorZ < 1.0 ? z * scaleFactorZ : z;
                  
                  positionAttribute.setXYZ(i, newX, y, newZ);
                  vertexCount++;
                }
                
                // Oznacz atrybut jako wymagający aktualizacji
                positionAttribute.needsUpdate = true;
                
                // Zaktualizuj bounding box geometrii
                geometry.computeBoundingBox();
                geometry.computeBoundingSphere();
              }
            }
          });
          console.log(`[PC]       Przeskalowano ${vertexCount} wierzchołków w ${meshCount} meshach`);
          
          el.updateMatrixWorld(true);
          
          console.log(`[PC]     ✅ PRZESKALOWANO GEOMETRIĘ SEKCJI:`);
          console.log(`[PC]       scaleFactorX: ${scaleFactorX.toFixed(4)} (${scaleFactorX < 1.0 ? `zmniejszono o ${((1 - scaleFactorX) * 100).toFixed(1)}%` : 'bez zmian'})`);
          console.log(`[PC]       scaleFactorZ: ${scaleFactorZ.toFixed(4)} (${scaleFactorZ < 1.0 ? `zmniejszono o ${((1 - scaleFactorZ) * 100).toFixed(1)}%` : 'bez zmian'})`);
          
          // 🎯 KROK 3: Oblicz nowe centrum bbox PO skalowaniu geometrii
          let newBbox = new THREE.Box3().setFromObject(el);
          const bboxCenterAfterScaling = new THREE.Vector3(
            (newBbox.min.x + newBbox.max.x) / 2,
            (newBbox.min.y + newBbox.max.y) / 2,
            (newBbox.min.z + newBbox.max.z) / 2
          );
          console.log(`[PC]     🎯 CENTRUM PO SKALOWANIU GEOMETRII: (${bboxCenterAfterScaling.x.toFixed(4)}, ${bboxCenterAfterScaling.z.toFixed(4)})`);
          
          // 🎯 KROK 4: Oblicz przesunięcie centrum (geometria skaluje się wokół swojego lokalnego origin)
          const scalingShiftX = bboxCenterAfterScaling.x - bboxCenterBeforeScaling.x;
          const scalingShiftZ = bboxCenterAfterScaling.z - bboxCenterBeforeScaling.z;
          
          if (Math.abs(scalingShiftX) > 0.001 || Math.abs(scalingShiftZ) > 0.001) {
            console.log(`[PC]     🎯 PRZESUNIĘCIE CENTRUM: X=${(scalingShiftX * 1000).toFixed(1)}mm, Z=${(scalingShiftZ * 1000).toFixed(1)}mm`);
            console.log(`[PC]     🔧 KORYGOWANIE POZYCJI (kompensacja przesunięcia centrum):`);
            const oldPosX = el.position.x;
            const oldPosZ = el.position.z;
            
            el.position.x -= scalingShiftX;
            el.position.z -= scalingShiftZ;
            el.updateMatrixWorld(true);
            
            console.log(`[PC]       Pozycja X: ${oldPosX.toFixed(4)} → ${el.position.x.toFixed(4)} (${(scalingShiftX * 1000).toFixed(1)}mm)`);
            console.log(`[PC]       Pozycja Z: ${oldPosZ.toFixed(4)} → ${el.position.z.toFixed(4)} (${(scalingShiftZ * 1000).toFixed(1)}mm)`);
            
            // Aktualizuj bbox po korekcie pozycji
            newBbox = new THREE.Box3().setFromObject(el);
          } else {
            console.log(`[PC]     ✅ Centrum nie przesunęło się - geometria przeskalowana idealnie wokół centrum`);
          }
          
          let newOverhangLeft = minPostX - newBbox.min.x;
          let newOverhangRight = newBbox.max.x - maxPostX;
          let newOverhangFront = minPostZ - newBbox.min.z;
          let newOverhangBack = newBbox.max.z - maxPostZ;
          
          console.log(`[PC]     ─────────────────────────────────────────────────────────────`);
          console.log(`[PC]     📏 WERYFIKACJA PO PRZESKALOWANIU GEOMETRII:`);
          console.log(`[PC]       Nowy bbox X: [${newBbox.min.x.toFixed(4)}, ${newBbox.max.x.toFixed(4)}] (szerokość: ${(newBbox.max.x - newBbox.min.x).toFixed(4)}m)`);
          console.log(`[PC]       Nowy bbox Z: [${newBbox.min.z.toFixed(4)}, ${newBbox.max.z.toFixed(4)}] (głębokość: ${(newBbox.max.z - newBbox.min.z).toFixed(4)}m)`);
          console.log(`[PC]       Zakres słupków X: [${minPostX.toFixed(4)}, ${maxPostX.toFixed(4)}]`);
          console.log(`[PC]       Zakres słupków Z: [${minPostZ.toFixed(4)}, ${maxPostZ.toFixed(4)}]`);
          
          if (newOverhangLeft > 0.001 || newOverhangRight > 0.001) {
            console.log(`[PC]       ⚠️  Nadal wystaje w osi X: lewo=${(Math.max(0, newOverhangLeft) * 1000).toFixed(1)}mm, prawo=${(Math.max(0, newOverhangRight) * 1000).toFixed(1)}mm`);
          } else {
            console.log(`[PC]       ✅ Nie wystaje w osi X`);
          }
          
          if (newOverhangFront > 0.001 || newOverhangBack > 0.001) {
            console.log(`[PC]       ⚠️  Nadal wystaje w osi Z: przód=${(Math.max(0, newOverhangFront) * 1000).toFixed(1)}mm, tył=${(Math.max(0, newOverhangBack) * 1000).toFixed(1)}mm`);
          } else {
            console.log(`[PC]       ✅ Nie wystaje w osi Z`);
          }
          
          // 🎯 KROK 5: WYRÓWNAJ CENTRUM SEKCJI DO CENTRUM MIĘDZY SŁUPKAMI
          console.log(`[PC]     ─────────────────────────────────────────────────────────────`);
          console.log(`[PC]     🔧 WYRÓWNYWANIE CENTRUM SEKCJI DO CENTRUM SŁUPKÓW:`);
          
          // Oblicz centrum bbox i centrum słupków
          const newBboxCenterX = (newBbox.min.x + newBbox.max.x) / 2;
          const newBboxCenterZ = (newBbox.min.z + newBbox.max.z) / 2;
          const postsCenterX = (minPostX + maxPostX) / 2;
          const postsCenterZ = (minPostZ + maxPostZ) / 2;
          const offsetX = postsCenterX - newBboxCenterX;
          const offsetZ = postsCenterZ - newBboxCenterZ;
          
          console.log(`[PC]       Centrum bbox sekcji: X=${newBboxCenterX.toFixed(4)}m, Z=${newBboxCenterZ.toFixed(4)}m`);
          console.log(`[PC]       Centrum słupków:     X=${postsCenterX.toFixed(4)}m, Z=${postsCenterZ.toFixed(4)}m`);
          console.log(`[PC]       Potrzebne przesunięcie: X=${(offsetX * 1000).toFixed(1)}mm, Z=${(offsetZ * 1000).toFixed(1)}mm`);
          
          const oldPosX = el.position.x;
          const oldPosZ = el.position.z;
          el.position.x += offsetX;
          el.position.z += offsetZ;
          el.updateMatrixWorld(true);
          
          console.log(`[PC]       Pozycja X: ${oldPosX.toFixed(4)} → ${el.position.x.toFixed(4)}`);
          console.log(`[PC]       Pozycja Z: ${oldPosZ.toFixed(4)} → ${el.position.z.toFixed(4)}`);
          
          // Weryfikacja po wyrównaniu
          newBbox = new THREE.Box3().setFromObject(el);
          newOverhangLeft = minPostX - newBbox.min.x;
          newOverhangRight = newBbox.max.x - maxPostX;
          newOverhangFront = minPostZ - newBbox.min.z;
          newOverhangBack = newBbox.max.z - maxPostZ;
          
          console.log(`[PC]       WERYFIKACJA FINALNA:`);
          console.log(`[PC]         bbox X: [${newBbox.min.x.toFixed(4)}, ${newBbox.max.x.toFixed(4)}] (szerokość: ${(newBbox.max.x - newBbox.min.x).toFixed(4)}m)`);
          console.log(`[PC]         bbox Z: [${newBbox.min.z.toFixed(4)}, ${newBbox.max.z.toFixed(4)}] (głębokość: ${(newBbox.max.z - newBbox.min.z).toFixed(4)}m)`);
          
          const finalOverhangX = Math.max(0, newOverhangLeft) + Math.max(0, newOverhangRight);
          const finalOverhangZ = Math.max(0, newOverhangFront) + Math.max(0, newOverhangBack);
          
          if (finalOverhangX > 0.001) {
            console.log(`[PC]         ⚠️  OŚ X: Nadal wystaje o ${(finalOverhangX * 1000).toFixed(1)}mm (lewo: ${(Math.max(0, newOverhangLeft) * 1000).toFixed(1)}mm, prawo: ${(Math.max(0, newOverhangRight) * 1000).toFixed(1)}mm)`);
          } else {
            console.log(`[PC]         ✅ OŚ X: Nie wystaje - WYRÓWNANE!`);
          }
          
          if (finalOverhangZ > 0.001) {
            console.log(`[PC]         ⚠️  OŚ Z: Nadal wystaje o ${(finalOverhangZ * 1000).toFixed(1)}mm (przód: ${(Math.max(0, newOverhangFront) * 1000).toFixed(1)}mm, tył: ${(Math.max(0, newOverhangBack) * 1000).toFixed(1)}mm)`);
          } else {
            console.log(`[PC]         ✅ OŚ Z: Nie wystaje - WYRÓWNANE!`);
          }
          
          // 🎯 OZNACZ ŻE SEKCJA ZOSTAŁA PRZESKALOWANA - POMIŃ DALSZE KOREKTY
          el.userData.wasAutoScaled = true;
          console.log(`[PC]     ✅ Sekcja została przeskalowana i wyrównana - dalsze korekty pozycji będą pominięte`);
        } else {
          console.log(`[PC]     ✅ Geometria sekcji mieści się w granicach słupków`);
        }
      }
    }
      
      console.log(`[PC]   ═══════════════════════════════════════════════════════════`);
      console.log(`[PC]   WARUNKI KOREKTY:`);
      console.log(`[PC]     totalError = ${totalError.toFixed(4)}m = ${(totalError * 1000).toFixed(1)}mm`);
      console.log(`[PC]     minErrorThreshold = ${minErrorThreshold.toFixed(4)}m = ${(minErrorThreshold * 1000).toFixed(1)}mm`);
      console.log(`[PC]     Czy uruchomić korektę? ${totalError > minErrorThreshold ? '✅ TAK' : '❌ NIE'}`);
      console.log(`[PC]   ═══════════════════════════════════════════════════════════\n`);
    }
    
    // 🎯 POMIŃ DALSZE KOREKTY JEŚLI SEKCJA ZOSTAŁA JUŻ PRZESKALOWANA
    const isMuroForSkip = fenceConfigParam?.id?.startsWith('lupane-muro') || false;
    const isMuroP22ForSkip = (fenceConfigParam?.id === 'lupane-muro-p22') || (isMuroForSkip && selectedVariant === 'p22');
    const isMuroP38ForSkip = (fenceConfigParam?.id === 'lupane-muro-p38') || (isMuroForSkip && selectedVariant === 'p38');
    const isMuroS22ForSkip = (fenceConfigParam?.id === 'lupane-muro-s22') || (isMuroForSkip && selectedVariant === 's22');
    const isMuroS38ForSkip = (fenceConfigParam?.id === 'lupane-muro-s38') || (isMuroForSkip && selectedVariant === 's38');
    const isMuroAnyForSkip = isMuroP22ForSkip || isMuroP38ForSkip || isMuroS22ForSkip || isMuroS38ForSkip;
    
    const shouldSkipCorrection = (isRoyalS50OrP50 || isMuroAnyForSkip) && el.userData?.isClosingSection && totalError > minErrorThreshold;
    
    if (shouldSkipCorrection) {
      if (el.userData?.wasAutoScaled || el.userData?.bboxWasScaled) {
        let systemName = 'ROYAL';
        let variantName = selectedVariant?.toUpperCase() || 'S/P50';
        if (isMuroAnyForSkip) {
          systemName = 'MURO';
          variantName = isMuroP22ForSkip ? 'P22' : isMuroP38ForSkip ? 'P38' : isMuroS22ForSkip ? 'S22' : 'S38';
        }
        console.log(`[PC]   ⏭️  ${systemName} ${variantName}: Pomijam korektę pozycji - sekcja została już przeskalowana i wyrównana`);
        console.log(`[PC]       wasAutoScaled: ${!!el.userData?.wasAutoScaled}, bboxWasScaled: ${!!el.userData?.bboxWasScaled}`);
      } else {
        let systemName = 'ROYAL';
        let variantName = selectedVariant?.toUpperCase() || 'S/P50';
        if (isMuroAnyForSkip) {
          systemName = 'MURO';
          variantName = isMuroP22ForSkip ? 'P22' : isMuroP38ForSkip ? 'P38' : isMuroS22ForSkip ? 'S22' : 'S38';
        }
        console.log(`[PC]   🎯 ${systemName} ${variantName} (sekcja zamykająca): PRECYZYJNA KOREKTA POZYCJI (błąd = ${(totalError * 1000).toFixed(1)}mm)`);
        
        // Oblicz błędy krawędzi
        const leftError = new THREE.Vector3().subVectors(lastRight, finalEdges.left);
        const rightError = new THREE.Vector3().subVectors(firstRight, finalEdges.right);
        
        console.log(`[PC]     leftError 3D = (${leftError.x.toFixed(4)}, ${leftError.z.toFixed(4)}), length = ${leftError.length().toFixed(4)}m`);
        console.log(`[PC]     rightError 3D = (${rightError.x.toFixed(4)}, ${rightError.z.toFixed(4)}), length = ${rightError.length().toFixed(4)}m`);
        
        // Oblicz kierunek sekcji (od right do left)
        const sectionDirection = new THREE.Vector3()
          .subVectors(finalEdges.left, finalEdges.right)
          .normalize();
        
        // Oblicz kierunek prostopadły (do korekty pozycji bocznej)
        const perpDirection = new THREE.Vector3(-sectionDirection.z, 0, sectionDirection.x);
        
        console.log(`[PC]     Kierunek sekcji: (${sectionDirection.x.toFixed(4)}, ${sectionDirection.z.toFixed(4)})`);
        console.log(`[PC]     Kierunek prostopadły: (${perpDirection.x.toFixed(4)}, ${perpDirection.z.toFixed(4)})`);
        
        // Projekcje błędów na kierunek prostopadły (korekta boczna)
        const leftPerpProj = leftError.dot(perpDirection);
        const rightPerpProj = rightError.dot(perpDirection);
        const avgPerpProj = (leftPerpProj + rightPerpProj) / 2;
        
        // Projekcje błędów na kierunek sekcji (korekta długości - nie potrzebna, bo długość jest OK)
        const leftParProj = leftError.dot(sectionDirection);
        const rightParProj = rightError.dot(sectionDirection);
        
        console.log(`[PC]     Projekcja LEWA prostopadła: ${(leftPerpProj * 1000).toFixed(1)}mm, równoległa: ${(leftParProj * 1000).toFixed(1)}mm`);
        console.log(`[PC]     Projekcja PRAWA prostopadła: ${(rightPerpProj * 1000).toFixed(1)}mm, równoległa: ${(rightParProj * 1000).toFixed(1)}mm`);
        console.log(`[PC]     Średnia projekcja prostopadła: ${(avgPerpProj * 1000).toFixed(1)}mm`);
        
        // Korekta: przesuń o średnią projekcję prostopadłą
        const positionCorrection = perpDirection.clone().multiplyScalar(avgPerpProj);
        
        console.log(`[PC]     positionCorrection = (${positionCorrection.x.toFixed(4)}, ${positionCorrection.z.toFixed(4)})`);
      
      const positionBefore = el.position.clone();
      el.position.x += positionCorrection.x;
      el.position.z += positionCorrection.z;
      el.updateMatrixWorld(true);
      
      console.log(`[PC]     Przed korektą: (${positionBefore.x.toFixed(4)}, ${positionBefore.z.toFixed(4)})`);
      console.log(`[PC]     Po korekcie: (${el.position.x.toFixed(4)}, ${el.position.z.toFixed(4)})`);
      
      // Oblicz ponownie finalne krawędzie po korekcie
      const finalEdgesAfterCorrection = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
      const finalLeftAfter = new THREE.Vector3(finalEdgesAfterCorrection.left.x, 0, finalEdgesAfterCorrection.left.z);
      const finalRightAfter = new THREE.Vector3(finalEdgesAfterCorrection.right.x, 0, finalEdgesAfterCorrection.right.z);
      const lastRight2DAfter = new THREE.Vector3(lastRight.x, 0, lastRight.z);
      const firstRight2DAfter = new THREE.Vector3(firstRight.x, 0, firstRight.z);
      
      const finalLeftErrAfter = finalLeftAfter.distanceTo(lastRight2DAfter);
      const finalRightErrAfter = finalRightAfter.distanceTo(firstRight2DAfter);
      const totalErrorAfter = finalLeftErrAfter + finalRightErrAfter;
      
      console.log(`[PC]     Po korekcie: LEWA błąd = ${(finalLeftErrAfter * 1000).toFixed(1)}mm, PRAWA błąd = ${(finalRightErrAfter * 1000).toFixed(1)}mm`);
      console.log(`[PC]     SUMA błędów po korekcie = ${(totalErrorAfter * 1000).toFixed(1)}mm`);
      
      // 🔍 SZCZEGÓŁOWA ANALIZA PO KOREKCIE POZYCJI
      console.log(`[PC]     ─────────────────────────────────────────────────────────────`);
      console.log(`[PC]     🔍 [ROYAL ${variantName}] ANALIZA KRAWĘDZI PO KOREKCIE POZYCJI:`);
      const finalLeft2DAfter = new THREE.Vector3(finalEdgesAfterCorrection.left.x, 0, finalEdgesAfterCorrection.left.z);
      const finalRight2DAfter = new THREE.Vector3(finalEdgesAfterCorrection.right.x, 0, finalEdgesAfterCorrection.right.z);
      const actualLengthAfter = finalLeft2DAfter.distanceTo(finalRight2DAfter);
      console.log(`[PC]       LEWA: finalLeft = (${finalLeft2DAfter.x.toFixed(4)}, ${finalLeft2DAfter.z.toFixed(4)}), target = (${lastRight2DAfter.x.toFixed(4)}, ${lastRight2DAfter.z.toFixed(4)})`);
      console.log(`[PC]       PRAWA: finalRight = (${finalRight2DAfter.x.toFixed(4)}, ${finalRight2DAfter.z.toFixed(4)}), target = (${firstRight2DAfter.x.toFixed(4)}, ${firstRight2DAfter.z.toFixed(4)})`);
      console.log(`[PC]       Długość sekcji = ${actualLengthAfter.toFixed(4)}m, wymagana = ${targetDistance.toFixed(4)}m`);
      
      // Sprawdź czy krawędzie sięgają do targetów
      const leftDir2 = new THREE.Vector3().subVectors(lastRight2DAfter, firstRight2DAfter).normalize();
      const leftToTarget2 = new THREE.Vector3().subVectors(lastRight2DAfter, finalLeft2DAfter);
      const leftProj2 = leftToTarget2.dot(leftDir2);
      const rightDir2 = new THREE.Vector3().subVectors(firstRight2DAfter, lastRight2DAfter).normalize();
      const rightToTarget2 = new THREE.Vector3().subVectors(firstRight2DAfter, finalRight2DAfter);
      const rightProj2 = rightToTarget2.dot(rightDir2);
      
      console.log(`[PC]       LEWA projekcja na kierunek = ${(leftProj2 * 1000).toFixed(1)}mm (${leftProj2 > 0.001 ? 'ZA KRÓTKA' : leftProj2 < -0.001 ? 'ZA DŁUGA' : 'OK'})`);
      console.log(`[PC]       PRAWA projekcja na kierunek = ${(rightProj2 * 1000).toFixed(1)}mm (${rightProj2 > 0.001 ? 'ZA KRÓTKA' : rightProj2 < -0.001 ? 'ZA DŁUGA' : 'OK'})`);
      console.log(`[PC]     ─────────────────────────────────────────────────────────────`);
      
      // 🎯 DODATKOWA KOREKTA DŁUGOŚCI jeśli krawędzie nadal nie sięgają do targetów
      // 🎯 POPRAWKA: Używamy projekcji na kierunek, żeby sprawdzić czy krawędzie sięgają do targetów
      // leftProj2 < 0 oznacza że finalLeft jest DALEJ niż lastRight (za długa)
      // leftProj2 > 0 oznacza że finalLeft jest BLIŻEJ niż lastRight (za krótka)
      const leftNeedsCorrection = Math.abs(leftProj2) > 0.002; // > 2mm różnicy
      const rightNeedsCorrection = Math.abs(rightProj2) > 0.002; // > 2mm różnicy
      const bothTooShort = leftProj2 > 0.002 && rightProj2 > 0.002; // Oba za krótkie
      const bothTooLong = leftProj2 < -0.002 && rightProj2 < -0.002; // Oba za długie
      
      console.log(`[PC]     🔍 WARUNKI KOREKTY DŁUGOŚCI (używam projekcji na kierunek):`);
      console.log(`[PC]       leftProj2 = ${(leftProj2 * 1000).toFixed(1)}mm (${leftProj2 > 0.002 ? 'ZA KRÓTKA' : leftProj2 < -0.002 ? 'ZA DŁUGA' : 'OK'})`);
      console.log(`[PC]       rightProj2 = ${(rightProj2 * 1000).toFixed(1)}mm (${rightProj2 > 0.002 ? 'ZA KRÓTKA' : rightProj2 < -0.002 ? 'ZA DŁUGA' : 'OK'})`);
      console.log(`[PC]       bothTooShort (oba za krótkie)? ${bothTooShort ? '✅ TAK' : '❌ NIE'}`);
      console.log(`[PC]       bothTooLong (oba za długie)? ${bothTooLong ? '✅ TAK' : '❌ NIE'}`);
      console.log(`[PC]       Czy uruchomić korektę długości? ${(bothTooShort || bothTooLong) && (leftNeedsCorrection || rightNeedsCorrection) ? '✅ TAK' : '❌ NIE'}`);
      
      if ((bothTooShort || bothTooLong) && (leftNeedsCorrection || rightNeedsCorrection)) {
        const currentLength = finalLeftAfter.distanceTo(finalRightAfter);
        let requiredLength: number;
        let lengthScaleFactor: number;
        
        if (bothTooShort) {
          console.log(`[PC]     🔧 KRAWĘDZIE NADAL ZA KRÓTKIE - zwiększam długość sekcji`);
          
          // 🎯 POPRAWKA: Używamy projekcji, żeby obliczyć ile dokładnie brakuje
          const neededExtension = Math.max(leftProj2, rightProj2); // Największy brak
          requiredLength = targetDistance + neededExtension;
          lengthScaleFactor = requiredLength / currentLength;
        } else { // bothTooLong
          console.log(`[PC]     🔧 KRAWĘDZIE NADAL ZA DŁUGIE - zmniejszam długość sekcji`);
          
          // 🎯 POPRAWKA: Używamy projekcji, żeby obliczyć ile dokładnie wystaje
          const excessLength = Math.min(leftProj2, rightProj2); // Najmniejszy nadmiar (ujemny)
          requiredLength = targetDistance + excessLength; // excessLength jest ujemny, więc odejmujemy
          lengthScaleFactor = requiredLength / currentLength;
        }
        
        const oldScaleX = el.scale.x;
        const newScaleX = el.scale.x * lengthScaleFactor;
        
        console.log(`[PC]       currentLength = ${currentLength.toFixed(4)}m`);
        console.log(`[PC]       requiredLength = ${requiredLength.toFixed(4)}m`);
        console.log(`[PC]       lengthScaleFactor = ${lengthScaleFactor.toFixed(6)}`);
        console.log(`[PC]       scale.x: ${oldScaleX.toFixed(6)} → ${newScaleX.toFixed(6)}`);
        
        el.scale.x = newScaleX;
        scaleX = newScaleX;
        el.updateMatrixWorld(true);
        
        // 🎯 PONOWNIE zastosuj Royal correction po zmianie skalowania
        this.applyRoyalMetalCorrection(el, fenceConfigParam, variant);
        el.updateMatrixWorld(true);
        
        // Oblicz ponownie po korekcie długości
        const finalEdgesAfterLengthCorrection = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
        const finalLeftAfterLength = new THREE.Vector3(finalEdgesAfterLengthCorrection.left.x, 0, finalEdgesAfterLengthCorrection.left.z);
        const finalRightAfterLength = new THREE.Vector3(finalEdgesAfterLengthCorrection.right.x, 0, finalEdgesAfterLengthCorrection.right.z);
        
        const finalLeftErrAfterLength = finalLeftAfterLength.distanceTo(lastRight2DAfter);
        const finalRightErrAfterLength = finalRightAfterLength.distanceTo(firstRight2DAfter);
        const totalErrorAfterLength = finalLeftErrAfterLength + finalRightErrAfterLength;
        
        console.log(`[PC]       Po korekcie długości: LEWA błąd = ${(finalLeftErrAfterLength * 1000).toFixed(1)}mm, PRAWA błąd = ${(finalRightErrAfterLength * 1000).toFixed(1)}mm`);
        console.log(`[PC]       SUMA błędów po korekcie długości = ${(totalErrorAfterLength * 1000).toFixed(1)}mm`);
        
        // Zaktualizuj finalEdges dla dalszych obliczeń (markerów)
        finalEdges.left.copy(finalEdgesAfterLengthCorrection.left);
        finalEdges.right.copy(finalEdgesAfterLengthCorrection.right);
      } else {
        // Zaktualizuj finalEdges dla dalszych obliczeń (markerów)
        finalEdges.left.copy(finalEdgesAfterCorrection.left);
        finalEdges.right.copy(finalEdgesAfterCorrection.right);
      }
      
      console.log(`[PC]     ✅ KOREKTA POZYCJI I DŁUGOŚCI ZAKOŃCZONA`);
      } // koniec bloku else (wasAutoScaled)
    }

    // 🎯 FINALNA KOREKTA SKALI - DLA ROYAL I MURO!
    const isRoyalS50Final = fenceConfigParam?.id === 'royal_s50' || 
                            fenceConfigParam?.id === 'royal_p50' ||
                            (fenceConfigParam?.id === 'royal-mix' && (selectedVariant === 's50' || selectedVariant === 'p50'));
    
    // 🔧 WYŁĄCZAM FINALNĄ KOREKTĘ SKALI DLA MURO - ma swoją iteracyjną korektę w liniach ~1099-1189
    // const isMuroFinalScale = fenceConfigParam?.id?.startsWith('lupane-muro') || false;
    // const isMuroP22FinalScale = (fenceConfigParam?.id === 'lupane-muro-p22') || (isMuroFinalScale && selectedVariant === 'p22');
    // const isMuroP38FinalScale = (fenceConfigParam?.id === 'lupane-muro-p38') || (isMuroFinalScale && selectedVariant === 'p38');
    // const isMuroS22FinalScale = (fenceConfigParam?.id === 'lupane-muro-s22') || (isMuroFinalScale && selectedVariant === 's22');
    // const isMuroS38FinalScale = (fenceConfigParam?.id === 'lupane-muro-s38') || (isMuroFinalScale && selectedVariant === 's38');
    // const isMuroAnyFinalScale = isMuroP22FinalScale || isMuroP38FinalScale || isMuroS22FinalScale || isMuroS38FinalScale;
    
    const needsFinalScaleCorrection = el.userData?.isClosingSection && isRoyalS50Final;
    
    if (needsFinalScaleCorrection) {
      const systemName = 'ROYAL';
      const variantName = selectedVariant?.toUpperCase() || 'S/P50';
      console.log(`\n[PC] 🎯 [${systemName} ${variantName}] FINALNA KOREKTA SKALI - idealne dopasowanie do targetDistance`);
      
      // Oblicz aktualną długość sekcji
      const currentActualLength = finalEdges.left.distanceTo(finalEdges.right);
      const lengthDiff = currentActualLength - targetDistance;
      
      console.log(`[PC]   Długość sekcji (aktualna) = ${currentActualLength.toFixed(4)}m`);
      console.log(`[PC]   Długość sekcji (wymagana) = ${targetDistance.toFixed(4)}m`);
      console.log(`[PC]   Różnica = ${(lengthDiff * 1000).toFixed(1)}mm`);
      
      // 🎯 Jeśli bbox był już przeskalowany, NIE ZMIENIAJ scale.x!
      if (el.userData?.bboxWasScaled) {
        console.log(`[PC]   ⏭️  ROYAL: Pomijam korektę scale.x - bbox był już przeskalowany do słupków`);
        console.log(`[PC]       (flaga bboxWasScaled=true, akceptuję że krawędzie mogą być lekko za krótkie)`);
      } else if (Math.abs(lengthDiff) > 0.002) {
        // Błąd > 2mm - skoryguj scale.x
        console.log(`[PC]   🎯 KOREKTUJĘ scale.x (błąd ${(lengthDiff * 1000).toFixed(1)}mm > 2mm):`);
        const edgeScaleFactor = targetDistance / currentActualLength;
        const idealScaleX = scaleX * edgeScaleFactor;
        
        console.log(`[PC]     Współczynnik: ${edgeScaleFactor.toFixed(4)}`);
        console.log(`[PC]     scale.x: ${scaleX.toFixed(3)} → ${idealScaleX.toFixed(3)}`);
        
        scaleX = idealScaleX;
        el.scale.x = idealScaleX;
      }
      
      // 🎯 PONOWNIE zastosuj Royal correction po zmianie skalowania
      this.applyRoyalMetalCorrection(el, fenceConfigParam, variant);
      el.updateMatrixWorld(true);
      
      // Oblicz ponownie finalne krawędzie po idealnej korekcie
      const finalEdgesAfterIdeal = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
      const finalActualLengthAfter = finalEdgesAfterIdeal.left.distanceTo(finalEdgesAfterIdeal.right);
      const finalLengthDiff = finalActualLengthAfter - targetDistance;
      
      console.log(`[PC]   Po idealnej korekcie:`);
      console.log(`[PC]     Długość sekcji = ${finalActualLengthAfter.toFixed(4)}m`);
      console.log(`[PC]     Różnica = ${(finalLengthDiff * 1000).toFixed(1)}mm`);
      
      // 🎯 KOREKTA POZYCJI PO ZMIANIE SKALI
      // Po zmianie skali krawędzie są w nowych miejscach - trzeba skorygować pozycję
      const lastRight2D_Final = new THREE.Vector3(lastRight.x, 0, lastRight.z);
      const firstRight2D_Final = new THREE.Vector3(firstRight.x, 0, firstRight.z);
      const finalLeft2D_Final = new THREE.Vector3(finalEdgesAfterIdeal.left.x, 0, finalEdgesAfterIdeal.left.z);
      const finalRight2D_Final = new THREE.Vector3(finalEdgesAfterIdeal.right.x, 0, finalEdgesAfterIdeal.right.z);
      
      const leftErrorFinal = new THREE.Vector3().subVectors(lastRight2D_Final, finalLeft2D_Final);
      const rightErrorFinal = new THREE.Vector3().subVectors(firstRight2D_Final, finalRight2D_Final);
      const positionCorrectionFinal = new THREE.Vector3().addVectors(leftErrorFinal, rightErrorFinal).multiplyScalar(0.5);
      
      const leftErrDistFinal = finalLeft2D_Final.distanceTo(lastRight2D_Final);
      const rightErrDistFinal = finalRight2D_Final.distanceTo(firstRight2D_Final);
      
      console.log(`[PC]     Błędy pozycji po zmianie skali: LEWA = ${(leftErrDistFinal * 1000).toFixed(1)}mm, PRAWA = ${(rightErrDistFinal * 1000).toFixed(1)}mm`);
      
      if (leftErrDistFinal > 0.001 || rightErrDistFinal > 0.001) {
        console.log(`[PC]     🔧 Koryguję pozycję po zmianie skali...`);
        console.log(`[PC]       positionCorrection = (${(positionCorrectionFinal.x * 1000).toFixed(1)}mm, ${(positionCorrectionFinal.z * 1000).toFixed(1)}mm)`);
        
        el.position.x += positionCorrectionFinal.x;
        el.position.z += positionCorrectionFinal.z;
        el.updateMatrixWorld(true);
        
        // Ponownie oblicz krawędzie po korekcie pozycji
        const finalEdgesAfterPosCorrection = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
        const finalLeft2DAfterPos = new THREE.Vector3(finalEdgesAfterPosCorrection.left.x, 0, finalEdgesAfterPosCorrection.left.z);
        const finalRight2DAfterPos = new THREE.Vector3(finalEdgesAfterPosCorrection.right.x, 0, finalEdgesAfterPosCorrection.right.z);
        
        const leftErrAfterPos = finalLeft2DAfterPos.distanceTo(lastRight2D_Final);
        const rightErrAfterPos = finalRight2DAfterPos.distanceTo(firstRight2D_Final);
        
        console.log(`[PC]       Po korekcie pozycji: LEWA = ${(leftErrAfterPos * 1000).toFixed(1)}mm, PRAWA = ${(rightErrAfterPos * 1000).toFixed(1)}mm`);
        
        // Zaktualizuj finalEdges
        finalEdges.left.copy(finalEdgesAfterPosCorrection.left);
        finalEdges.right.copy(finalEdgesAfterPosCorrection.right);
      } else {
        console.log(`[PC]     ✅ Pozycja OK (błędy < 1mm)`);
        // Zaktualizuj finalEdges
        finalEdges.left.copy(finalEdgesAfterIdeal.left);
        finalEdges.right.copy(finalEdgesAfterIdeal.right);
      }
      
      console.log(`[PC]     ✅ IDEALNA KOREKTA SKALI ZAKOŃCZONA`);
    }

    // 🎯 FINALNA WERYFIKACJA - CZY KRAWĘDZIE SIĘGAJĄ DO TARGETÓW (za pomocą projekcji)
    // TYLKO DLA ROYAL! Muro ma swój własny system korekty w liniach ~1090-1150
    if (el.userData?.isClosingSection && isRoyalS50Final) {
      const variantName = selectedVariant?.toUpperCase() || 'S/P50';
      console.log(`\n[PC] 🎯 [ROYAL ${variantName}] FINALNA WERYFIKACJA PROJEKCJI`);
      
      // Oblicz kierunek sekcji
      const finalDirection = new THREE.Vector3().subVectors(finalEdges.left, finalEdges.right).normalize();
      
      // Oblicz projekcje błędów na kierunek sekcji
      const leftErrorVec = new THREE.Vector3().subVectors(lastRight, finalEdges.left);
      const rightErrorVec = new THREE.Vector3().subVectors(firstRight, finalEdges.right);
      
      const leftProjection = leftErrorVec.dot(finalDirection);
      const rightProjection = rightErrorVec.dot(finalDirection);
      
      console.log(`[PC]   Projekcje błędów na kierunek sekcji:`);
      console.log(`[PC]     LEWA: ${(leftProjection * 1000).toFixed(1)}mm ${leftProjection > 0 ? '(ZA KRÓTKA)' : '(ZA DŁUGA)'}`);
      console.log(`[PC]     PRAWA: ${(rightProjection * 1000).toFixed(1)}mm ${rightProjection < 0 ? '(ZA KRÓTKA)' : '(ZA DŁUGA)'}`);
      
      // Jeśli obie krawędzie są za krótkie (nie sięgają targetów), wydłuż sekcję
      const leftTooShort = leftProjection > 0.002; // 2mm
      const rightTooShort = rightProjection < -0.002; // 2mm (ujemna projekcja = brakuje w kierunku right)
      
      if (leftTooShort && rightTooShort) {
        console.log(`[PC]   ⚠️ OBE KRAWĘDZIE ZA KRÓTKIE - wydłużam sekcję`);
        
        // Oblicz średni brakujący dystans
        const avgShortage = (leftProjection - rightProjection) / 2;
        console.log(`[PC]     Średni brak: ${(avgShortage * 1000).toFixed(1)}mm`);
        
        // Zwiększ długość sekcji o ten brak
        const currentLength = finalEdges.left.distanceTo(finalEdges.right);
        const requiredLength = currentLength + avgShortage;
        const lengthScaleFactor = requiredLength / currentLength;
        
        console.log(`[PC]     Aktualna długość: ${currentLength.toFixed(4)}m`);
        console.log(`[PC]     Wymagana długość: ${requiredLength.toFixed(4)}m`);
        console.log(`[PC]     Współczynnik skali: ${lengthScaleFactor.toFixed(6)}`);
        
        const oldScaleX = el.scale.x;
        el.scale.x *= lengthScaleFactor;
        scaleX = el.scale.x;
        el.updateMatrixWorld(true);
        
        console.log(`[PC]     scale.x: ${oldScaleX.toFixed(6)} → ${scaleX.toFixed(6)}`);
        
        // Zastosuj Royal correction ponownie
        this.applyRoyalMetalCorrection(el, fenceConfigParam, variant);
        el.updateMatrixWorld(true);
        
        // Rekalkuluj krawędzie
        const finalEdgesAfterExtension = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
        const newLength = finalEdgesAfterExtension.left.distanceTo(finalEdgesAfterExtension.right);
        
        console.log(`[PC]     Nowa długość sekcji: ${newLength.toFixed(4)}m`);
        
        // Zaktualizuj finalEdges
        finalEdges.left.copy(finalEdgesAfterExtension.left);
        finalEdges.right.copy(finalEdgesAfterExtension.right);
        
        console.log(`[PC]     ✅ WYDŁUŻENIE ZAKOŃCZONE`);
      } else {
        console.log(`[PC]   ✅ Długość sekcji OK (krawędzie sięgają do targetów lub są za długie)`);
      }
    }

    // 🎯 DODAJ WIZUALNE MARKERY L i P dla lewej i prawej krawędzi
    // 🎯 SZCZEGÓŁOWE LOGI KOŃCOWE - DOKŁADNA ANALIZA DŁUGOŚCI SEKCJI
    console.log(`[PC] ═══════════════════════════════════════════════════════════`);
    console.log(`[PC] 🎯 FINALNA ANALIZA DŁUGOŚCI SEKCJI ZAMYKAJĄCEJ:`);
    console.log(`[PC] ═══════════════════════════════════════════════════════════`);
    
    // 🎯 NAJPIERW: Znajdź RZECZYWISTE skrajne punkty geometryczne (bez projekcji)
    console.log(`[PC] 🔍 ANALIZA RZECZYWISTYCH SKRAJNYCH PUNKTÓW GEOMETRII (wszystkie meshes):`);
    const bboxFinal = new THREE.Box3().setFromObject(el);
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    let leftmostPoint = new THREE.Vector3();
    let rightmostPoint = new THREE.Vector3();
    let topmostPoint = new THREE.Vector3();
    let bottommostPoint = new THREE.Vector3();
    
    el.traverse((child) => {
      if ((child as any).isMesh) {
        const mesh = child as THREE.Mesh;
        const geom = mesh.geometry;
        const positions = geom.attributes.position;
        if (positions) {
          // Upewnij się, że matrixWorld jest zaktualizowany
          mesh.updateMatrixWorld(true);
          
          for (let i = 0; i < positions.count; i++) {
            const vertex = new THREE.Vector3();
            vertex.fromBufferAttribute(positions, i);
            // Przekształć vertex z local space do world space
            vertex.applyMatrix4(mesh.matrixWorld);
            
            if (vertex.x < minX) {
              minX = vertex.x;
              leftmostPoint.copy(vertex);
            }
            if (vertex.x > maxX) {
              maxX = vertex.x;
              rightmostPoint.copy(vertex);
            }
            if (vertex.z < minZ) {
              minZ = vertex.z;
              bottommostPoint.copy(vertex);
            }
            if (vertex.z > maxZ) {
              maxZ = vertex.z;
              topmostPoint.copy(vertex);
            }
          }
        }
      }
    });
    
    console.log(`[PC]   📍 SKRAJNE PUNKTY GEOMETRII (z wszystkich vertices):`);
    console.log(`[PC]     LEWY (min X)    = (${leftmostPoint.x.toFixed(4)}, ${leftmostPoint.y.toFixed(4)}, ${leftmostPoint.z.toFixed(4)})`);
    console.log(`[PC]     PRAWY (max X)   = (${rightmostPoint.x.toFixed(4)}, ${rightmostPoint.y.toFixed(4)}, ${rightmostPoint.z.toFixed(4)})`);
    console.log(`[PC]     DOLNY (min Z)   = (${bottommostPoint.x.toFixed(4)}, ${bottommostPoint.y.toFixed(4)}, ${bottommostPoint.z.toFixed(4)})`);
    console.log(`[PC]     GÓRNY (max Z)   = (${topmostPoint.x.toFixed(4)}, ${topmostPoint.y.toFixed(4)}, ${topmostPoint.z.toFixed(4)})`);
    console.log(`[PC]   📐 BBOX (world space):`);
    console.log(`[PC]     min = (${bboxFinal.min.x.toFixed(4)}, ${bboxFinal.min.y.toFixed(4)}, ${bboxFinal.min.z.toFixed(4)})`);
    console.log(`[PC]     max = (${bboxFinal.max.x.toFixed(4)}, ${bboxFinal.max.y.toFixed(4)}, ${bboxFinal.max.z.toFixed(4)})`);
    console.log(`[PC]     width (X) = ${(bboxFinal.max.x - bboxFinal.min.x).toFixed(4)}m`);
    console.log(`[PC]     depth (Z) = ${(bboxFinal.max.z - bboxFinal.min.z).toFixed(4)}m`);
    
    // Oblicz aktualne krawędzie sekcji (z getWorldEdges - używają projekcji)
    const finalEdgesFinal = this.getWorldEdges(el, baseWidth, el.scale.x, fenceConfigParam, selectedVariant);
    const finalLeftFinal = new THREE.Vector3(finalEdgesFinal.left.x, 0, finalEdgesFinal.left.z);
    const finalRightFinal = new THREE.Vector3(finalEdgesFinal.right.x, 0, finalEdgesFinal.right.z);
    
    console.log(`[PC]   🔍 PORÓWNANIE: KRAWĘDZIE Z PROJEKCJI vs SKRAJNE PUNKTY:`);
    console.log(`[PC]     finalLeft (z projekcji)  = (${finalLeftFinal.x.toFixed(4)}, ${finalLeftFinal.z.toFixed(4)})`);
    console.log(`[PC]     finalRight (z projekcji) = (${finalRightFinal.x.toFixed(4)}, ${finalRightFinal.z.toFixed(4)})`);
    console.log(`[PC]     leftmostPoint (geometry)  = (${leftmostPoint.x.toFixed(4)}, ${leftmostPoint.z.toFixed(4)})`);
    console.log(`[PC]     rightmostPoint (geometry) = (${rightmostPoint.x.toFixed(4)}, ${rightmostPoint.z.toFixed(4)})`);
    console.log(`[PC]     bottommostPoint (geometry) = (${bottommostPoint.x.toFixed(4)}, ${bottommostPoint.z.toFixed(4)})`);
    console.log(`[PC]     topmostPoint (geometry)   = (${topmostPoint.x.toFixed(4)}, ${topmostPoint.z.toFixed(4)})`);
    
    // Oblicz kierunek sekcji
    const sectionDirFinal = new THREE.Vector3().subVectors(finalLeftFinal, finalRightFinal).normalize();
    
    // Oblicz projekcje błędów na kierunek sekcji
    const leftErrorVecFinal = new THREE.Vector3().subVectors(lastRight2D, finalLeftFinal);
    const rightErrorVecFinal = new THREE.Vector3().subVectors(firstRight2D, finalRightFinal);
    
    const leftProjectionFinal = leftErrorVecFinal.dot(sectionDirFinal);
    const rightProjectionFinal = rightErrorVecFinal.dot(sectionDirFinal);
    
    // Oblicz długości
    const actualLengthFinal = finalLeftFinal.distanceTo(finalRightFinal);
    const targetLengthFinal = lastRight2D.distanceTo(firstRight2D);
    const lengthDiffFinal = actualLengthFinal - targetLengthFinal;
    
    // 🎯 Oblicz krawędzie wzdłuż kierunku sekcji (używając skrajnych punktów geometrycznych)
    const sectionDirForGeometry = new THREE.Vector3().subVectors(lastRight2D, firstRight2D).normalize();
    const leftmost2D = new THREE.Vector3(leftmostPoint.x, 0, leftmostPoint.z);
    const rightmost2D = new THREE.Vector3(rightmostPoint.x, 0, rightmostPoint.z);
    const bottommost2D = new THREE.Vector3(bottommostPoint.x, 0, bottommostPoint.z);
    const topmost2D = new THREE.Vector3(topmostPoint.x, 0, topmostPoint.z);
    
    // Projekcje skrajnych punktów na kierunek sekcji
    const refPoint = new THREE.Vector3().addVectors(lastRight2D, firstRight2D).multiplyScalar(0.5);
    const leftmostProj = new THREE.Vector3().subVectors(leftmost2D, refPoint).dot(sectionDirForGeometry);
    const rightmostProj = new THREE.Vector3().subVectors(rightmost2D, refPoint).dot(sectionDirForGeometry);
    const bottommostProj = new THREE.Vector3().subVectors(bottommost2D, refPoint).dot(sectionDirForGeometry);
    const topmostProj = new THREE.Vector3().subVectors(topmost2D, refPoint).dot(sectionDirForGeometry);
    
    const minProj = Math.min(leftmostProj, rightmostProj, bottommostProj, topmostProj);
    const maxProj = Math.max(leftmostProj, rightmostProj, bottommostProj, topmostProj);
    
    const geometryLeft = new THREE.Vector3().addVectors(refPoint, sectionDirForGeometry.clone().multiplyScalar(minProj));
    const geometryRight = new THREE.Vector3().addVectors(refPoint, sectionDirForGeometry.clone().multiplyScalar(maxProj));
    const geometryLength = geometryLeft.distanceTo(geometryRight);
    
    console.log(`[PC] 📐 KRAWĘDZIE SEKCJI (z projekcji getWorldEdges):`);
    console.log(`[PC]   finalLeft  = (${finalLeftFinal.x.toFixed(4)}, ${finalLeftFinal.z.toFixed(4)})`);
    console.log(`[PC]   finalRight = (${finalRightFinal.x.toFixed(4)}, ${finalRightFinal.z.toFixed(4)})`);
    console.log(`[PC]   Długość sekcji (z projekcji) = ${actualLengthFinal.toFixed(4)}m = ${(actualLengthFinal * 1000).toFixed(1)}mm`);
    
    console.log(`[PC] 📐 KRAWĘDZIE SEKCJI (z rzeczywistej geometrii - wszystkie vertices):`);
    console.log(`[PC]   geometryLeft  = (${geometryLeft.x.toFixed(4)}, ${geometryLeft.z.toFixed(4)})`);
    console.log(`[PC]   geometryRight = (${geometryRight.x.toFixed(4)}, ${geometryRight.z.toFixed(4)})`);
    console.log(`[PC]   Długość sekcji (z geometrii) = ${geometryLength.toFixed(4)}m = ${(geometryLength * 1000).toFixed(1)}mm`);
    console.log(`[PC]   RÓŻNICA (geometria - projekcja) = ${((geometryLength - actualLengthFinal) * 1000).toFixed(1)}mm`);
    
    console.log(`[PC] 🎯 TARGETY (punkty docelowe):`);
    console.log(`[PC]   lastRight  = (${lastRight2D.x.toFixed(4)}, ${lastRight2D.z.toFixed(4)})`);
    console.log(`[PC]   firstRight = (${firstRight2D.x.toFixed(4)}, ${firstRight2D.z.toFixed(4)})`);
    console.log(`[PC]   Wymagana długość = ${targetLengthFinal.toFixed(4)}m = ${(targetLengthFinal * 1000).toFixed(1)}mm`);
    
    console.log(`[PC] 🔍 PORÓWNANIE GEOMETRII vs TARGETY:`);
    const geometryLeftToTarget = geometryLeft.distanceTo(lastRight2D);
    const geometryRightToTarget = geometryRight.distanceTo(firstRight2D);
    const geometryLeftProj = new THREE.Vector3().subVectors(lastRight2D, geometryLeft).dot(sectionDirForGeometry);
    const geometryRightProj = new THREE.Vector3().subVectors(firstRight2D, geometryRight).dot(sectionDirForGeometry);
    console.log(`[PC]   geometryLeft → lastRight:`);
    console.log(`[PC]     geometryLeft = (${geometryLeft.x.toFixed(4)}, ${geometryLeft.z.toFixed(4)})`);
    console.log(`[PC]     lastRight = (${lastRight2D.x.toFixed(4)}, ${lastRight2D.z.toFixed(4)})`);
    console.log(`[PC]     dystans euklidesowy = ${(geometryLeftToTarget * 1000).toFixed(1)}mm`);
    console.log(`[PC]     projekcja na kierunek = ${(geometryLeftProj * 1000).toFixed(1)}mm ${geometryLeftProj > 0 ? '(ZA KRÓTKA - brakuje)' : geometryLeftProj < 0 ? '(ZA DŁUGA - wystaje)' : '(OK)'}`);
    console.log(`[PC]   geometryRight → firstRight:`);
    console.log(`[PC]     geometryRight = (${geometryRight.x.toFixed(4)}, ${geometryRight.z.toFixed(4)})`);
    console.log(`[PC]     firstRight = (${firstRight2D.x.toFixed(4)}, ${firstRight2D.z.toFixed(4)})`);
    console.log(`[PC]     dystans euklidesowy = ${(geometryRightToTarget * 1000).toFixed(1)}mm`);
    console.log(`[PC]     projekcja na kierunek = ${(geometryRightProj * 1000).toFixed(1)}mm ${geometryRightProj < 0 ? '(ZA KRÓTKA - brakuje)' : geometryRightProj > 0 ? '(ZA DŁUGA - wystaje)' : '(OK)'}`);
    console.log(`[PC]   geometryLength vs targetLength:`);
    console.log(`[PC]     geometryLength = ${geometryLength.toFixed(4)}m = ${(geometryLength * 1000).toFixed(1)}mm`);
    console.log(`[PC]     targetLength = ${targetLengthFinal.toFixed(4)}m = ${(targetLengthFinal * 1000).toFixed(1)}mm`);
    console.log(`[PC]     różnica = ${((geometryLength - targetLengthFinal) * 1000).toFixed(1)}mm ${geometryLength < targetLengthFinal ? '(ZA KRÓTKA)' : geometryLength > targetLengthFinal ? '(ZA DŁUGA)' : '(OK)'}`);
    
    console.log(`[PC] 📏 RÓŻNICA DŁUGOŚCI:`);
    console.log(`[PC]   Różnica = ${lengthDiffFinal.toFixed(4)}m = ${(lengthDiffFinal * 1000).toFixed(1)}mm`);
    if (lengthDiffFinal > 0.001) {
      console.log(`[PC]   ⚠️  SEKCJA JEST ZA DŁUGA o ${(lengthDiffFinal * 1000).toFixed(1)}mm`);
    } else if (lengthDiffFinal < -0.001) {
      console.log(`[PC]   ⚠️  SEKCJA JEST ZA KRÓTKA o ${(-lengthDiffFinal * 1000).toFixed(1)}mm`);
    } else {
      console.log(`[PC]   ✅ DŁUGOŚĆ IDEALNA (różnica < 1mm)`);
    }
    
    console.log(`[PC] 🔍 PROJEKCJE NA KIERUNEK SEKCJI:`);
    console.log(`[PC]   Kierunek sekcji = (${sectionDirFinal.x.toFixed(4)}, ${sectionDirFinal.z.toFixed(4)})`);
    console.log(`[PC]   LEWA projekcja  = ${(leftProjectionFinal * 1000).toFixed(1)}mm ${leftProjectionFinal > 0 ? '(ZA KRÓTKA - brakuje)' : leftProjectionFinal < 0 ? '(ZA DŁUGA - wystaje)' : '(OK)'}`);
    console.log(`[PC]   PRAWA projekcja = ${(rightProjectionFinal * 1000).toFixed(1)}mm ${rightProjectionFinal < 0 ? '(ZA KRÓTKA - brakuje)' : rightProjectionFinal > 0 ? '(ZA DŁUGA - wystaje)' : '(OK)'}`);
    
    // Oblicz dystanse euklidesowe
    const leftDistanceFinal = finalLeftFinal.distanceTo(lastRight2D);
    const rightDistanceFinal = finalRightFinal.distanceTo(firstRight2D);
    
    console.log(`[PC] 📍 DYSTANSE EUKLIDESOWE DO TARGETÓW:`);
    console.log(`[PC]   LEWA krawędź → lastRight  = ${(leftDistanceFinal * 1000).toFixed(1)}mm`);
    console.log(`[PC]   PRAWA krawędź → firstRight = ${(rightDistanceFinal * 1000).toFixed(1)}mm`);
    
    // Podsumowanie
    console.log(`[PC] ═══════════════════════════════════════════════════════════`);
    console.log(`[PC] 📊 PODSUMOWANIE:`);
    
    // 🎯 LOGOWANIE WYKRYTEGO WARIANTU W PODSUMOWANIU
    console.log(`[PC] 🔍 WYKRYTY WARIANT:`);
    console.log(`[PC]   fenceConfigParam?.id = "${fenceConfigParam?.id}"`);
    console.log(`[PC]   selectedVariant (parametr wejściowy) = "${selectedVariant}"`);
    console.log(`[PC]   variant (określony przez determineVariant) = "${variant}"`);
    
    // Sprawdź warunki dla Muro
    const isMuroMixSummary = fenceConfigParam?.id?.startsWith('lupane-muro');
    const isMuroP22Summary = (fenceConfigParam?.id === 'lupane-muro-p22') || (isMuroMixSummary && selectedVariant === 'p22');
    const isMuroS38Summary = (fenceConfigParam?.id === 'lupane-muro-s38') || (isMuroMixSummary && selectedVariant === 's38');
    const isMuroP38Summary = (fenceConfigParam?.id === 'lupane-muro-p38') || (isMuroMixSummary && selectedVariant === 'p38');
    const isMuroS22Summary = (fenceConfigParam?.id === 'lupane-muro-s22') || (isMuroMixSummary && selectedVariant === 's22');
    
    console.log(`[PC]   isMuroMix = ${isMuroMixSummary}`);
    console.log(`[PC]   isMuroP22 = ${isMuroP22Summary}`);
    console.log(`[PC]   isMuroS38 = ${isMuroS38Summary}`);
    console.log(`[PC]   isMuroP38 = ${isMuroP38Summary}`);
    console.log(`[PC]   isMuroS22 = ${isMuroS22Summary}`);
    
    if (isMuroS38Summary) {
      console.log(`[PC]   🎯 WYKRYTO S38 - używa grup: group_21, group_63, group_0`);
    } else if (isMuroP22Summary) {
      console.log(`[PC]   🎯 WYKRYTO P22 - używa grup: group_13, group_42, group_0`);
    } else if (isMuroP38Summary) {
      console.log(`[PC]   🎯 WYKRYTO P38 - używa grup: group_21, group_57, group_0`);
    } else if (isMuroS22Summary) {
      console.log(`[PC]   🎯 WYKRYTO S22 - używa grup: group_44, group_77, group_0`);
    }
    
    console.log(`[PC] ──────────────────────────────────────────────────────────`);
    
    if (leftProjectionFinal > 0.5 || rightProjectionFinal < -0.5) {
      console.log(`[PC]   ⚠️  SEKCJA JEST ZA KRÓTKA`);
      if (leftProjectionFinal > 0.5) {
        console.log(`[PC]     - LEWA krawędź nie sięga do targetu (brakuje ${(leftProjectionFinal * 1000).toFixed(1)}mm)`);
      }
      if (rightProjectionFinal < -0.5) {
        console.log(`[PC]     - PRAWA krawędź nie sięga do targetu (brakuje ${(-rightProjectionFinal * 1000).toFixed(1)}mm)`);
      }
    } else if (leftProjectionFinal < -0.5 || rightProjectionFinal > 0.5) {
      console.log(`[PC]   ⚠️  SEKCJA JEST ZA DŁUGA`);
      if (leftProjectionFinal < -0.5) {
        console.log(`[PC]     - LEWA krawędź wystaje poza target (wystaje ${(-leftProjectionFinal * 1000).toFixed(1)}mm)`);
      }
      if (rightProjectionFinal > 0.5) {
        console.log(`[PC]     - PRAWA krawędź wystaje poza target (wystaje ${(rightProjectionFinal * 1000).toFixed(1)}mm)`);
      }
    } else {
      console.log(`[PC]   ✅ SEKCJA MA PRAWIDŁOWĄ DŁUGOŚĆ (różnice < 0.5mm)`);
    }
    console.log(`[PC] ═══════════════════════════════════════════════════════════`);
    
    // 🎯 MARKERY BĘDĄ USTAWIONE PO KOREKCIE POZYCJI DLA S38 (jeśli dotyczy)

    // 📐 ANALIZA SZEROKOŚCI
    console.log(`\n[PC] 📐 ANALIZA SZEROKOŚCI sekcji zamykającej:`);
    // 🎯 POPRAWKA: Używamy actualSectionLength (rzeczywista długość w kierunku sekcji) zamiast actualWidth z bbox (który jest tylko w osi X)
    const realSectionWidth = actualSectionLength; // To jest rzeczywista długość sekcji obliczona z getWorldEdges
    const calculatedWidth = modelBaseWidth * scaleX; // 🎯 POPRAWKA: używaj modelBaseWidth, nie baseWidth!
    const widthDifference = realSectionWidth - calculatedWidth;
    const widthDifferencePercent = (widthDifference / calculatedWidth) * 100;
    
    console.log(`[PC]   Szerokości (teoretyczna vs rzeczywista):`);
    console.log(`[PC]     modelBaseWidth = ${modelBaseWidth.toFixed(4)}m`);
    console.log(`[PC]     scaleX = ${scaleX.toFixed(3)}`);
    console.log(`[PC]     calculatedWidth = modelBaseWidth × scaleX = ${calculatedWidth.toFixed(4)}m`);
    console.log(`[PC]     realSectionWidth (z projekcji, rzeczywista długość) = ${realSectionWidth.toFixed(4)}m`);
    console.log(`[PC]     targetGap (pierwotny, obliczony na początku) = ${targetGap.toFixed(4)}m`);
    console.log(`[PC]     targetLengthFinal (rzeczywisty target, odległość lastRight→firstRight) = ${targetLengthFinal.toFixed(4)}m`);
    
    console.log(`[PC]   Różnice:`);
    console.log(`[PC]     realSectionWidth - calculatedWidth = ${widthDifference.toFixed(4)}m (${widthDifferencePercent.toFixed(1)}%)`);
    console.log(`[PC]     realSectionWidth - targetGap (pierwotny) = ${(realSectionWidth - targetGap).toFixed(4)}m`);
    console.log(`[PC]     realSectionWidth - targetLengthFinal (rzeczywisty) = ${(realSectionWidth - targetLengthFinal).toFixed(4)}m ${realSectionWidth < targetLengthFinal ? '(ZA KRÓTKA)' : realSectionWidth > targetLengthFinal ? '(ZA DŁUGA)' : '(OK)'}`);
    
    console.log(`[PC]   Ocena:`);
    if (Math.abs(widthDifference) > 0.5) {
      console.log(`[PC]     ⚠️ DUŻA RÓŻNICA (> 0.5m)! Może być problem z modelem lub skalowaniem`);
    } else if (Math.abs(widthDifference) > 0.1) {
      console.log(`[PC]     ⚠️ ŚREDNIA RÓŻNICA (> 0.1m) - możliwy problem z metalem lub modelem`);
    } else if (Math.abs(widthDifference) > 0.05) {
      console.log(`[PC]     ⚠️ MAŁA RÓŻNICA (> 0.05m) - prawdopodobnie OK`);
    } else {
      console.log(`[PC]     ✅ BARDZO MAŁA RÓŻNICA (< 0.05m) - doskonale!`);
    }
    
    if (realSectionWidth > targetGap + 0.1) {
      console.log(`[PC]     ⚠️ SEKCJA ZA DŁUGA o ${((realSectionWidth - targetGap) * 1000).toFixed(1)}mm`);
    } else if (realSectionWidth < targetGap - 0.1) {
      console.log(`[PC]     ⚠️ SEKCJA ZA KRÓTKA o ${((targetGap - realSectionWidth) * 1000).toFixed(1)}mm`);
    } else {
      console.log(`[PC]     ✅ Długość sekcji OK (różnica < 100mm)`);
    }
    
    // 🎯 Pobierz wysokość z userData lub użyj domyślnej z konfiguracji
    let actualHeight = el.userData?.height;
    if (!actualHeight || actualHeight === 0) {
      // Fallback: użyj wysokości z konfiguracji
      const defaultHeight = fenceConfigParam?.elements?.section?.defaultHeight || 
                           fenceConfigParam?.elements?.post?.defaultHeight || 
                           6; // Domyślnie 6 bloków
      const blockHeightCm = fenceConfigParam?.elements?.post?.blockHeight || 30;
      const decorationBlockHeightCm = fenceConfigParam?.elements?.post?.decorationBlockHeight || blockHeightCm;
      const decorationBlocksCount = fenceConfigParam?.elements?.section?.decorationBlocksCount || 
                                   fenceConfigParam?.elements?.post?.decorationBlocksCount || 
                                   1;
      const regularBlocks = Math.max(defaultHeight - decorationBlocksCount, 0);
      const regularHeight = regularBlocks * (blockHeightCm / 100);
      const decorationHeight = decorationBlocksCount * (decorationBlockHeightCm / 100);
      actualHeight = regularHeight + decorationHeight;
    }

    console.log(`[PC] 📐 Wysokość sekcji zamykającej: ${actualHeight.toFixed(4)}m`);

    // 🎯 MEGA SZCZEGÓŁOWE LOGI NA KOŃCU - DIAGNOSTYKA WSZYSTKIEGO
    console.log(`\n[PC] ═══════════════════════════════════════════════════════════`);
    console.log(`[PC] 🔬🔬🔬 MEGA SZCZEGÓŁOWA DIAGNOSTYKA - WSZYSTKIE DANE 🔬🔬🔬`);
    console.log(`[PC] ═══════════════════════════════════════════════════════════\n`);
    
    // 1. TRANSFORMACJE ELEMENTU
    console.log(`[PC] 📐 1. TRANSFORMACJE ELEMENTU:`);
    console.log(`[PC]     el.position = (${el.position.x.toFixed(4)}, ${el.position.y.toFixed(4)}, ${el.position.z.toFixed(4)})`);
    console.log(`[PC]     el.rotation.y = ${(el.rotation.y * 180 / Math.PI).toFixed(2)}° (${el.rotation.y.toFixed(4)} rad)`);
    console.log(`[PC]     el.scale = (${el.scale.x.toFixed(4)}, ${el.scale.y.toFixed(4)}, ${el.scale.z.toFixed(4)})`);
    console.log(`[PC]     scaleX (używany) = ${scaleX.toFixed(4)}`);
    console.log(`[PC]     modelBaseWidth = ${modelBaseWidth.toFixed(4)}m`);
    console.log(`[PC]     calculatedWidth = modelBaseWidth × scaleX = ${(modelBaseWidth * scaleX).toFixed(4)}m\n`);
    
    // 2. TARGETY (PUNKTY DOCELOWE)
    console.log(`[PC] 🎯 2. TARGETY (PUNKTY DOCELOWE):`);
    console.log(`[PC]     lastRight  = (${lastRight2D.x.toFixed(4)}, ${lastRight2D.z.toFixed(4)})`);
    console.log(`[PC]     firstRight = (${firstRight2D.x.toFixed(4)}, ${firstRight2D.z.toFixed(4)})`);
    console.log(`[PC]     targetLengthFinal (odległość lastRight→firstRight) = ${targetLengthFinal.toFixed(4)}m = ${(targetLengthFinal * 1000).toFixed(1)}mm`);
    console.log(`[PC]     targetGap (pierwotny) = ${targetGap.toFixed(4)}m = ${(targetGap * 1000).toFixed(1)}mm`);
    console.log(`[PC]     różnica targetGap - targetLengthFinal = ${((targetGap - targetLengthFinal) * 1000).toFixed(1)}mm\n`);
    
    // 3. RZECZYWISTE KRAWĘDZIE (RÓŻNE METODY)
    console.log(`[PC] 📏 3. RZECZYWISTE KRAWĘDZIE (RÓŻNE METODY):`);
    console.log(`[PC]     A) Z PROJEKCJI (getWorldEdges):`);
    console.log(`[PC]        finalLeft  = (${finalLeftFinal.x.toFixed(4)}, ${finalLeftFinal.z.toFixed(4)})`);
    console.log(`[PC]        finalRight = (${finalRightFinal.x.toFixed(4)}, ${finalRightFinal.z.toFixed(4)})`);
    console.log(`[PC]        actualLengthFinal = ${actualLengthFinal.toFixed(4)}m = ${(actualLengthFinal * 1000).toFixed(1)}mm`);
    console.log(`[PC]        różnica z targetLengthFinal = ${((actualLengthFinal - targetLengthFinal) * 1000).toFixed(1)}mm`);
    console.log(`[PC]     B) Z GEOMETRII (wszystkie vertices):`);
    console.log(`[PC]        geometryLeft  = (${geometryLeft.x.toFixed(4)}, ${geometryLeft.z.toFixed(4)})`);
    console.log(`[PC]        geometryRight = (${geometryRight.x.toFixed(4)}, ${geometryRight.z.toFixed(4)})`);
    console.log(`[PC]        geometryLength = ${geometryLength.toFixed(4)}m = ${(geometryLength * 1000).toFixed(1)}mm`);
    console.log(`[PC]        różnica z targetLengthFinal = ${((geometryLength - targetLengthFinal) * 1000).toFixed(1)}mm (ZA KRÓTKA)`);
    console.log(`[PC]     C) Z BBOX (bounding box):`);
    const bboxForDiagnostics = new THREE.Box3().setFromObject(el);
    const bboxWidthForDiagnostics = bboxForDiagnostics.max.x - bboxForDiagnostics.min.x;
    console.log(`[PC]        bbox.min = (${bboxForDiagnostics.min.x.toFixed(4)}, ${bboxForDiagnostics.min.y.toFixed(4)}, ${bboxForDiagnostics.min.z.toFixed(4)})`);
    console.log(`[PC]        bbox.max = (${bboxForDiagnostics.max.x.toFixed(4)}, ${bboxForDiagnostics.max.y.toFixed(4)}, ${bboxForDiagnostics.max.z.toFixed(4)})`);
    console.log(`[PC]        bboxWidth = ${bboxWidthForDiagnostics.toFixed(4)}m = ${(bboxWidthForDiagnostics * 1000).toFixed(1)}mm`);
    console.log(`[PC]        różnica z targetLengthFinal = ${((bboxWidthForDiagnostics - targetLengthFinal) * 1000).toFixed(1)}mm\n`);
    
    // 4. WSZYSTKIE GRUPY I ICH POZYCJE
    console.log(`[PC] 🧩 4. WSZYSTKIE GRUPY I ICH POZYCJE:`);
    const groupsFound: { name: string; type: string; bbox: THREE.Box3; worldPos: THREE.Vector3 }[] = [];
    el.traverse((child) => {
      const name = child.name || '';
      if (name.startsWith('group_')) {
        const bbox = new THREE.Box3().setFromObject(child);
        const center = new THREE.Vector3();
        bbox.getCenter(center);
        const width = bbox.max.x - bbox.min.x;
        const depth = bbox.max.z - bbox.min.z;
        let type = 'unknown';
        if (name === 'group_0') type = 'metal';
        else if (name === 'group_13' || name === 'group_42') type = 'block (P22)';
        else if (name === 'group_21' || name === 'group_63') type = 'block (S38/P38)';
        else if (name === 'group_44' || name === 'group_77') type = 'block (S22)';
        else if (name === 'group_57') type = 'decoration';
        groupsFound.push({ name, type, bbox, worldPos: center });
      }
    });
    console.log(`[PC]     Znaleziono ${groupsFound.length} grup:`);
    groupsFound.forEach((g, i) => {
      const width = g.bbox.max.x - g.bbox.min.x;
      const depth = g.bbox.max.z - g.bbox.min.z;
      console.log(`[PC]     Grupa #${i + 1}: ${g.name} (${g.type})`);
      console.log(`[PC]       bbox: X=[${g.bbox.min.x.toFixed(4)}, ${g.bbox.max.x.toFixed(4)}], Z=[${g.bbox.min.z.toFixed(4)}, ${g.bbox.max.z.toFixed(4)}]`);
      console.log(`[PC]       szerokość = ${width.toFixed(4)}m, głębokość = ${depth.toFixed(4)}m`);
      console.log(`[PC]       środek (world) = (${g.worldPos.x.toFixed(4)}, ${g.worldPos.y.toFixed(4)}, ${g.worldPos.z.toFixed(4)})`);
    });
    console.log(``);
    
    // 5. SZCZEGÓŁOWA ANALIZA DLACZEGO SEKCJA JEST ZA KRÓTKA
    console.log(`[PC] 🔍 5. ANALIZA DLACZEGO SEKCJA JEST ZA KRÓTKA:`);
    const geometryToTargetDiff = geometryLength - targetLengthFinal;
    console.log(`[PC]     geometryLength (${geometryLength.toFixed(4)}m) - targetLengthFinal (${targetLengthFinal.toFixed(4)}m) = ${(geometryToTargetDiff * 1000).toFixed(1)}mm`);
    if (geometryToTargetDiff < -0.1) {
      console.log(`[PC]     ⚠️ SEKCJA JEST ZA KRÓTKA O ${(-geometryToTargetDiff * 1000).toFixed(1)}mm`);
      console.log(`[PC]     💡 Potrzebne wydłużenie: ${(-geometryToTargetDiff * 1000).toFixed(1)}mm`);
      const neededScaleFactor = targetLengthFinal / geometryLength;
      console.log(`[PC]     💡 Współczynnik skalowania potrzebny: ${neededScaleFactor.toFixed(4)}`);
      const currentScaleX = el.scale.x;
      const neededScaleX = currentScaleX * neededScaleFactor;
      console.log(`[PC]     💡 Obecny scale.x = ${currentScaleX.toFixed(4)}, potrzebny scale.x = ${neededScaleX.toFixed(4)}`);
      console.log(`[PC]     💡 Różnica w scale.x = ${((neededScaleX - currentScaleX) * 1000).toFixed(1)} (${(((neededScaleX - currentScaleX) / currentScaleX) * 100).toFixed(1)}%)`);
    } else if (geometryToTargetDiff > 0.1) {
      console.log(`[PC]     ⚠️ SEKCJA JEST ZA DŁUGA O ${(geometryToTargetDiff * 1000).toFixed(1)}mm`);
    } else {
      console.log(`[PC]     ✅ SEKCJA MA PRAWIDŁOWĄ DŁUGOŚĆ (różnica < 100mm)`);
    }
    console.log(``);
    
    // 6. PORÓWNANIE Z P22 (jeśli to S38)
    if (isMuroS38Summary) {
      console.log(`[PC] 🔄 6. PORÓWNANIE S38 Z P22:`);
      console.log(`[PC]     S38 używa swoich grup: group_21, group_63, group_0`);
      console.log(`[PC]     P22 używa swoich grup: group_13, group_42, group_0`);
      const p22Groups = groupsFound.filter(g => g.name === 'group_13' || g.name === 'group_42');
      const s38Groups = groupsFound.filter(g => g.name === 'group_21' || g.name === 'group_63');
      console.log(`[PC]     Znalezione grupy P22 (group_13, group_42): ${p22Groups.length}`);
      console.log(`[PC]     Znalezione grupy S38 (group_21, group_63): ${s38Groups.length}`);
      if (s38Groups.length > 0) {
        console.log(`[PC]     ✅ Model S38 ma właściwe grupy S38 (group_21, group_63)`);
      } else {
        console.log(`[PC]     ⚠️ PROBLEM: Model S38 nie ma grup S38 (group_21, group_63)!`);
      }
      console.log(``);
    }
    
    // 7. PODSUMOWANIE KOŃCOWE
    console.log(`[PC] 📊 7. PODSUMOWANIE KOŃCOWE:`);
    console.log(`[PC]     Metoda obliczania długości: geometryLength (z rzeczywistych vertices)`);
    console.log(`[PC]     geometryLength = ${geometryLength.toFixed(4)}m = ${(geometryLength * 1000).toFixed(1)}mm`);
    console.log(`[PC]     targetLengthFinal = ${targetLengthFinal.toFixed(4)}m = ${(targetLengthFinal * 1000).toFixed(1)}mm`);
    console.log(`[PC]     RÓŻNICA = ${((geometryLength - targetLengthFinal) * 1000).toFixed(1)}mm`);
    if (geometryLength < targetLengthFinal - 0.001) {
      console.log(`[PC]     ❌ SEKCJA JEST ZA KRÓTKA - potrzeba wydłużenia o ${((targetLengthFinal - geometryLength) * 1000).toFixed(1)}mm`);
    } else if (geometryLength > targetLengthFinal + 0.001) {
      console.log(`[PC]     ⚠️ SEKCJA JEST ZA DŁUGA - potrzeba skrócenia o ${((geometryLength - targetLengthFinal) * 1000).toFixed(1)}mm`);
    } else {
      console.log(`[PC]     ✅ SEKCJA MA PRAWIDŁOWĄ DŁUGOŚĆ`);
    }
    console.log(`[PC] ═══════════════════════════════════════════════════════════`);
    console.log(`[PC] 🔬🔬🔬 KONIEC MEGA SZCZEGÓŁOWEJ DIAGNOSTYKI 🔬🔬🔬`);
    console.log(`[PC] ═══════════════════════════════════════════════════════════\n`);
    
    // 🎯 AUTOMATYCZNA KOREKTA DŁUGOŚCI DLA S38 - PROSTA LOGIKA JAK ROYAL
    console.log(`[PC] 🔍 SPRAWDZAM CZY S38: isMuroS38Summary = ${isMuroS38Summary}`);
    if (isMuroS38Summary) {
      console.log(`[PC] ═══════════════════════════════════════════════════════════`);
      console.log(`[PC] 🎯🎯🎯 S38: PROSTA KOREKTA SKALOWANIA (jak Royal) 🎯🎯🎯`);
      console.log(`[PC] ═══════════════════════════════════════════════════════════`);
      
      // 🎯 DLA S38: Użyj DIAGNOSTYCZNEJ wartości geometryLength
      // Oblicz projekcję wszystkich mesh bboxes na kierunek ogrodzenia
      const bboxS38 = new THREE.Box3().setFromObject(el);
      const bboxCenterS38 = new THREE.Vector3();
      bboxS38.getCenter(bboxCenterS38);
      
      // Oblicz kierunek ogrodzenia
      const directionS38 = new THREE.Vector3(
        -Math.sin(el.rotation.y),
        0,
        -Math.cos(el.rotation.y)
      ).normalize();
      
      // Znajdź min/max projekcje narożników BBOX wszystkich meshów na kierunek
      let minProjS38 = Infinity;
      let maxProjS38 = -Infinity;
      let processedMeshes = 0;
      
      el.updateMatrixWorld(true);
      
      el.traverse((child: THREE.Object3D) => {
        if (child instanceof THREE.Mesh && child.geometry) {
          // Pobierz bbox mesha w world space
          const meshBbox = new THREE.Box3().setFromObject(child);
          
          // Pobierz 8 narożników bbox
          const corners = [
            new THREE.Vector3(meshBbox.min.x, meshBbox.min.y, meshBbox.min.z),
            new THREE.Vector3(meshBbox.max.x, meshBbox.min.y, meshBbox.min.z),
            new THREE.Vector3(meshBbox.min.x, meshBbox.max.y, meshBbox.min.z),
            new THREE.Vector3(meshBbox.max.x, meshBbox.max.y, meshBbox.min.z),
            new THREE.Vector3(meshBbox.min.x, meshBbox.min.y, meshBbox.max.z),
            new THREE.Vector3(meshBbox.max.x, meshBbox.min.y, meshBbox.max.z),
            new THREE.Vector3(meshBbox.min.x, meshBbox.max.y, meshBbox.max.z),
            new THREE.Vector3(meshBbox.max.x, meshBbox.max.y, meshBbox.max.z)
          ];
          
          for (const corner of corners) {
            const toCorner = new THREE.Vector3().subVectors(corner, bboxCenterS38);
            const proj = toCorner.dot(directionS38);
            
            if (proj < minProjS38) minProjS38 = proj;
            if (proj > maxProjS38) maxProjS38 = proj;
          }
          processedMeshes++;
        }
      });
      
      // Geometry length = różnica między max i min projekcją
      const geometryLengthS38 = maxProjS38 - minProjS38;
      console.log(`[PC]     Processed ${processedMeshes} meshes, minProj=${minProjS38.toFixed(4)}, maxProj=${maxProjS38.toFixed(4)}`);
      
      // Dla porównania - inne metryki
      const bboxSizeS38 = new THREE.Vector3();
      bboxS38.getSize(bboxSizeS38);
      const bboxDiagonalS38 = Math.sqrt(bboxSizeS38.x * bboxSizeS38.x + bboxSizeS38.z * bboxSizeS38.z);
      const currentEdgesS38 = this.getWorldEdges(el, baseWidth, el.scale.x, fenceConfigParam, selectedVariant);
      const projectionLengthS38 = currentEdgesS38.left.distanceTo(currentEdgesS38.right);
      
      console.log(`[PC]     🎯 GEOMETRY LENGTH (z vertices bloków) = ${geometryLengthS38.toFixed(4)}m`);
      console.log(`[PC]     bboxDiagonalS38 (przekątna) = ${bboxDiagonalS38.toFixed(4)}m`);
      console.log(`[PC]     projectionLengthS38 (z getWorldEdges) = ${projectionLengthS38.toFixed(4)}m`);
      console.log(`[PC]     targetLengthFinal = ${targetLengthFinal.toFixed(4)}m`);
      console.log(`[PC]     obecny scale.x = ${el.scale.x.toFixed(4)}`);
      
      // 🎯 SKALOWANIE NA PODSTAWIE GEOMETRY LENGTH (rzeczywistej szerokości bloków)
      const scaleCorrectionS38 = targetLengthFinal / geometryLengthS38;
      const newScaleXS38 = el.scale.x * scaleCorrectionS38;
      
      console.log(`[PC]     scaleCorrectionS38 = ${targetLengthFinal.toFixed(4)} / ${geometryLengthS38.toFixed(4)} = ${scaleCorrectionS38.toFixed(4)}`);
      console.log(`[PC]     🎯 NOWY scale.x: ${el.scale.x.toFixed(4)} × ${scaleCorrectionS38.toFixed(4)} = ${newScaleXS38.toFixed(4)}`);
      
      // Zastosuj nowy scale
      el.scale.x = newScaleXS38;
      scaleX = newScaleXS38;
      el.updateMatrixWorld(true);
      
      // Zastosuj Royal/Metal correction
      this.applyRoyalMetalCorrection(el, fenceConfigParam, variant);
      el.updateMatrixWorld(true);
      
      // 🎯 WERYFIKACJA SKALOWANIA
      const edgesAfterS38Scale = this.getWorldEdges(el, baseWidth, scaleX, fenceConfigParam, selectedVariant);
      const lengthAfterS38Scale = edgesAfterS38Scale.left.distanceTo(edgesAfterS38Scale.right);
      console.log(`[PC]     🔍 WERYFIKACJA: długość po skalowaniu = ${lengthAfterS38Scale.toFixed(4)}m, target = ${targetLengthFinal.toFixed(4)}m, różnica = ${((lengthAfterS38Scale - targetLengthFinal) * 1000).toFixed(1)}mm`);
      
      // 🎯 KOREKTA POZYCJI S38 - używamy BBOX (nie projekcji!)
      // Środek między targetami to idealna pozycja środka sekcji
      console.log(`[PC]     🔧 KOREKTA POZYCJI S38 (oparta na BBOX, nie projekcji):`);
      
      // Oblicz środek między targetami (tam gdzie powinien być środek sekcji)
      const targetCenter = new THREE.Vector3().addVectors(lastRight, firstRight).multiplyScalar(0.5);
      
      // Oblicz aktualny środek bbox sekcji
      const bboxAfterScale = new THREE.Box3().setFromObject(el);
      const bboxCenterAfterScale = new THREE.Vector3();
      bboxAfterScale.getCenter(bboxCenterAfterScale);
      
      // Korekta = różnica między docelowym środkiem a aktualnym środkiem bbox
      const posCorrection = new THREE.Vector3().subVectors(targetCenter, bboxCenterAfterScale);
      posCorrection.y = 0; // Zachowaj Y
      
      console.log(`[PC]        targetCenter (środek między lastRight i firstRight) = (${targetCenter.x.toFixed(3)}, ${targetCenter.z.toFixed(3)})`);
      console.log(`[PC]        bboxCenterAfterScale = (${bboxCenterAfterScale.x.toFixed(3)}, ${bboxCenterAfterScale.z.toFixed(3)})`);
      console.log(`[PC]        posCorrection = (${posCorrection.x.toFixed(3)}, ${posCorrection.z.toFixed(3)})`);
      
      el.position.x += posCorrection.x;
      el.position.z += posCorrection.z;
      el.updateMatrixWorld(true);
      
      console.log(`[PC]        🎯 Nowa pozycja: (${el.position.x.toFixed(3)}, ${el.position.z.toFixed(3)})`);
      
      // 🎯 FINALNE KRAWĘDZIE - dla S38 używamy projekcji narożników bbox na kierunek ogrodzenia
      const finalBboxS38 = new THREE.Box3().setFromObject(el);
      const finalBboxMin = finalBboxS38.min;
      const finalBboxMax = finalBboxS38.max;
      const finalBboxSize = new THREE.Vector3();
      finalBboxS38.getSize(finalBboxSize);
      
      // Przekątna bbox = rzeczywista szerokość obrócnego modelu
      const bboxDiagonalFinal = Math.sqrt(finalBboxSize.x * finalBboxSize.x + finalBboxSize.z * finalBboxSize.z);
      
      // Oblicz wszystkie 4 narożniki bbox w płaszczyźnie XZ
      const corners = [
        new THREE.Vector3(finalBboxMin.x, 0.257, finalBboxMin.z),
        new THREE.Vector3(finalBboxMax.x, 0.257, finalBboxMin.z),
        new THREE.Vector3(finalBboxMin.x, 0.257, finalBboxMax.z),
        new THREE.Vector3(finalBboxMax.x, 0.257, finalBboxMax.z)
      ];
      
      // Znajdź który narożnik jest najbliżej którego targetu
      let closestToLastRight = corners[0];
      let closestToFirstRight = corners[0];
      let minDistLastRight = lastRight.distanceTo(corners[0]);
      let minDistFirstRight = firstRight.distanceTo(corners[0]);
      
      for (const corner of corners) {
        const distLastRight = lastRight.distanceTo(corner);
        const distFirstRight = firstRight.distanceTo(corner);
        if (distLastRight < minDistLastRight) {
          minDistLastRight = distLastRight;
          closestToLastRight = corner;
        }
        if (distFirstRight < minDistFirstRight) {
          minDistFirstRight = distFirstRight;
          closestToFirstRight = corner;
        }
      }
      
      const finalLeftErrS38 = minDistLastRight;
      const finalRightErrS38 = minDistFirstRight;
      
      console.log(`[PC]     📊 FINALNA WERYFIKACJA S38 (oparta na PRZEKĄTNEJ BBOX):`);
      console.log(`[PC]        bbox.min = (${finalBboxMin.x.toFixed(3)}, ${finalBboxMin.z.toFixed(3)})`);
      console.log(`[PC]        bbox.max = (${finalBboxMax.x.toFixed(3)}, ${finalBboxMax.z.toFixed(3)})`);
      console.log(`[PC]        bboxDiagonal = ${bboxDiagonalFinal.toFixed(4)}m, target = ${targetLengthFinal.toFixed(4)}m`);
      console.log(`[PC]        różnica = ${((bboxDiagonalFinal - targetLengthFinal) * 1000).toFixed(1)}mm`);
      console.log(`[PC]        closestToLastRight = (${closestToLastRight.x.toFixed(3)}, ${closestToLastRight.z.toFixed(3)})`);
      console.log(`[PC]        closestToFirstRight = (${closestToFirstRight.x.toFixed(3)}, ${closestToFirstRight.z.toFixed(3)})`);
      console.log(`[PC]        closestToLastRight → lastRight = ${(finalLeftErrS38 * 1000).toFixed(1)}mm`);
      console.log(`[PC]        closestToFirstRight → firstRight = ${(finalRightErrS38 * 1000).toFixed(1)}mm`);
      
      // Ustaw finalEdges na najbliższe narożniki
      finalEdges.left.copy(closestToLastRight);
      finalEdges.right.copy(closestToFirstRight);
      finalLeftErr = finalLeftErrS38;
      finalRightErr = finalRightErrS38;
      
      // Dodaj markery na najbliższych narożnikach
      this.addEdgeMarkers(closestToLastRight, closestToFirstRight);
      console.log(`[PC]     ✅ S38 ZAKOŃCZONE - sekcja przeskalowana na podstawie PRZEKĄTNEJ BBOX`);
    } else {
      // 🎯 DLA INNYCH WARIANTÓW - ustaw markery tutaj (jeśli nie były ustawione wcześniej)
      if (typeof finalEdges !== 'undefined') {
        this.addEdgeMarkers(finalEdges.left, finalEdges.right);
      }
    }

    return { 
      success: true, 
      message: `✅ ${gap.toFixed(2)}m (L:${finalLeftErr.toFixed(3)}m R:${finalRightErr.toFixed(3)}m)`, 
      closingElementId: id, 
      gapDistance: gap, 
      finalGap: finalLeftErr + finalRightErr,
      addedSection: {
        id: id,
        position: { x: el.position.x, y: el.position.y, z: el.position.z },
        dimensions: { width: realSectionWidth, height: actualHeight }, // 🎯 POPRAWKA: używamy realSectionWidth zamiast actualWidth z bbox
        widthMultiplier: scaleX, // scaleX jest faktycznym mnożnikiem szerokości
        baseWidth: baseWidth
      }
    };
  }

  private getWorldEdges(el: THREE.Group, baseWidth: number, scaleX: number, fenceConfigParam?: any, selectedVariant?: string): { left: THREE.Vector3; right: THREE.Vector3; maxLeftOverhang: number; maxRightOverhang: number } {
    // Dla wszystkich przypadków: użyj bbox (tak samo jak dla P22)
    console.log(`\n[PC] 🔍 getWorldEdges - Obliczam krawędzie w WORLD SPACE:`);
    
    const bbox = new THREE.Box3().setFromObject(el);
    const bboxCenter = new THREE.Vector3();
    bbox.getCenter(bboxCenter);

    const actualWidth = (bbox.max.x - bbox.min.x);
    const halfWidth = actualWidth / 2;
    
    // 🔍 SZCZEGÓŁOWA ANALIZA BBOX (wszystkie narożniki)
    console.log(`[PC]   🔍 WSZYSTKIE 8 NAROŻNIKÓW BBOX (world space):`);
    console.log(`[PC]     1. min-min-min = (${bbox.min.x.toFixed(4)}, ${bbox.min.y.toFixed(4)}, ${bbox.min.z.toFixed(4)})`);
    console.log(`[PC]     2. max-min-min = (${bbox.max.x.toFixed(4)}, ${bbox.min.y.toFixed(4)}, ${bbox.min.z.toFixed(4)})`);
    console.log(`[PC]     3. min-max-min = (${bbox.min.x.toFixed(4)}, ${bbox.max.y.toFixed(4)}, ${bbox.min.z.toFixed(4)})`);
    console.log(`[PC]     4. max-max-min = (${bbox.max.x.toFixed(4)}, ${bbox.max.y.toFixed(4)}, ${bbox.min.z.toFixed(4)})`);
    console.log(`[PC]     5. min-min-max = (${bbox.min.x.toFixed(4)}, ${bbox.min.y.toFixed(4)}, ${bbox.max.z.toFixed(4)})`);
    console.log(`[PC]     6. max-min-max = (${bbox.max.x.toFixed(4)}, ${bbox.min.y.toFixed(4)}, ${bbox.max.z.toFixed(4)})`);
    console.log(`[PC]     7. min-max-max = (${bbox.min.x.toFixed(4)}, ${bbox.max.y.toFixed(4)}, ${bbox.max.z.toFixed(4)})`);
    console.log(`[PC]     8. max-max-max = (${bbox.max.x.toFixed(4)}, ${bbox.max.y.toFixed(4)}, ${bbox.max.z.toFixed(4)})`);
    
    // 🔍 ANALIZA DZIECI (meshes)
    console.log(`[PC]   🔍 ANALIZA DZIECI elementu:`);
    let meshCount = 0;
    let totalVertices = 0;
    el.traverse((child) => {
      if ((child as any).isMesh) {
        meshCount++;
        const mesh = child as THREE.Mesh;
        const geom = mesh.geometry;
        const vertCount = geom.attributes.position?.count || 0;
        totalVertices += vertCount;
        
        // Bbox tego konkretnego mesha
        const meshBbox = new THREE.Box3().setFromObject(mesh);
        const meshWidth = meshBbox.max.x - meshBbox.min.x;
        
        console.log(`[PC]     Mesh #${meshCount}: name="${mesh.name}", vertices=${vertCount}, bbox.width=${meshWidth.toFixed(4)}m`);
        console.log(`[PC]       bbox: X=[${meshBbox.min.x.toFixed(3)}, ${meshBbox.max.x.toFixed(3)}], Z=[${meshBbox.min.z.toFixed(3)}, ${meshBbox.max.z.toFixed(3)}]`);
        console.log(`[PC]       scale: (${mesh.scale.x.toFixed(3)}, ${mesh.scale.y.toFixed(3)}, ${mesh.scale.z.toFixed(3)})`);
        console.log(`[PC]       position (local): (${mesh.position.x.toFixed(3)}, ${mesh.position.y.toFixed(3)}, ${mesh.position.z.toFixed(3)})`);
      }
    });
    console.log(`[PC]   Łącznie: ${meshCount} meshes, ${totalVertices} vertices`);

    console.log(`[PC]   Bounding Box (world space):`);
    console.log(`[PC]     bbox.min = (${bbox.min.x.toFixed(4)}, ${bbox.min.y.toFixed(4)}, ${bbox.min.z.toFixed(4)})`);
    console.log(`[PC]     bbox.max = (${bbox.max.x.toFixed(4)}, ${bbox.max.y.toFixed(4)}, ${bbox.max.z.toFixed(4)})`);
    console.log(`[PC]     actualWidth = ${actualWidth.toFixed(4)}m`);
    console.log(`[PC]     halfWidth = ${halfWidth.toFixed(4)}m`);
    console.log(`[PC]   Element transform:`);
    console.log(`[PC]     el.position = (${el.position.x.toFixed(3)}, ${el.position.y.toFixed(3)}, ${el.position.z.toFixed(3)})`);
    console.log(`[PC]     el.rotation.y = ${(el.rotation.y * 180 / Math.PI).toFixed(2)}° (${el.rotation.y.toFixed(4)} rad)`);
    console.log(`[PC]     el.scale.x = ${el.scale.x.toFixed(3)}`);
    console.log(`[PC]     bboxCenter = (${bboxCenter.x.toFixed(3)}, ${bboxCenter.y.toFixed(3)}, ${bboxCenter.z.toFixed(3)})`);

    const directionVector = new THREE.Vector3(1, 0, 0).applyAxisAngle(
      new THREE.Vector3(0, 1, 0), 
      el.rotation.y
    );

    console.log(`[PC]   Kierunek (direction vector):`);
    console.log(`[PC]     directionVector = (${directionVector.x.toFixed(4)}, ${directionVector.y.toFixed(4)}, ${directionVector.z.toFixed(4)})`);
    console.log(`[PC]     length = ${directionVector.length().toFixed(4)} (powinno być 1.0)`);

    // 🎯 NOWE PODEJŚCIE: Znajdź rzeczywiste skrajne punkty meshes
    console.log(`[PC]   🎯 SZUKAM RZECZYWISTYCH SKRAJNYCH PUNKTÓW GEOMETRII:`);
    
    // 🎯 Dla Royal Mix S50/P50: ignoruj metal (group_30) przy obliczaniu szerokości
    // Metal ma pionowe elementy, które powodują błędne obliczenia realWidth
    // 🎯 Dla Muro Mix P22/P38/S22/S38: ignoruj metal (group_0) przy obliczaniu szerokości
    // Metal może być przesunięty i wystawać poza bloczki, powodując błędne obliczenia realWidth
    const fenceConfigId = fenceConfigParam?.id || '';
    const isRoyalS50 = fenceConfigId === 'royal_s50' || (fenceConfigId === 'royal-mix' && selectedVariant === 's50');
    const isRoyalP50 = fenceConfigId === 'royal_p50' || (fenceConfigId === 'royal-mix' && selectedVariant === 'p50');
    const isRoyalMix = isRoyalS50 || isRoyalP50;
    
    const isMuroMix = fenceConfigId?.startsWith('lupane-muro');
    // 🎯 S38 używa dokładnie tej samej logiki co P22 - sprawdź zarówno bezpośrednie ID jak i selectedVariant
    const isMuroP22 = fenceConfigId === 'lupane-muro-p22' || (isMuroMix && selectedVariant === 'p22') || fenceConfigId === 'lupane-muro-s38' || (isMuroMix && selectedVariant === 's38');
    const isMuroP38 = fenceConfigId === 'lupane-muro-p38' || (isMuroMix && selectedVariant === 'p38');
    const isMuroS22 = fenceConfigId === 'lupane-muro-s22' || (isMuroMix && selectedVariant === 's22');
    const isMuroS38 = isMuroP22; // 🎯 S38 używa dokładnie tej samej logiki co P22
    const isMuroAny = isMuroP22 || isMuroP38 || isMuroS22 || isMuroS38;
    
    let leftmostProjection = Infinity;
    let rightmostProjection = -Infinity;
    let leftmostPoint = new THREE.Vector3();
    let rightmostPoint = new THREE.Vector3();
    
    // Punkt odniesienia (środek bbox)
    const referencePoint = bboxCenter.clone();
    
    el.traverse((child) => {
      if ((child as any).isMesh) {
        const mesh = child as THREE.Mesh;
        const meshBbox = new THREE.Box3().setFromObject(mesh);
        
        // 🎯 Dla Royal Mix: pomiń metal (group_30) i meshes z dużym zakresem Z (pionowe pręty)
        // Oś Z nie jest istotna przy obliczaniu szerokości sekcji w płaszczyźnie XZ
        // 🎯 Dla Muro Mix: pomiń metal (group_0) - może być przesunięty i wystawać poza bloczki
        if (isRoyalMix || isMuroAny) {
          // Sprawdź zakres w osi Z - jeśli jest bardzo duży (>1.5m), to prawdopodobnie pionowe pręty metalu
          const meshZRange = Math.abs(meshBbox.max.z - meshBbox.min.z);
          
          // Znajdź grupę rodzica (jeśli mesh jest w grupie)
          let parentGroup = child.parent;
          let isInMetalGroup = false;
          let metalGroupName = '';
          while (parentGroup && parentGroup !== el) {
            const groupName = parentGroup.name || '';
            // Dla Royal Mix: pomiń metal (group_30)
            // Dla Muro Mix: pomiń metal (group_0)
            if (isRoyalMix && groupName === 'group_30') {
              isInMetalGroup = true;
              metalGroupName = 'group_30';
              break;
            } else if (isMuroAny && groupName === 'group_0') {
              isInMetalGroup = true;
              metalGroupName = 'group_0';
              break;
            }
            parentGroup = parentGroup.parent;
          }
          
          // Pomiń jeśli jest w grupie metalu LUB (dla Royal Mix) ma bardzo duży zakres Z (pionowe pręty)
          if (isInMetalGroup || (isRoyalMix && meshZRange > 1.5)) {
            console.log(`[PC]     ⏭️  Pomijam metal (${isInMetalGroup ? metalGroupName : `Z-range=${meshZRange.toFixed(2)}m`}) - ${isMuroAny ? 'metal może być przesunięty' : 'oś Z nie jest istotna'} przy obliczaniu szerokości`);
            return; // Pomiń ten mesh
          }
        }
        
        // Wszystkie 4 narożniki w płaszczyźnie XZ
        const corners = [
          new THREE.Vector3(meshBbox.min.x, 0, meshBbox.min.z),
          new THREE.Vector3(meshBbox.max.x, 0, meshBbox.min.z),
          new THREE.Vector3(meshBbox.min.x, 0, meshBbox.max.z),
          new THREE.Vector3(meshBbox.max.x, 0, meshBbox.max.z),
        ];
        
        corners.forEach((corner) => {
          // Rzutuj corner na linię przez referencePoint w kierunku directionVector
          const toCorner = new THREE.Vector3().subVectors(corner, referencePoint);
          const projection = toCorner.dot(directionVector);
          
          if (projection < leftmostProjection) {
            leftmostProjection = projection;
            leftmostPoint = corner.clone();
          }
          if (projection > rightmostProjection) {
            rightmostProjection = projection;
            rightmostPoint = corner.clone();
          }
        });
      }
    });
    
    // Oblicz rzeczywiste krawędzie z projekcji
    let left = new THREE.Vector3()
      .copy(referencePoint)
      .addScaledVector(directionVector, leftmostProjection);
    
    let right = new THREE.Vector3()
      .copy(referencePoint)
      .addScaledVector(directionVector, rightmostProjection);
    
    // 🎯 DLA S38: SPRAWDŹ CZY KRAWĘDZIE SĄ ODWRÓCONE
    // Jeśli sekcja jest obrócona o ~180 stopni, to leftmostProjection i rightmostProjection mogą być odwrócone
    const isMuroS38_Edges = fenceConfigId === 'lupane-muro-s38' || (isMuroMix && selectedVariant === 's38');
    if (isMuroS38_Edges) {
      console.log(`[PC]     🔍 S38: Sprawdzam orientację krawędzi:`);
      console.log(`[PC]       leftmostProjection = ${leftmostProjection.toFixed(4)}m`);
      console.log(`[PC]       rightmostProjection = ${rightmostProjection.toFixed(4)}m`);
      console.log(`[PC]       left = (${left.x.toFixed(3)}, ${left.z.toFixed(3)})`);
      console.log(`[PC]       right = (${right.x.toFixed(3)}, ${right.z.toFixed(3)})`);
      console.log(`[PC]       el.rotation.y = ${(el.rotation.y * 180 / Math.PI).toFixed(2)}°`);
      
      // 🎯 Sprawdź czy leftmostProjection > rightmostProjection (co oznaczałoby odwrócenie)
      // Normalnie leftmostProjection powinno być < rightmostProjection
      // Ale też sprawdź, czy krawędzie są bliżej odpowiednich targetów
      // Jeśli nie mamy dostępu do targetów tutaj, sprawdźmy tylko projekcje
      const shouldSwap = leftmostProjection > rightmostProjection;
      
      if (shouldSwap) {
        console.log(`[PC]       ⚠️ WYKRYTO ODWRÓCENIE! leftmostProjection (${leftmostProjection.toFixed(4)}) > rightmostProjection (${rightmostProjection.toFixed(4)})`);
        console.log(`[PC]       🔄 ZAMIENIAM KRAWĘDZIE W getWorldEdges`);
        // Zamień krawędzie
        const tempLeft = left.clone();
        left = right.clone();
        right = tempLeft;
        console.log(`[PC]       ✅ Po zamianie: left = (${left.x.toFixed(3)}, ${left.z.toFixed(3)}), right = (${right.x.toFixed(3)}, ${right.z.toFixed(3)})`);
      } else {
        console.log(`[PC]       ✅ KRAWĘDZIE SĄ POPRAWNIE ORIENTOWANE (leftmostProjection < rightmostProjection)`);
      }
    }
    
    // Ustaw Y na poziom geometrii (środek wysokości)
    left.y = bboxCenter.y;
    right.y = bboxCenter.y;
    
    console.log(`[PC]     leftmostProjection = ${leftmostProjection.toFixed(4)}m`);
    console.log(`[PC]     rightmostProjection = ${rightmostProjection.toFixed(4)}m`);
    console.log(`[PC]     leftmostPoint (mesh corner) = (${leftmostPoint.x.toFixed(3)}, ${leftmostPoint.z.toFixed(3)})`);
    console.log(`[PC]     rightmostPoint (mesh corner) = (${rightmostPoint.x.toFixed(3)}, ${rightmostPoint.z.toFixed(3)})`);

    console.log(`[PC]   Obliczone krawędzie (z rzeczywistych skrajnych punktów):`);
    console.log(`[PC]     left  = referencePoint + directionVector × leftmostProjection`);
    console.log(`[PC]     left  = (${left.x.toFixed(3)}, ${left.y.toFixed(3)}, ${left.z.toFixed(3)})`);
    console.log(`[PC]     right = referencePoint + directionVector × rightmostProjection`);
    console.log(`[PC]     right = (${right.x.toFixed(3)}, ${right.y.toFixed(3)}, ${right.z.toFixed(3)})`);
    console.log(`[PC]   Weryfikacja:`);
    const distanceBetweenEdges = left.distanceTo(right);
    const realWidth = rightmostProjection - leftmostProjection;
    console.log(`[PC]     dystans między krawędziami = ${distanceBetweenEdges.toFixed(4)}m`);
    console.log(`[PC]     realWidth (z projekcji) = ${realWidth.toFixed(4)}m`);
    console.log(`[PC]     bbox actualWidth = ${actualWidth.toFixed(4)}m`);
    console.log(`[PC]     różnica (realWidth - actualWidth) = ${(realWidth - actualWidth).toFixed(4)}m`);
    
    // 🔍 SPRAWDŹ CZY METAL LUB BLOCZKI WYSTAJĄ POZA BBOX
    console.log(`[PC]   🔍 SPRAWDZAM CZY COKOLWIEK WYSTAJE POZA OBLICZONE KRAWĘDZIE:`);
    let anythingOutsideLeft = false;
    let anythingOutsideRight = false;
    let maxLeftOverhang = 0;
    let maxRightOverhang = 0;
    
    el.traverse((child) => {
      if ((child as any).isMesh) {
        const mesh = child as THREE.Mesh;
        const meshBbox = new THREE.Box3().setFromObject(mesh);
        
        // 🎯 Dla Royal Mix: pomiń metal (group_30) i meshes z dużym zakresem Z także przy sprawdzaniu wystąpień
        // Oś Z nie jest istotna - pomijamy pionowe pręty metalu
        // 🎯 Dla Muro Mix: pomiń metal (group_0) także przy sprawdzaniu wystąpień
        if (isRoyalMix || isMuroAny) {
          // Sprawdź zakres w osi Z - jeśli jest bardzo duży (>1.5m), to prawdopodobnie pionowe pręty metalu
          const meshZRange = Math.abs(meshBbox.max.z - meshBbox.min.z);
          
          // Znajdź grupę rodzica (jeśli mesh jest w grupie)
          let parentGroup = child.parent;
          let isInMetalGroup = false;
          while (parentGroup && parentGroup !== el) {
            const groupName = parentGroup.name || '';
            // Dla Royal Mix: pomiń metal (group_30)
            // Dla Muro Mix: pomiń metal (group_0)
            if ((isRoyalMix && groupName === 'group_30') || (isMuroAny && groupName === 'group_0')) {
              isInMetalGroup = true;
              break;
            }
            parentGroup = parentGroup.parent;
          }
          
          // Pomiń jeśli jest w grupie metalu LUB (dla Royal Mix) ma bardzo duży zakres Z (pionowe pręty)
          if (isInMetalGroup || (isRoyalMix && meshZRange > 1.5)) {
            return; // Pomiń ten mesh
          }
        }
        
        // Sprawdź narożniki w płaszczyźnie XZ względem linii left-right
        const corners = [
          new THREE.Vector3(meshBbox.min.x, 0, meshBbox.min.z),
          new THREE.Vector3(meshBbox.max.x, 0, meshBbox.min.z),
          new THREE.Vector3(meshBbox.min.x, 0, meshBbox.max.z),
          new THREE.Vector3(meshBbox.max.x, 0, meshBbox.max.z),
        ];
        
        const left2D = new THREE.Vector3(left.x, 0, left.z);
        const right2D = new THREE.Vector3(right.x, 0, right.z);
        const direction = new THREE.Vector3().subVectors(right2D, left2D).normalize();
        
        corners.forEach((corner, idx) => {
          // Sprawdź, czy corner jest na lewo od left lub na prawo od right
          const toCorner = new THREE.Vector3().subVectors(corner, left2D);
          const projection = toCorner.dot(direction);
          const lineLength = left2D.distanceTo(right2D);
          
          if (projection < 0) {
            const dist = Math.abs(projection);
            if (dist > 0.001) { // powyżej 1mm
              anythingOutsideLeft = true;
              maxLeftOverhang = Math.max(maxLeftOverhang, dist);
              console.log(`[PC]     ⚠️  ${mesh.name} narożnik #${idx} wystaje NA LEWO o ${(dist * 1000).toFixed(1)}mm`);
              console.log(`[PC]        corner=(${corner.x.toFixed(3)}, ${corner.z.toFixed(3)}), left=(${left2D.x.toFixed(3)}, ${left2D.z.toFixed(3)})`);
            }
          } else if (projection > lineLength) {
            const dist = projection - lineLength;
            if (dist > 0.001) { // powyżej 1mm
              anythingOutsideRight = true;
              maxRightOverhang = Math.max(maxRightOverhang, dist);
              console.log(`[PC]     ⚠️  ${mesh.name} narożnik #${idx} wystaje NA PRAWO o ${(dist * 1000).toFixed(1)}mm`);
              console.log(`[PC]        corner=(${corner.x.toFixed(3)}, ${corner.z.toFixed(3)}), right=(${right2D.x.toFixed(3)}, ${right2D.z.toFixed(3)})`);
            }
          }
        });
      }
    });
    
    if (!anythingOutsideLeft && !anythingOutsideRight) {
      console.log(`[PC]     ✅ Wszystkie meshes mieszczą się między obliczonymi krawędziami`);
    } else {
      console.log(`[PC]   📊 MAKSYMALNE WYSTĄPIENIA:`);
      console.log(`[PC]     maxLeftOverhang = ${(maxLeftOverhang * 1000).toFixed(1)}mm`);
      console.log(`[PC]     maxRightOverhang = ${(maxRightOverhang * 1000).toFixed(1)}mm`);
      console.log(`[PC]     Łączne wystąpienie = ${((maxLeftOverhang + maxRightOverhang) * 1000).toFixed(1)}mm`);
    }

    return { left, right, maxLeftOverhang, maxRightOverhang };
  }

  private getBaseWidth(elements: THREE.Group[], fenceConfig: any): number {
    const existingSection = elements.find(el => el.userData?.elementType === 'section');
    
    // 🎯 Najpierw sprawdź baseWidth (bazowa szerokość 1x)
    if (existingSection?.userData?.baseWidth) {
      return existingSection.userData.baseWidth;
    }
    
    // 🎯 Jeśli nie ma baseWidth, ale jest width i widthMultiplier, oblicz baseWidth = width / widthMultiplier
    if (existingSection?.userData?.width && existingSection?.userData?.widthMultiplier) {
      const width = existingSection.userData.width;
      const widthMultiplier = existingSection.userData.widthMultiplier;
      if (widthMultiplier > 1) {
        const calculatedBaseWidth = width / widthMultiplier;
        console.log(`[PC] 📐 Obliczam baseWidth z width=${width.toFixed(4)}m / widthMultiplier=${widthMultiplier} = ${calculatedBaseWidth.toFixed(4)}m`);
        return calculatedBaseWidth;
      }
      // Jeśli widthMultiplier = 1, width jest już bazową szerokością
      return width;
    }
    
    // 🎯 Fallback: użyj width jeśli jest dostępne (dla widthMultiplier = 1)
    if (existingSection?.userData?.width) {
      return existingSection.userData.width;
    }

    // 🎯 Ostatni fallback: oblicz z konfiguracji
    const sectionConfig = fenceConfig?.elements?.section;
    
    // 🎯 NAJWAŻNIEJSZE: rightOffset[1] to rzeczywista szerokość sekcji 1x (od lewej do prawej krawędzi)
    if (sectionConfig?.rightOffset?.[1]) {
      console.log(`[PC] 📐 ✅ Używam rightOffset[1] z konfiguracji: ${sectionConfig.rightOffset[1].toFixed(4)}m`);
      return sectionConfig.rightOffset[1];
    }
    
    // 🎯 Fallback 1: defaultWidth (dla starszych konfiguracji bez rightOffset)
    if (sectionConfig?.defaultWidth) {
      console.log(`[PC] 📐 ⚠️ Używam defaultWidth z konfiguracji: ${sectionConfig.defaultWidth.toFixed(4)}m (brak rightOffset)`);
      return sectionConfig.defaultWidth;
    }
    
    // 🎯 Fallback 2: spacing (dla bardzo starych konfiguracji)
    if (sectionConfig?.spacing) {
      const baseWidth = sectionConfig.spacing * 2;
      console.log(`[PC] 📐 ⚠️ Obliczam baseWidth z spacing: ${sectionConfig.spacing.toFixed(4)}m × 2 = ${baseWidth.toFixed(4)}m (brak rightOffset)`);
      return baseWidth;
    }

    console.warn('[PC] ⚠️ Używam domyślnej baseWidth 0.396m');
    return 0.396;
  }

  private determineVariant(fenceConfig: any, selectedVariant?: string): string | undefined {
    console.log(`[PC] 🔍 determineVariant() - START:`);
    console.log(`[PC]   fenceConfig?.id = "${fenceConfig?.id}"`);
    console.log(`[PC]   selectedVariant = "${selectedVariant}"`);
    
    if (fenceConfig?.id === 'royal-mix') {
      const result = selectedVariant || 's50';
      console.log(`[PC]   ✅ royal-mix → zwracam "${result}"`);
      return result;
    }
    
    if (fenceConfig?.id === 'lupane-muro-mix') {
      const result = selectedVariant || 'p22';
      console.log(`[PC]   ✅ lupane-muro-mix → zwracam "${result}"`);
      return result;
    }

    // 🎯 Dla bezpośrednich ID wariantów (np. lupane-muro-s38, lupane-muro-p22) - wyciągnij wariant z ID lub użyj selectedVariant
    if (fenceConfig?.id?.startsWith('lupane-muro-')) {
      const idMatch = fenceConfig.id.match(/lupane-muro-(p22|p38|s22|s38)/);
      if (idMatch) {
        const result = idMatch[1]; // Zwróć wariant z ID (np. 's38' z 'lupane-muro-s38')
        console.log(`[PC]   ✅ bezpośrednie ID "${fenceConfig.id}" → wyciągnięto wariant "${result}" z ID`);
        return result;
      }
      // Jeśli to mix, użyj selectedVariant
      if (fenceConfig?.id === 'lupane-muro-mix' && selectedVariant) {
        console.log(`[PC]   ✅ lupane-muro-mix z selectedVariant → zwracam "${selectedVariant}"`);
        return selectedVariant;
      }
    }

    // 🎯 Dla bezpośrednich ID Royal (np. royal_s50, royal_p50) - wyciągnij wariant z ID
    if (fenceConfig?.id?.startsWith('royal_')) {
      const idMatch = fenceConfig.id.match(/royal_(s50|p50)/);
      if (idMatch) {
        const result = idMatch[1]; // Zwróć wariant z ID (np. 's50' z 'royal_s50')
        console.log(`[PC]   ✅ bezpośrednie ID Royal "${fenceConfig.id}" → wyciągnięto wariant "${result}" z ID`);
        return result;
      }
    }

    console.log(`[PC]   ⚠️ determineVariant() → zwracam undefined (nie rozpoznano wariantu)`);
    return undefined;
  }

  private applyRoyalMetalCorrection(
    el: THREE.Group, 
    fenceConfig: any, 
    variant?: string
  ): void {
    const isRoyalS50 = fenceConfig?.id === 'royal_s50' || 
                       (fenceConfig?.id === 'royal-mix' && variant === 's50');
    const isRoyalP50 = fenceConfig?.id === 'royal_p50' || 
                       (fenceConfig?.id === 'royal-mix' && variant === 'p50');
    
    // 🎯 FIX: Muro może mieć różne ID: lupane-muro-mix, lupane-muro-p22, lupane-muro-p38, etc.
    const isMuroMix = fenceConfig?.id?.startsWith('lupane-muro');
    // 🎯 S38 używa dokładnie tej samej logiki i TYCH SAMYCH grup co P22 (group_13, group_42)
    const isMuroS38 = (fenceConfig?.id === 'lupane-muro-s38') || (isMuroMix && variant === 's38');
    const isMuroP22 = (fenceConfig?.id === 'lupane-muro-p22') || (isMuroMix && variant === 'p22');
    const isMuroS22 = (fenceConfig?.id === 'lupane-muro-s22') || (isMuroMix && variant === 's22');
    const isMuroP38 = (fenceConfig?.id === 'lupane-muro-p38') || (isMuroMix && variant === 'p38');
    // 🎯 S38 i P22 używają tej samej logiki i TYCH SAMYCH grup
    const isMuroP22OrS38 = isMuroP22 || isMuroS38;

    if (!isRoyalS50 && !isRoyalP50 && !isMuroMix) return;

    const isRoyal = isRoyalS50 || isRoyalP50;
    const isMuro = isMuroMix;

    console.log(`[PC] 🔧 applyRoyalMetalCorrection - WARUNKI:`);
    console.log(`[PC]    fenceConfig.id = "${fenceConfig?.id}"`);
    console.log(`[PC]    variant = "${variant}"`);
    console.log(`[PC]    isMuroMix = ${isMuroMix}`);
    console.log(`[PC]    isMuroP22 = ${isMuroP22}`);
    console.log(`[PC]    isMuroS22 = ${isMuroS22}`);
    console.log(`[PC]    isMuroP38 = ${isMuroP38}`);
    console.log(`[PC]    isMuroS38 = ${isMuroS38}`);
    console.log(`[PC]    isRoyalS50 = ${isRoyalS50}`);
    console.log(`[PC]    isRoyalP50 = ${isRoyalP50}`);
    console.log(`[PC] 🔧 ${isRoyal ? `Royal ${isRoyalS50 ? 'S50' : 'P50'}` : `Muro Mix ${variant}`}: Korygowanie metalu...`);

    const blocks: THREE.Object3D[] = [];
    const metals: THREE.Object3D[] = [];
    
    // 🎯 DLA S38: Zbierz wszystkie grupy przed przetwarzaniem, żeby zobaczyć co jest dostępne
    if (isMuroS38) {
      console.log(`[PC] 🔍 S38: Zbieram wszystkie grupy w modelu przed przetwarzaniem...`);
      const allGroupsInModel: string[] = [];
      el.traverse((child) => {
        const childName = child.name || '';
        if (childName.startsWith('group_')) {
          allGroupsInModel.push(childName);
        }
      });
      const uniqueGroups = [...new Set(allGroupsInModel)].sort();
      console.log(`[PC] 🔍 S38: Znalezione grupy w modelu (${uniqueGroups.length} unikalnych):`, uniqueGroups);
      console.log(`[PC] 🔍 S38: Szukam grup S38: group_21, group_63, group_0 (model fizycznie ma te grupy)`);
      console.log(`[PC] 🔍 S38: group_21 obecna? ${uniqueGroups.includes('group_21')}`);
      console.log(`[PC] 🔍 S38: group_63 obecna? ${uniqueGroups.includes('group_63')}`);
      console.log(`[PC] 🔍 S38: group_0 obecna? ${uniqueGroups.includes('group_0')}`);
    }

    el.traverse((child) => {
      const name = child.name || '';
      
      if (isRoyal && name === 'group_30') {
        metals.push(child);
        console.log(`[PC] 🔧 applyRoyalMetalCorrection: Dodano metal (Royal): ${name}`);
      }
      else if (isMuro && name === 'group_0') {
        metals.push(child);
        console.log(`[PC] 🔧 applyRoyalMetalCorrection: Dodano metal (Muro): ${name}`);
      }
      else if (isRoyalS50 && (name === 'group_15' || name === 'group_21')) {
        blocks.push(child);
        console.log(`[PC] 🔧 applyRoyalMetalCorrection: Dodano blok (Royal S50): ${name}`);
      }
      else if (isRoyalP50 && (name === 'group_9' || name === 'group_7')) {
        blocks.push(child);
        console.log(`[PC] 🔧 applyRoyalMetalCorrection: Dodano blok (Royal P50): ${name}`);
      }
      else if (isMuroP22 && (name === 'group_13' || name === 'group_42')) {
        blocks.push(child);
        console.log(`[PC] 🔧 applyRoyalMetalCorrection: Dodano blok (P22): ${name}, isMuroP22=${isMuroP22}`);
      }
      else if (isMuroS38 && (name === 'group_21' || name === 'group_63')) {
        // 🎯 S38 używa swoich grup (group_21, group_63) - model fizycznie ma te grupy, nie P22
        blocks.push(child);
        console.log(`[PC] 🔧 applyRoyalMetalCorrection: Dodano blok (S38): ${name}, isMuroS38=${isMuroS38}`);
      }
      else if (isMuroS22 && (name === 'group_44' || name === 'group_77')) {
        blocks.push(child);
        console.log(`[PC] 🔧 applyRoyalMetalCorrection: Dodano blok (S22): ${name}`);
      }
      else if (isMuroP38 && (name === 'group_21' || name === 'group_57')) {
        blocks.push(child);
        console.log(`[PC] 🔧 applyRoyalMetalCorrection: Dodano blok (P38): ${name}`);
      }
      else {
        // Loguj tylko jeśli to może być grupa bloków
        if (name.startsWith('group_') && (name === 'group_13' || name === 'group_42' || name === 'group_21' || name === 'group_63' || name === 'group_44' || name === 'group_77' || name === 'group_57')) {
          console.log(`[PC] 🔧 applyRoyalMetalCorrection: POMINIĘTO grupę ${name} - isMuroP22=${isMuroP22}, isMuroS22=${isMuroS22}, isMuroP38=${isMuroP38}, isMuroS38=${isMuroS38}`);
        }
      }
      // 🎯 S38 używa tej samej logiki co P22, ale ma swoje grupy (group_21, group_63)
    });

    if (blocks.length === 0 || metals.length === 0) {
      console.log(`[PC] ⚠️ applyRoyalMetalCorrection: PRZERYWAM - blocks.length=${blocks.length}, metals.length=${metals.length}`);
      if (isMuroP38 || isMuroS38 || isMuroP22) {
        console.log(`[PC] ⚠️ ${isMuroS38 ? 'S38' : isMuroP22 ? 'P22' : 'P38'}: Nie znaleziono bloków lub metalu! Sprawdzam strukturę...`);
        const allGroups: string[] = [];
        el.traverse((child) => {
          const name = child.name || '';
          if (name.startsWith('group_')) {
            allGroups.push(name);
          }
        });
        console.log(`[PC]   Wszystkie grupy w modelu:`, [...new Set(allGroups)].sort());
        if (isMuroS38) {
          console.log(`[PC]   S38 szuka: group_21, group_63, group_0`);
        } else if (isMuroP22) {
          console.log(`[PC]   P22 szuka: group_13, group_42, group_0`);
        }
      }
      return;
    }

    // Oblicz bounding box wszystkich bloków w LOKALNYM układzie współrzędnych elementu
    // ❗ Używamy boundingBox z geometry + local scale/position (bez world transformów), żeby nie mieszać różnych układów.
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;

    console.log(`[PC] 🔧 BLOCZKI (${blocks.length} sztuk):`);
    if (isMuroP38) {
      console.log(`[PC] 🔧 P38: Znaleziono ${blocks.length} bloków i ${metals.length} elementów metalu`);
      blocks.forEach((b, i) => console.log(`[PC]   Bloczek #${i+1}: name="${b.name}"`));
    }
    blocks.forEach((block, blockIndex) => {
      // Pobierz bounding box GEOMETRII w lokalnym układzie współrzędnych mesha
      const mesh = block as THREE.Mesh;
      const geom = mesh.geometry as THREE.BufferGeometry | undefined;
      if (!geom) return;
      if (!geom.boundingBox) {
        geom.computeBoundingBox();
      }
      const gbb = geom.boundingBox as THREE.Box3;

      // Szerokość w lokalnym X po uwzględnieniu skali mesha
      const blockWidth = (gbb.max.x - gbb.min.x) * mesh.scale.x;
      // Grubość (głębokość) w lokalnym Z po uwzględnieniu skali mesha
      const blockDepth = (gbb.max.z - gbb.min.z) * mesh.scale.z;

      // Środek bloku w lokalnym X/Z (po skalowaniu), przesunięty o position mesha
      const geomCenterLocalX = (gbb.min.x + gbb.max.x) * 0.5 * mesh.scale.x;
      const geomCenterLocalZ = (gbb.min.z + gbb.max.z) * 0.5 * mesh.scale.z;
      const blockLocalCenterX = mesh.position.x + geomCenterLocalX;
      const blockLocalCenterZ = mesh.position.z + geomCenterLocalZ;
      
      console.log(`[PC]   Bloczek #${blockIndex + 1}:`);
      console.log(`[PC]     name="${block.name}"`);
      console.log(`[PC]     widthLocal(X)=${blockWidth.toFixed(4)}m`);
      console.log(`[PC]     centerLocalX=${blockLocalCenterX.toFixed(4)}m`);
      console.log(`[PC]     scale.x=${mesh.scale.x.toFixed(4)}`);
      console.log(
        `[PC]     zakresLocal X: [${(blockLocalCenterX - blockWidth / 2).toFixed(4)}, ${(blockLocalCenterX + blockWidth / 2).toFixed(4)}]`
      );
      console.log(
        `[PC]     centerLocalZ=${blockLocalCenterZ.toFixed(4)}m, depthLocal(Z)=${blockDepth.toFixed(
          4
        )}m`
      );

      minX = Math.min(minX, blockLocalCenterX - blockWidth / 2);
      maxX = Math.max(maxX, blockLocalCenterX + blockWidth / 2);
      minZ = Math.min(minZ, blockLocalCenterZ - blockDepth / 2);
      maxZ = Math.max(maxZ, blockLocalCenterZ + blockDepth / 2);
    });

    if (minX === Infinity || maxX === -Infinity) return;

    // Oblicz środek i szerokość / głębokość obszaru bloków (LOCAL X/Z)
    let blocksCenterX = (minX + maxX) / 2;
    let blocksWidth = maxX - minX;
    const blocksCenterZ = (minZ + maxZ) / 2;
    const blocksDepth = maxZ - minZ;

    console.log(
      `[PC] 🔧 Bloczki LOCAL: X[min=${minX.toFixed(3)}, max=${maxX.toFixed(
        3
      )}, width=${blocksWidth.toFixed(3)}, center=${blocksCenterX.toFixed(
        3
      )}], Z[min=${minZ.toFixed(3)}, max=${maxZ.toFixed(3)}, depth=${blocksDepth.toFixed(
        3
      )}, center=${blocksCenterZ.toFixed(3)}]`
    );

    // Dla S22: obniż bloczki dekoracyjne (group_77) - powinny być niżej jak w normalnych sekcjach
    // 🎯 W sekcji zamykającej: obniż bardziej
    if (isMuroS22) {
      const isClosingSection = el.userData?.isClosingSection;
      console.log(`[PC] 🔧 S22: isClosingSection=${isClosingSection}, blocks.length=${blocks.length}`);
      const decorationBlocks = blocks.filter(block => block.name === 'group_77');
      console.log(`[PC] 🔧 S22: Znaleziono ${decorationBlocks.length} bloków dekoracyjnych (group_77)`);
      
      decorationBlocks.forEach((decBlock, index) => {
        const originalY = decBlock.position.y;
        if (isClosingSection) {
          // W sekcji zamykającej: obniż o 0.13m
          decBlock.position.y -= 0.13; // Obniżenie o 0.13m dla sekcji zamykającej
          console.log(`[PC] 🔧 Bloczek dekoracyjny #${index + 1} (S22, sekcja zamykająca): Y=${originalY.toFixed(3)}m → ${decBlock.position.y.toFixed(3)}m (obniżony o 0.13m)`);
        } else {
          // W normalnych sekcjach: standardowe obniżenie
          decBlock.position.y -= 0.125; // Obniżenie o decorationSpacing
          console.log(`[PC] 🔧 Bloczek dekoracyjny #${index + 1} (S22): Y=${originalY.toFixed(3)}m → ${decBlock.position.y.toFixed(3)}m (obniżony o 0.125m)`);
        }
      });
      
      if (decorationBlocks.length === 0) {
        console.log(`[PC] ⚠️ S22: Nie znaleziono bloków dekoracyjnych! Wszystkie bloki:`);
        blocks.forEach((b, i) => console.log(`[PC]   Bloczek #${i + 1}: name="${b.name}"`));
      }
    }

    // Dla S38: obniż bloczki dekoracyjne (group_63) - powinny być niżej jak w normalnych sekcjach
    console.log(`[PC] 🔧 SPRAWDZAM S38 LOWERING: isMuroS38=${isMuroS38}, variant=${variant}, fenceConfig?.id=${fenceConfig?.id}`);
    if (isMuroS38) {
      const isClosingSection = el.userData?.isClosingSection;
      console.log(`[PC] 🔧 S38 LOWERING: isClosingSection=${isClosingSection}, blocks.length=${blocks.length}`);
      console.log(`[PC] 🔧 S38 LOWERING: Wszystkie bloki w tablicy:`, blocks.map(b => b.name));
      const decorationBlocks = blocks.filter(block => block.name === 'group_63');
      console.log(`[PC] 🔧 S38 LOWERING: Znaleziono ${decorationBlocks.length} bloków dekoracyjnych (group_63)`);
      
      decorationBlocks.forEach((decBlock, index) => {
        const originalY = decBlock.position.y;
        console.log(`[PC] 🔧 S38 LOWERING: Przetwarzam blok #${index + 1}, isClosingSection=${isClosingSection}`);
        if (isClosingSection) {
          // W sekcji zamykającej: obniż o 0.05m
          decBlock.position.y -= 0.05;
          console.log(`[PC] 🔧 Bloczek dekoracyjny #${index + 1} (S38, sekcja zamykająca): Y=${originalY.toFixed(3)}m → ${decBlock.position.y.toFixed(3)}m (obniżony o 0.05m)`);
        } else {
          console.log(`[PC] 🔧 Bloczek dekoracyjny #${index + 1} (S38): POMINIĘTY - nie jest sekcja zamykająca`);
        }
      });
      
      if (decorationBlocks.length === 0) {
        console.log(`[PC] ⚠️ S38 LOWERING: Nie znaleziono bloków dekoracyjnych group_63! Sprawdź nazwy bloków w tablicy.`);
      }
    } else {
      console.log(`[PC] 🔧 S38 LOWERING: POMINIĘTY - isMuroS38=${isMuroS38}`);
    }

    // Dla każdego metalu: ustaw szerokość i pozycję
    metals.forEach((metal, metalIndex) => {
      // Pobierz bounding box GEOMETRII metalu w lokalnym układzie współrzędnych mesha
      const metalMesh = metal as THREE.Mesh;
      const metalGeom = metalMesh.geometry as THREE.BufferGeometry | undefined;
      if (!metalGeom) return;
      if (!metalGeom.boundingBox) {
        metalGeom.computeBoundingBox();
      }
      const metalGbb = metalGeom.boundingBox as THREE.Box3;

      // Oryginalna szerokość metalu w lokalnym X (po skali)
      const originalMetalWidth = (metalGbb.max.x - metalGbb.min.x) * metalMesh.scale.x;
      const originalScaleX = metalMesh.scale.x;
      
      console.log(`[PC] 🔧 Metal #${metalIndex + 1} PRZED korektą:`);
      console.log(`[PC]    name="${metal.name}"`);
      console.log(`[PC]    originalWidthLocal=${originalMetalWidth.toFixed(4)}m`);
      console.log(`[PC]    scale.x=${originalScaleX.toFixed(4)}`);
      console.log(`[PC]    position.x=${metalMesh.position.x.toFixed(4)}m`);
      
      // Oblicz skalę potrzebną do dopasowania szerokości metalu do szerokości bloków
      let scaleFactor = blocksWidth / originalMetalWidth;
      
      // 🎯 Dla P22, S38 i P38: zmniejsz szerokość metalu o 3%, żeby nie wystawał poza bloczki
      if (isMuroP22OrS38 || isMuroP38) {
        scaleFactor *= 0.97; // Zmniejsz o 3% - żeby metal był lekko węższy niż bloczki
        console.log(`[PC]    ${isMuroP22OrS38 ? (isMuroP22 ? 'P22' : 'S38') : 'P38'}: scaleFactor × 0.97 = ${scaleFactor.toFixed(4)}`);
      }
      
      // 🎯 Dla Royal Mix S50/P50: dopasuj metal do szerokości bloczków (oś X), z większym luzem (inset),
      // żeby metal NIE wystawał, nawet przy kątach bliskich 90°, kosztem minimalnego skrócenia.
      if (isRoyalS50 || isRoyalP50) {
        const inset = 0.020; // 20mm luzu łącznie (po 10mm na stronę) w osi X
        const desiredMetalWidth = Math.max(0, blocksWidth - inset);
        scaleFactor = desiredMetalWidth / originalMetalWidth;
        console.log(
          `[PC]    Royal ${isRoyalS50 ? 'S50' : 'P50'}: dopasowanie metalu do bloczków: ` +
          `desiredMetalWidth=${desiredMetalWidth.toFixed(4)}m (blocksWidth=${blocksWidth.toFixed(4)}m, inset=${(inset * 1000).toFixed(0)}mm), ` +
          `scaleFactor=${scaleFactor.toFixed(4)}, originalMetalWidth=${originalMetalWidth.toFixed(4)}m`
        );
      }
      
      // 🎯 Dla P38 i S38: NIE zmniejszaj szerokości metalu dla sekcji zamykającej (żeby działało tak samo jak P22)
      // (Ta logika była używana tylko dla normalnych sekcji, nie dla sekcji zamykającej)
      if (false && (isMuroP38 || isMuroS38)) {
        scaleFactor *= 0.95; // Zmniejsz o 5% - WYŁĄCZONE dla sekcji zamykającej
      }
      
      // Zastosuj skalowanie w osi X (zachowaj Y i Z)
      metalMesh.scale.x *= scaleFactor;

      // Po skalowaniu przelicz szerokość i środek metalu w LOKALNYM X/Z
      const scaledMetalWidth = (metalGbb.max.x - metalGbb.min.x) * metalMesh.scale.x;
      const scaledMetalCenterLocalX =
        metalMesh.position.x + (metalGbb.min.x + metalGbb.max.x) * 0.5 * metalMesh.scale.x;
      const scaledMetalDepth = (metalGbb.max.z - metalGbb.min.z) * metalMesh.scale.z;
      const scaledMetalCenterLocalZ =
        metalMesh.position.z + (metalGbb.min.z + metalGbb.max.z) * 0.5 * metalMesh.scale.z;

      // Wyśrodkuj metal nad bloczkami w LOKALNYM X/Z (żeby lewa/prawa i przód/tył były symetryczne względem bloczków)
      // 🎯 Dla Muro Mix: NIE wyśrodkowuj - od razu wyrównaj do lewej krawędzi (edge align zrobi to później)
      const centerOffsetLocalX = blocksCenterX - scaledMetalCenterLocalX;
      const centerOffsetLocalZ = blocksCenterZ - scaledMetalCenterLocalZ;
      
      if (!isMuro) {
        // Dla Royal: wyśrodkuj metal nad bloczkami
        metalMesh.position.x += centerOffsetLocalX;
        metalMesh.position.z += centerOffsetLocalZ;
      } else {
        // Dla Muro: NIE wyśrodkowuj - tylko przesuń w Z (głębokość), X zostanie wyrównany przez edge align
        metalMesh.position.z += centerOffsetLocalZ;
        console.log(`[PC] 🔧 Metal (Muro): POMIJAM wyśrodkowanie w X - od razu wyrównam do lewej krawędzi (edge align)`);
      }

      console.log(`[PC] 🔧 Metal #${metalIndex + 1} PO korekcie:`);
      console.log(`[PC]    blocksWidthLocal=${blocksWidth.toFixed(4)}m`);
      console.log(`[PC]    scaleFactor=${scaleFactor.toFixed(4)}`);
      console.log(
        `[PC]    nowy scale.x=${metalMesh.scale.x.toFixed(4)} (poprzedni=${originalScaleX.toFixed(
          4
        )}), centerOffsetLocalX=${centerOffsetLocalX.toFixed(4)}m, centerOffsetLocalZ=${centerOffsetLocalZ.toFixed(4)}m`
      );
      console.log(
        `[PC]    nowy position LOCAL = (${metalMesh.position.x.toFixed(
          4
        )}, ${metalMesh.position.z.toFixed(4)})`
      );

      // Zmierz ponownie po korekcji – tym razem w LOKALNYM X
      const metalWidthAfterLocal = (metalGbb.max.x - metalGbb.min.x) * metalMesh.scale.x;
      const metalDepthAfterLocal = (metalGbb.max.z - metalGbb.min.z) * metalMesh.scale.z;
      const metalCenterAfterLocalX =
        metalMesh.position.x + (metalGbb.min.x + metalGbb.max.x) * 0.5 * metalMesh.scale.x;
      const metalCenterAfterLocalZ =
        metalMesh.position.z + (metalGbb.min.z + metalGbb.max.z) * 0.5 * metalMesh.scale.z;

      const metalLeftLocalX = metalCenterAfterLocalX - metalWidthAfterLocal / 2;
      const metalRightLocalX = metalCenterAfterLocalX + metalWidthAfterLocal / 2;
      const metalFrontLocalZ = metalCenterAfterLocalZ - metalDepthAfterLocal / 2;
      const metalBackLocalZ = metalCenterAfterLocalZ + metalDepthAfterLocal / 2;

      console.log(
        `[PC]    szerokość metalu LOCAL (X)=${metalWidthAfterLocal.toFixed(4)}m (oczekiwana≈${blocksWidth.toFixed(
          4
        )}m)`
      );
      console.log(
        `[PC]    metal LOCAL: X[left=${metalLeftLocalX.toFixed(4)}m, right=${metalRightLocalX.toFixed(
          4
        )}m, center=${metalCenterAfterLocalX.toFixed(
          4
        )}m], Z[front=${metalFrontLocalZ.toFixed(4)}m, back=${metalBackLocalZ.toFixed(
          4
        )}m, center=${metalCenterAfterLocalZ.toFixed(4)}m]`
      );

      // 📏 DODATKOWE LOGI: nad- / niedowis metalu względem bloczków po LEWEJ i PRAWEJ stronie (LOCAL X)
      const blocksLeftLocalX = minX;
      const blocksRightLocalX = maxX;
      const overhangLeftLocalMm = (blocksLeftLocalX - metalLeftLocalX) * 1000; // >0: metal krótszy od bloczków z lewej
      const overhangRightLocalMm = (metalRightLocalX - blocksRightLocalX) * 1000; // >0: metal wystaje za bloczki z prawej

      console.log(
        `[PC]    LEWA STRONA LOCAL (X): bloczki.minX=${blocksLeftLocalX.toFixed(4)}m, metal.minX=${metalLeftLocalX.toFixed(
          4
        )}m, delta=${overhangLeftLocalMm.toFixed(1)}mm ( dodatnia = metal krótszy )`
      );
      console.log(
        `[PC]    PRAWA STRONA LOCAL (X): bloczki.maxX=${blocksRightLocalX.toFixed(
          4
        )}m, metal.maxX=${metalRightLocalX.toFixed(
          4
        )}m, delta=${overhangRightLocalMm.toFixed(1)}mm ( dodatnia = metal WYSTAJE )`
      );

      // 🔧 PRECYZYJNE DOSTROJENIE POŁOŻENIA WZDŁUŻ BLOKÓW (TYLKO oś X, bez kombinowania z Z):
      // Chcemy, żeby metal zaczynał/kończył się dokładnie tam, gdzie bloczki (w LOCAL X).
      // Wyrównaj IDEALNIE do bloczków: dopasuj początek (lewą krawędź) metalu do początku bloczków.
      // Jeśli szerokość jest zgodna (jak teraz w S50), prawa krawędź też automatycznie się zgra.
      const edgeShiftLocalX = (blocksLeftLocalX - metalLeftLocalX);
      if (Math.abs(edgeShiftLocalX) > 1e-5) {
        metalMesh.position.x += edgeShiftLocalX;

        // Przelicz krawędzie metalu po ostatecznym przesunięciu, tylko do logów
        const metalCenterFinalLocalX =
          metalCenterAfterLocalX + edgeShiftLocalX; // center przesuwa się o ten sam offset
        const metalLeftFinalLocalX = metalCenterFinalLocalX - metalWidthAfterLocal / 2;
        const metalRightFinalLocalX = metalCenterFinalLocalX + metalWidthAfterLocal / 2;

        const overhangLeftFinalMm = (blocksLeftLocalX - metalLeftFinalLocalX) * 1000;
        const overhangRightFinalMm = (metalRightFinalLocalX - blocksRightLocalX) * 1000;

        console.log(
          `[PC]    🔧 EDGE ALIGN LOCAL (X): edgeShiftLocalX=${edgeShiftLocalX.toFixed(
            4
          )}m – dokładne dosunięcie metalu do bloczków`
        );
        console.log(
          `[PC]    PO EDGE ALIGN LOCAL (X): metal.left=${metalLeftFinalLocalX.toFixed(
            4
          )}m, metal.right=${metalRightFinalLocalX.toFixed(4)}m`
        );
        console.log(
          `[PC]    OVERHANG FINAL (X): left=${overhangLeftFinalMm.toFixed(
            1
          )}mm (bloczek - metal), right=${overhangRightFinalMm.toFixed(
            1
          )}mm (metal - bloczek) `
        );
      }
      
      // 🎯 Dodatkowe logi porównawcze dla Royal S50/P50 – żeby zobaczyć, kiedy “metal jest prawie dwa razy dłuższy”
      if (isRoyalS50 || isRoyalP50) {
        const ratioX = blocksWidth > 0 ? metalWidthAfterLocal / blocksWidth : 0;
        console.log(
          `[PC]    Royal ${isRoyalS50 ? 'S50' : 'P50'}: PORÓWNANIE METAL vs BLOKI: ` +
          `blocksWidthX=${blocksWidth.toFixed(4)}m, metalWidthX=${metalWidthAfterLocal.toFixed(4)}m, ratioX=${ratioX.toFixed(3)}`
        );

        // 📏 DODATKOWE, „PRAWDZIWE” LOGI W WORLD SPACE – metal vs bloczki (tylko S50/P50)
        try {
          // 🔍 Użyj WSPÓLNEGO bbox wszystkich bloczków, żeby log i korekta były "prawdziwe" dla całej ściany
          const blocksWorldBbox = new THREE.Box3();
          blocks.forEach(b => {
            blocksWorldBbox.expandByObject(b);
          });
          const metalWorldBbox = new THREE.Box3().setFromObject(metalMesh);

          console.log(`[PC]    WORLD SPACE – BLOKI (WSZYSTKIE) vs METAL (X/Z):`);
          console.log(
            `[PC]      BLOKI: X=[${blocksWorldBbox.min.x.toFixed(4)}, ${blocksWorldBbox.max.x.toFixed(
              4
            )}], Z=[${blocksWorldBbox.min.z.toFixed(4)}, ${blocksWorldBbox.max.z.toFixed(4)}]`
          );
          console.log(
            `[PC]      METAL: X=[${metalWorldBbox.min.x.toFixed(4)}, ${metalWorldBbox.max.x.toFixed(
              4
            )}], Z=[${metalWorldBbox.min.z.toFixed(4)}, ${metalWorldBbox.max.z.toFixed(4)}]`
          );

          const worldDeltaLeftX = (blocksWorldBbox.min.x - metalWorldBbox.min.x) * 1000;
          const worldDeltaRightX = (metalWorldBbox.max.x - blocksWorldBbox.max.x) * 1000;
          const worldDeltaFrontZ = (blocksWorldBbox.min.z - metalWorldBbox.min.z) * 1000;
          const worldDeltaBackZ = (metalWorldBbox.max.z - blocksWorldBbox.max.z) * 1000;

          console.log(
            `[PC]      WORLD DELTA X: left=${worldDeltaLeftX.toFixed(
              1
            )}mm (bloczek - metal), right=${worldDeltaRightX.toFixed(1)}mm (metal - bloczek)`
          );
          console.log(
            `[PC]      WORLD DELTA Z: front=${worldDeltaFrontZ.toFixed(
              1
            )}mm (bloczek - metal), back=${worldDeltaBackZ.toFixed(1)}mm (metal - bloczek)`
          );

          // 🎯 KOREKTA W OSI X (ŚRODEK METALU NAD ŚRODKIEM BLOKÓW/SŁUPKÓW W WORLD SPACE)
          // Jeśli widać przesunięcie "w bok" względem słupków, to w praktyce jest różnica
          // w world-space X pomiędzy centrum bloczków a centrum metalu.
          const blocksWorldCenter = new THREE.Vector3();
          blocksWorldBbox.getCenter(blocksWorldCenter);
          const metalWorldCenter = new THREE.Vector3();
          metalWorldBbox.getCenter(metalWorldCenter);

          const deltaCenterX = blocksWorldCenter.x - metalWorldCenter.x; // dodatnie: metal trzeba przesunąć w +X
          console.log(
            `[PC]      CENTER DELTA X (WORLD): blocksCenterX=${blocksWorldCenter.x.toFixed(
              4
            )}m, metalCenterX=${metalWorldCenter.x.toFixed(4)}m, deltaCenterX=${deltaCenterX.toFixed(4)}m`
          );

          // Przesuń TYLKO METAL wzdłuż osi X w WORLD SPACE tak, żeby jego środek X pokrył się ze środkiem bloczków/słupków.
          if (Math.abs(deltaCenterX) > 0.0005) { // >0.5mm żeby nie szarpać przy idealnym ustawieniu
            const parent = metalMesh.parent;
            if (parent) {
              const worldDelta = new THREE.Vector3(deltaCenterX, 0, 0);
              const parentInverse = new THREE.Matrix4().copy(parent.matrixWorld).invert();
              const localDelta = worldDelta.applyMatrix4(parentInverse);

              metalMesh.position.add(localDelta);
              el.updateMatrixWorld(true);

              console.log(
                `[PC]      🔧 KOREKTA X (WORLD→LOCAL): przesuwam metal o localDelta=(${localDelta.x.toFixed(
                  4
                )}, ${localDelta.y.toFixed(4)}, ${localDelta.z.toFixed(
                  4
                )}) żeby jego środek X był IDEALNIE nad środkiem bloczków/słupków`
              );
            }
          }

          // 🎯 JEŚLI metal jest przesunięty w osi Z względem całej ściany – popraw to w LOKALNYM Z,
          // tak żeby metal leżał idealnie "nad bloczkami" także w grubości muru.
          if (Math.abs(worldDeltaFrontZ) > 1 || Math.abs(worldDeltaBackZ) > 1) {
            // Użyj lokalnych danych (minZ / metalFrontLocalZ) żeby wyrównać PRZÓD metalu do PRZODU bloczków.
            const blocksFrontLocalZ = minZ;
            const edgeShiftLocalZ = blocksFrontLocalZ - metalFrontLocalZ;

            console.log(
              `[PC]      🔧 KOREKTA LOCAL Z (S50/P50): przesuwam metal w osi Z o ${edgeShiftLocalZ.toFixed(
                4
              )}m, żeby PRZÓD metalu pokrył się z PRZODEM bloczków`
            );
            metalMesh.position.z += edgeShiftLocalZ;
            el.updateMatrixWorld(true);
          }
        } catch (e) {
          console.log('[PC]    ⚠️ WORLD SPACE porównanie BLOKI vs METAL nie powiodło się:', e);
        }
      }
      
      if (isRoyal) {
        console.log(`[PC] 🔧 Metal (Royal): originalWidth=${originalMetalWidth.toFixed(3)}, blocksWidth=${blocksWidth.toFixed(3)}, scaleFactor=${scaleFactor.toFixed(3)}, newPosX=${blocksCenterX.toFixed(3)}`);
      } else if (isMuro) {
        // Obniż metal w dół
        metal.position.y -= 0.07;
        
        // 🎯 DODATKOWA KOREKTA POZYCJI X dla Muro Mix: WYMUSZ wyrównanie lewej krawędzi metalu do lewej krawędzi bloków
        // Użyj dokładnie tej samej logiki co edge align, ale upewnij się, że jest wykonywana po wszystkich korektach
        // Recalculate metal edges after all previous corrections (skalowanie, wyśrodkowanie, edge align)
        const metalCenterAfterAll = metalMesh.position.x + (metalGbb.min.x + metalGbb.max.x) * 0.5 * metalMesh.scale.x;
        const metalLeftAfterAll = metalCenterAfterAll - metalWidthAfterLocal / 2;
        
        // WYMUSZ wyrównanie - użyj dokładnie tej samej formuły co edge align
        const finalEdgeShiftX = blocksLeftLocalX - metalLeftAfterAll;
        
        if (Math.abs(finalEdgeShiftX) > 1e-5) {
          metalMesh.position.x += finalEdgeShiftX;
          
          console.log(`[PC] 🔧 Metal (Muro): WYMUSZAM wyrównanie lewej krawędzi:`);
          console.log(`[PC] 🔧   metalLeftAfterAll=${metalLeftAfterAll.toFixed(4)}m, blocksLeftLocalX=${blocksLeftLocalX.toFixed(4)}m`);
          console.log(`[PC] 🔧   finalEdgeShiftX=${finalEdgeShiftX.toFixed(4)}m`);
          console.log(`[PC] 🔧   nowa pozycja X=${metalMesh.position.x.toFixed(4)}m`);
          
          // Weryfikacja po korekcie
          const metalCenterFinal = metalMesh.position.x + (metalGbb.min.x + metalGbb.max.x) * 0.5 * metalMesh.scale.x;
          const metalLeftFinal = metalCenterFinal - metalWidthAfterLocal / 2;
          const metalRightFinal = metalCenterFinal + metalWidthAfterLocal / 2;
          console.log(`[PC] 🔧   PO KOREKCIE: metalLeftFinal=${metalLeftFinal.toFixed(4)}m, metalRightFinal=${metalRightFinal.toFixed(4)}m`);
          console.log(`[PC] 🔧   blocksLeftLocalX=${blocksLeftLocalX.toFixed(4)}m, blocksRightLocalX=${blocksRightLocalX.toFixed(4)}m`);
          console.log(`[PC] 🔧   różnica lewej=${((blocksLeftLocalX - metalLeftFinal) * 1000).toFixed(2)}mm, różnica prawej=${((metalRightFinal - blocksRightLocalX) * 1000).toFixed(2)}mm`);
        } else {
          console.log(`[PC] 🔧 Metal (Muro): metal jest już idealnie wyrównany (różnica < 0.01mm)`);
        }
        
        console.log(`[PC] 🔧 Metal (Muro): originalWidth=${originalMetalWidth.toFixed(3)}, blocksWidth=${blocksWidth.toFixed(3)}, scaleFactor=${scaleFactor.toFixed(3)}, finalPosX=${metalMesh.position.x.toFixed(3)}, newPosY=${metal.position.y.toFixed(3)}`);
      }
    });

    el.updateMatrixWorld(true);
    
    // 🎯 LOGOWANIE NA KOŃCU - POKAŻ WYKRYTY WARIANT
    console.log(`[PC] ✅ applyRoyalMetalCorrection ZAKOŃCZONA - PODSUMOWANIE WYKRYTEGO WARIANTU:`);
    console.log(`[PC]    fenceConfig.id = "${fenceConfig?.id}"`);
    console.log(`[PC]    variant (parametr) = "${variant}"`);
    console.log(`[PC]    isMuroMix = ${isMuroMix}`);
    console.log(`[PC]    isMuroP22 = ${isMuroP22}`);
    console.log(`[PC]    isMuroS38 = ${isMuroS38}`);
    console.log(`[PC]    isMuroP22OrS38 = ${isMuroP22OrS38}`);
    console.log(`[PC]    isMuroS22 = ${isMuroS22}`);
    console.log(`[PC]    isMuroP38 = ${isMuroP38}`);
    console.log(`[PC]    znalezione bloki: ${blocks.length} (nazwy: ${blocks.map(b => b.name).join(', ')})`);
    console.log(`[PC]    znalezione metale: ${metals.length} (nazwy: ${metals.map(m => m.name).join(', ')})`);
    if (isMuroS38) {
      console.log(`[PC]    🎯 WYKRYTO S38 - używa grup: group_21, group_63, group_0`);
    } else if (isMuroP22) {
      console.log(`[PC]    🎯 WYKRYTO P22 - używa grup: group_13, group_42, group_0`);
    }
  }

  private addEdgeMarkers(leftEdge: THREE.Vector3, rightEdge: THREE.Vector3): void {
    // Utwórz większą kropkę dla lewej krawędzi (L) - czerwony
    const leftGeometry = new THREE.SphereGeometry(0.08, 16, 16);
    const leftMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xff0000, // Czerwony
      transparent: true,
      opacity: 0.8
    });
    const leftMarker = new THREE.Mesh(leftGeometry, leftMaterial);
    leftMarker.position.copy(leftEdge);
    leftMarker.position.y = 0.4; // Wyżej, żeby było widoczne
    leftMarker.userData.isEdgeMarker = true;
    leftMarker.userData.markerType = 'L';
    this.scene.add(leftMarker);

    // Utwórz większą kropkę dla prawej krawędzi (P) - zielony
    const rightGeometry = new THREE.SphereGeometry(0.08, 16, 16);
    const rightMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x00ff00, // Zielony
      transparent: true,
      opacity: 0.8
    });
    const rightMarker = new THREE.Mesh(rightGeometry, rightMaterial);
    rightMarker.position.copy(rightEdge);
    rightMarker.position.y = 0.4; // Wyżej, żeby było widoczne
    rightMarker.userData.isEdgeMarker = true;
    rightMarker.userData.markerType = 'P';
    this.scene.add(rightMarker);

    console.log(`[PC] 🎯 Dodano markery: 🔴 L (czerwony) w (${leftEdge.x.toFixed(3)}, ${leftEdge.z.toFixed(3)}), 🟢 P (zielony) w (${rightEdge.x.toFixed(3)}, ${rightEdge.z.toFixed(3)})`);
  }

  private findClosingElementSafe(id: string): THREE.Group | null {
    try {
      const ref = (window as any).elements3DRef?.current;
      if (ref?.get?.(id)) return ref.get(id);
      
      const find = (children: THREE.Object3D[]): THREE.Group | null => {
        for (const child of children) {
          if ((child.userData as any)?.fenceElementId === id) {
            return child as THREE.Group;
          }
          if (child.children?.length) {
            const found = find(child.children);
            if (found) return found;
          }
        }
        return null;
      };
      
      return find(this.scene.children || []);
    } catch (e) {
      console.error('[PC] Błąd podczas szukania elementu:', e);
      return null;
    }
  }
}
